// src/components/examenes/ScheduleManager.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import {
  Plus,
  Edit2,
  Trash2,
  Calendar,
  Clock,
  MapPin,
  Laptop,
  Info,
  X,
  Users,
  CheckCircle2,
  Search,
  ChevronDown,
  ArrowLeft,
  Eye,
  Lock,
  Unlock,
  FileText,
  CheckSquare,
  AlertTriangle,
  RefreshCw,
  Filter,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Activity,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  List,
  Shield,
} from "lucide-react";

import { useFuncion } from "../../context/FuncionContext";
import { useAuth } from "../../context/AuthContext";
import LoadingSpinner from "../LoadingSpinner";
import ManageQuestionsModal from "./exams/ManageQuestionsModal";
import ExamScheduleStudentsModal from "./exams/ExamScheduleStudentsModal";
import useFacultyScope from "../../hooks/useFacultyScope";

const MAX_SCHEDULE_PHOTOS = 15;
const MIN_SCHEDULE_PENALTIES = 3;

const parseHourToMinutes = (value) => {
  const [hoursRaw, minutesRaw] = String(value || "").split(":");
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);

  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return hours * 60 + minutes;
};

const getScheduleWindowMinutes = (start, end) => {
  const startMinutes = parseHourToMinutes(start);
  const endMinutes = parseHourToMinutes(end);

  if (!Number.isFinite(startMinutes) || !Number.isFinite(endMinutes)) return null;
  return endMinutes - startMinutes;
};

const sanitizeScheduleCodePrefix = (rawValue) => {
  const cleaned = String(rawValue || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 6);

  return cleaned || "HOR";
};

/* ========= CSS ÚNICO, MOBILE-FIRST (TABLA PC / CARDS MÓVIL) ========= */
const CSS = `
.sm-shell{
  --sm-border:#e5e7eb;
  --sm-border-strong:#d1d5db;
  --sm-muted:#6b7280;
  --sm-text:#111827;
  --sm-bg:#ffffff;
  --sm-soft:#f8fafc;
  --sm-soft-2:#f3f4f6;
  --sm-brand:#3b82f6;
  --sm-brand-dark:#2563eb;
  --sm-green:#16a34a;
  --sm-orange:#ea580c;
  --sm-red:#dc2626;
  --sm-violet:#7c3aed;
  --sm-teal:#0f766e;
  --sm-shadow-sm:0 1px 2px rgba(0,0,0,.04), 0 8px 24px rgba(15,23,42,.06);
  --sm-shadow-md:0 10px 30px rgba(15,23,42,.12);
  width:100%;
  max-width:100%;
  padding:16px;
  box-sizing:border-box;
}

.sm-shell *{
  box-sizing:border-box;
}

.sm{
  display:grid;
  gap:16px;
}

/* cards */
.sm-card{
  background:var(--sm-bg);
  border:1px solid var(--sm-border);
  border-radius:18px;
  padding:16px;
  min-width:0;
}

.sm-card.soft{
  box-shadow:var(--sm-shadow-sm);
}

/* helpers */
.sm-center{ text-align:center; }
.sm-muted{ color:var(--sm-muted); }

.sm-col{
  display:flex;
  flex-direction:column;
  gap:6px;
  min-width:0;
}

.sm-row{
  display:flex;
  align-items:center;
  gap:12px;
  flex-wrap:wrap;
}

.sm-row.right{
  justify-content:flex-end;
}

/* labels */
.sm-label-sm{
  display:block;
  font-size:12px;
  font-weight:700;
  color:#6b7280;
  margin:0 0 6px;
}

.sm-label-lg{
  display:block;
  font-size:13px;
  font-weight:700;
  color:#111827;
  margin:0 0 6px;
}

/* inputs */
.sm-input,
.sm-select,
.sm-textarea{
  width:100%;
  min-height:44px;
  border:1px solid #d1d5db;
  background:#fff;
  color:#111827;
  border-radius:14px;
  padding:10px 12px;
  outline:none;
  font-size:14px;
  transition:border-color .18s ease, box-shadow .18s ease, background .18s ease;
}

.sm-input.small{
  min-height:40px;
  padding:8px 12px;
}

.sm-textarea{
  min-height:96px;
  resize:vertical;
}

.sm-input:focus,
.sm-select:focus,
.sm-textarea:focus{
  border-color:#60a5fa;
  box-shadow:0 0 0 4px rgba(59,130,246,.12);
}

.sm-select{
  appearance:none;
  -webkit-appearance:none;
  -moz-appearance:none;
  padding-right:38px;
  background-image:
    linear-gradient(45deg, transparent 50%, #9ca3af 50%),
    linear-gradient(135deg, #9ca3af 50%, transparent 50%);
  background-position:
    calc(100% - 18px) 55%,
    calc(100% - 12px) 55%;
  background-size:6px 6px, 6px 6px;
  background-repeat:no-repeat;
}

.sm-input-pill,
.sm-select.sm-input-pill{
  border-radius:999px !important;
}

/* buttons */
.sm-btn,
.sm-btn-primary,
.sm-btn-outline,
.sm-btn-danger,
.sm-btn-ghost{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:8px;
  min-height:40px;
  padding:10px 14px;
  border-radius:14px;
  font-size:13px;
  font-weight:700;
  cursor:pointer;
  white-space:nowrap;
  transition:all .18s ease;
}

.sm-btn{
  background:#fff;
  color:#374151;
  border:1px solid var(--sm-border-strong);
}

.sm-btn:hover{
  background:#f9fafb;
}

.sm-btn-primary{
  background:#3f9e4d;
  color:#fff;
  border:1px solid #3f9e4d;
}

.sm-btn-primary:hover{
  background:#2b7a3e;
  border-color:#2b7a3e;
}

.sm-btn-outline{
  background:#fff;
  color:#374151;
  border:1px solid var(--sm-border);
}

.sm-btn-outline:hover{
  background:#f9fafb;
}

.sm-btn-danger{
  background:#fff;
  color:#dc2626;
  border:1px solid #fecaca;
}

.sm-btn-danger:hover{
  background:#fef2f2;
}

.sm-btn-ghost{
  background:#f3f4f6;
  color:#4b5563;
  border:1px solid #e5e7eb;
}

.sm-btn-ghost:hover{
  background:#e5e7eb;
}

.sm-btn:disabled,
.sm-btn-primary:disabled,
.sm-btn-outline:disabled,
.sm-btn-danger:disabled,
.sm-btn-ghost:disabled{
  opacity:.55;
  cursor:not-allowed;
  box-shadow:none;
}

/* summary cards */
.sm-sItem{
  min-width:150px;
  border:1px solid #e5e7eb;
  border-radius:14px;
  background:#f9fafb;
  padding:10px 12px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
}

.sm-sLeft{
  display:flex;
  align-items:center;
  gap:10px;
  min-width:0;
}

.sm-sIcon{
  width:36px;
  height:36px;
  border-radius:12px;
  display:flex;
  align-items:center;
  justify-content:center;
  background:#eef2ff;
  color:#2563eb;
  flex:0 0 auto;
}

.sm-sMeta{
  min-width:0;
}

.sm-sMeta .k{
  font-size:11px;
  font-weight:700;
  color:#6b7280;
}

.sm-sMeta .v{
  font-size:18px;
  font-weight:800;
  color:#111827;
  line-height:1.1;
}

/* pills */
.sm-modpill{
  display:inline-flex;
  align-items:center;
  gap:5px;
  padding:5px 10px;
  border-radius:999px;
  font-size:11px;
  font-weight:700;
  border:1px solid transparent;
  white-space:nowrap;
}

.sm-modpill.virtual{
  background:#fff7ed;
  color:#ea580c;
  border-color:#fdba74;
}

.sm-modpill.presencial{
  background:#faf5ff;
  color:#7c3aed;
  border-color:#d8b4fe;
}

.sm-statusFinal{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  padding:6px 10px;
  border-radius:999px;
  font-size:11px;
  font-weight:800;
  white-space:nowrap;
  border:1px solid transparent;
}

.sm-statusFinal.on,
.sm-statusFinal.active{
  background:#ecfdf5;
  color:#166534;
  border-color:#bbf7d0;
}

.sm-statusFinal.off,
.sm-statusFinal.inactive{
  background:#f3f4f6;
  color:#4b5563;
  border-color:#e5e7eb;
}

.sm-statusFinal.running{
  background:#eff6ff;
  color:#1d4ed8;
  border-color:#bfdbfe;
}

.sm-statusFinal.final{
  background:#f3f4f6;
  color:#6b7280;
  border-color:#d1d5db;
}

/* notes */
.sm-note{
  background:#eff6ff;
  border:1px solid #bfdbfe;
  border-radius:14px;
  padding:14px;
  color:#1d4ed8;
  font-size:13px;
  line-height:1.5;
}

.sm-warning{
  background:#fff7ed;
  border:1px solid #fdba74;
  border-radius:14px;
  padding:12px;
  color:#c2410c;
  font-size:13px;
}

.is-error{
  border-color:#f87171 !important;
}

.sm-error{
  color:#dc2626;
  font-size:12px;
  margin-top:4px;
}

/* table */
.sm-table-wrap{
  width:100%;
  overflow:auto;
}

.sm-table{
  width:100%;
  min-width:0;
  border-collapse:collapse;
  table-layout:fixed;
}

.sm-table thead tr{
  background:#f3f4f6;
}

.sm-th{
  padding:12px 10px;
  text-align:left;
  font-size:11px;
  font-weight:800;
  color:#6b7280;
  text-transform:uppercase;
  letter-spacing:.04em;
  border-bottom:1px solid #e5e7eb;
}

.sm-td{
  padding:12px 10px;
  vertical-align:middle;
  font-size:13px;
  color:#1f2937;
  border-bottom:1px solid #eef2f7;
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap;
}

.sm-th.center,
.sm-td.center{
  text-align:center;
}

.sm-tr:hover{
  background:#f8fbff;
}

.sm-th-sort{
  background:none;
  border:none;
  padding:0;
  font:inherit;
  font-weight:800;
  color:inherit;
  cursor:pointer;
  display:inline-flex;
  align-items:center;
  gap:6px;
  text-transform:inherit;
  letter-spacing:inherit;
}

.sm-subline{
  font-size:12px;
  color:#6b7280;
  line-height:1.35;
}

@media (min-width:1024px){
  .sm-table thead th{
    position:sticky;
    top:0;
    z-index:10;
    background:#f3f4f6;
    box-shadow:0 1px 0 #e5e7eb;
  }
}

/* small actions */
.sm-mini-actions{
  display:flex;
  gap:6px;
  justify-content:center;
  flex-wrap:wrap;
}

.sm-mini-actions .sm-btn-outline,
.sm-mini-actions .sm-btn-danger{
  min-height:32px;
  padding:6px 8px;
  font-size:11px;
  border-radius:10px;
}

/* modal */
.sm-overlay{
  position:fixed;
  inset:0;
  background:rgba(17,24,39,.60);
  backdrop-filter:blur(4px);
  display:flex;
  align-items:center;
  justify-content:center;
  z-index:1000;
  padding:16px;
}

.sm-modal{
  width:100%;
  max-height:90vh;
  overflow:auto;
  background:#fff;
  border-radius:20px;
  padding:22px;
  box-shadow:0 24px 60px rgba(15,23,42,.20);
  scrollbar-width:none;
  -ms-overflow-style:none;
}

.sm-modal::-webkit-scrollbar{
  display:none;
}

.sm-modal.w500{ max-width:520px; }
.sm-modal.w900{ max-width:900px; }
.sm-modal.w980{ max-width:980px; }

.sm-modal-title{
  margin:0;
  color:#111827;
  display:flex;
  align-items:center;
  gap:10px;
  font-size:18px;
  font-weight:800;
}

/* add student modal */
.sm-modal.sm-modal-addStudent{
  width:min(560px,92vw);
  max-width:560px;
  min-height:620px;
  height:min(720px,92vh);
  max-height:720px;
  display:flex;
  flex-direction:column;
  overflow:hidden;
}

.sm-modal-addStudent .sm-modal-head{ flex:0 0 auto; }
.sm-modal-addStudent .sm-modal-body{ flex:1 1 auto; overflow:auto; padding-right:2px; }
.sm-modal-addStudent .sm-modal-foot{
  flex:0 0 auto;
  padding-top:12px;
  margin-top:10px;
  border-top:1px solid #e5e7eb;
  background:#fff;
}

/* selected students bar */
.sm-selectedPin{
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  gap:12px;
  padding:10px 12px;
  border:1px solid #bbf7d0;
  background:#f0fdf4;
  border-radius:14px;
  margin:10px 0 12px;
}

.sm-selectedPin .left{
  display:flex;
  align-items:flex-start;
  gap:10px;
  min-width:0;
}

.sm-selectedPin .chipsRow{
  display:flex;
  gap:8px;
  flex-wrap:nowrap;
  overflow-x:auto;
  overflow-y:hidden;
  padding-bottom:4px;
  max-width:100%;
}

.sm-selectedPin .chipsRow .sm-chip{
  flex:0 0 auto;
  white-space:nowrap;
}

.sm-selectedPin .name{
  font-weight:800;
  color:#111827;
}

.sm-selectedPin .tag{
  font-size:11px;
  font-weight:800;
  padding:4px 10px;
  border-radius:999px;
  border:1px solid #bbf7d0;
  background:#fff;
  color:#166534;
  white-space:nowrap;
}

/* chips */
.sm-chip{
  display:inline-flex;
  align-items:center;
  gap:8px;
  padding:6px 10px;
  border-radius:999px;
  background:#ffffff;
  color:#374151;
  border:1px solid #d1d5db;
  font-size:12px;
  font-weight:700;
}

.sm-chip-x{
  background:none;
  border:none;
  cursor:pointer;
  color:#6b7280;
  font-size:14px;
  line-height:1;
  padding:0;
}

/* avatar */
.sm-avatar{
  width:40px;
  height:40px;
  border-radius:999px;
  background:linear-gradient(135deg,#3b82f6,#2563eb);
  color:#fff;
  font-weight:800;
  font-size:15px;
  display:flex;
  align-items:center;
  justify-content:center;
  flex:0 0 auto;
}

.sm-code{
  margin-left:8px;
  font-size:11px;
  padding:2px 8px;
  border-radius:999px;
  background:#f3f4f6;
  color:#4b5563;
  border:1px solid #e5e7eb;
}

/* pagination */
.sm-pagination{
  background:#fff;
  padding:14px 16px;
  border-radius:18px;
  box-shadow:var(--sm-shadow-sm);
  display:flex;
  justify-content:space-between;
  align-items:center;
  gap:12px;
  flex-wrap:wrap;
  border:1px solid #e5e7eb;
}

.sm-pageinfo{
  color:#6b7280;
  font-size:13px;
}

.sm-pagebtn{
  min-height:36px;
  min-width:36px;
  padding:8px 12px;
  border:1px solid #d1d5db;
  border-radius:10px;
  background:#fff;
  color:#374151;
  font-weight:700;
  cursor:pointer;
  transition:background .18s ease;
}

.sm-pagebtn:hover{
  background:#f9fafb;
}

.sm-pagebtn[disabled]{
  opacity:.35;
  cursor:not-allowed;
}

.sm-pagesize{
  display:flex;
  align-items:center;
  gap:12px;
  flex-wrap:wrap;
}

.sm-pagesize .pagesizeBox{
  display:inline-flex;
  align-items:center;
  gap:10px;
  padding:8px 10px;
  border:1px solid #e5e7eb;
  background:#fff;
  border-radius:999px;
}

.sm-pagesize .pagesizeLbl{
  display:inline-flex;
  align-items:center;
  gap:8px;
  font-size:12px;
  font-weight:700;
  color:#4b5563;
}

.sm-pagesize .pagesizeLbl .dot{
  width:8px;
  height:8px;
  border-radius:999px;
  background:#60a5fa;
  display:inline-block;
}

.sm-pagesize .pagesizeSelect{
  width:auto;
  min-width:120px;
  min-height:36px;
  padding:6px 34px 6px 12px;
  border-radius:999px !important;
  font-size:12px;
  font-weight:700;
  background:#f9fafb;
}

/* students table */
.sm-table-wrap.studentsTableWrap{
  max-height:420px;
  overflow:auto;
}

.sm-table.studentsTable{
  min-width:100%;
  table-layout:fixed;
}

.sm-table.studentsTable .sm-th,
.sm-table.studentsTable .sm-td{
  padding:10px 12px;
}

.sm-table.studentsTable .sm-th:nth-child(1),
.sm-table.studentsTable .sm-td:nth-child(1){
  width:54px;
}

.sm-table.studentsTable .sm-th:nth-child(3),
.sm-table.studentsTable .sm-td:nth-child(3){
  width:150px;
}

.sm-table.studentsTable .sm-th:nth-child(4),
.sm-table.studentsTable .sm-td:nth-child(4){
  width:210px;
}

.sm-table.studentsTable .sm-th:nth-child(5),
.sm-table.studentsTable .sm-td:nth-child(5){
  width:120px;
}

.sm-studentCell{
  display:flex;
  align-items:center;
  gap:10px;
  min-width:0;
}

.sm-studentMeta{
  min-width:0;
}

.sm-studentName{
  font-weight:800;
  color:#111827;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}

.sm-studentSub{
  font-size:12px;
  color:#6b7280;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}

.sm-enableBox{
  display:flex;
  flex-direction:column;
  align-items:center;
  gap:4px;
}

/* searchbox */
.sm-searchbox{
  flex:1;
  min-width:240px;
  max-width:420px;
  position:relative;
}

.sm-count{
  position:absolute;
  right:6px;
  top:50%;
  transform:translateY(-50%);
  font-size:11px;
  color:#9ca3af;
}

.sm-count.with-clear{
  right:30px;
}

.sm-clear-search{
  position:absolute;
  right:8px;
  top:50%;
  transform:translateY(-50%);
  border:none;
  background:#f3f4f6;
  border:1px solid #e5e7eb;
  width:30px;
  height:30px;
  border-radius:999px;
  cursor:pointer;
  font-weight:800;
  color:#6b7280;
}

/* loading */
.spin{
  animation:sm-spin 1s linear infinite;
}

@keyframes sm-spin{
  from{ transform:rotate(0deg); }
  to{ transform:rotate(360deg); }
}

/* responsive */
@media (max-width:820px){
  .sm-shell{
    padding:12px;
  }

  .sm-pagination{
    align-items:stretch;
  }

  .sm-pagesize{
    width:100%;
    justify-content:space-between;
  }

  .sm-pagesize .pagesizeBox{
    width:100%;
    justify-content:space-between;
  }

  .sm-pagesize .pagesizeSelect{
    min-width:150px;
  }

  .sm-modal.sm-modal-addStudent{
    width:92vw;
    max-width:92vw;
    min-height:78vh;
    height:88vh;
    max-height:88vh;
    border-radius:16px;
    padding:16px;
  }
}

@media (max-width:640px){
  .sm-row.right{
    justify-content:stretch;
  }

  .sm-row.right > .sm-btn,
  .sm-row.right > .sm-btn-primary,
  .sm-row.right > .sm-btn-outline,
  .sm-row.right > .sm-btn-danger,
  .sm-row.right > .sm-btn-ghost{
    width:100%;
  }

  .sm-sItem{
    flex:1 1 100%;
    min-width:0;
  }
}

.sm-sectionCard{
  border:1px solid var(--sm-border);
  border-radius:18px;
  overflow:hidden;
  background:#fff;
  box-shadow:var(--sm-shadow-sm);
}

.sm-sectionHead{
  width:100%;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  padding:16px 18px;
  background:#f8fafc;
  border:none;
  cursor:pointer;
  text-align:left;
}

.sm-sectionHead:hover{
  background:#f1f5f9;
}

.sm-sectionTitle{
  display:flex;
  align-items:center;
  gap:10px;
  font-size:15px;
  font-weight:800;
  color:#111827;
}

.sm-sectionBody{
  padding:18px;
  display:grid;
  gap:16px;
}

.sm-toggleRow{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:14px;
  padding:10px 0;
}

.sm-toggleRow.is-horizontal{
  padding:0;
}

.sm-toggleRow.is-disabled{
  opacity:.6;
}

.sm-toggleMeta{
  min-width:0;
  flex:1;
}

.sm-toggleLabel{
  font-size:13px;
  font-weight:800;
  color:#111827;
  line-height:1.2;
}

.sm-toggleDesc{
  margin-top:3px;
  font-size:11px;
  color:#6b7280;
  line-height:1.35;
}

.sm-switchBtn{
  position:relative;
  width:46px;
  height:26px;
  border:none;
  border-radius:999px;
  cursor:pointer;
  transition:background .2s ease;
  flex:0 0 auto;
}

.sm-switchBtn.is-off{
  background:#cbd5e1;
}

.sm-switchBtn.is-on{
  background:#2563eb;
}

.sm-switchBtn:disabled{
  cursor:not-allowed;
}

.sm-switchThumb{
  position:absolute;
  top:3px;
  left:3px;
  width:20px;
  height:20px;
  border-radius:999px;
  background:#fff;
  box-shadow:0 2px 8px rgba(0,0,0,.18);
  transition:transform .2s ease;
}

.sm-switchBtn.is-on .sm-switchThumb{
  transform:translateX(20px);
}

.sm-assistantPicker{
  border:1px solid var(--sm-border);
  border-radius:16px;
  background:#fff;
  overflow:hidden;
}

.sm-assistantPickerTop{
  padding:12px;
  border-bottom:1px solid var(--sm-border);
  background:#f8fafc;
  display:grid;
  gap:10px;
}

.sm-assistantPickerList{
  max-height:260px;
  overflow:auto;
  padding:10px;
  display:grid;
  gap:8px;
}

.sm-assistantCard{
  width:100%;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  padding:12px 14px;
  border-radius:14px;
  border:1px solid var(--sm-border);
  background:#fff;
  cursor:pointer;
  text-align:left;
  transition:border-color .18s ease, background .18s ease, box-shadow .18s ease;
}

.sm-assistantCard:hover{
  background:#f9fafb;
}

.sm-assistantCard.is-selected{
  border-color:#93c5fd;
  background:#eff6ff;
}

.sm-assistantLeft{
  display:flex;
  align-items:center;
  gap:10px;
  min-width:0;
}

.sm-assistantMeta{
  min-width:0;
}

.sm-assistantName{
  font-size:13px;
  font-weight:800;
  color:#111827;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}

.sm-assistantSub{
  font-size:12px;
  color:#6b7280;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}

.sm-assistantBadge{
  flex:0 0 auto;
  font-size:11px;
  font-weight:800;
  padding:4px 10px;
  border-radius:999px;
  border:1px solid #d1d5db;
  background:#f9fafb;
  color:#6b7280;
}

.sm-assistantCard.is-selected .sm-assistantBadge{
  border-color:#bfdbfe;
  background:#dbeafe;
  color:#1d4ed8;
}

.sm-selectedAssistants{
  display:flex;
  flex-wrap:wrap;
  gap:8px;
}

.sm-selectedAssistantsEmpty{
  font-size:12px;
  color:#6b7280;
  font-style:italic;
}

/* =========================
   TABLA / CARDS REDISEÑADOS
   ========================= */

.sm-tableFrame{
  background:#fff;
  border:1px solid #dfe3ea;
  border-radius:22px;
  overflow:hidden;
  box-shadow:0 8px 24px rgba(15,23,42,.06);
}

.sm-tableViewport{
  overflow:auto;
  width:100%;
}

.sm-scheduleTable{
  width:100%;
  min-width:1180px;
  border-collapse:separate;
  border-spacing:0;
  table-layout:fixed;
  background:#fff;
}

.sm-scheduleTable thead th{
  background:#f3f4f6;
  color:#334155;
  font-size:11px;
  font-weight:900;
  text-transform:uppercase;
  letter-spacing:.05em;
  padding:16px 14px;
  border-bottom:1px solid #e5e7eb;
  position:sticky;
  top:0;
  z-index:3;
}

.sm-scheduleTable tbody td{
  padding:14px 12px;
  border-bottom:1px solid #e5e7eb;
  vertical-align:middle;
  background:#fff;
}

.sm-scheduleTable tbody tr:hover td{
  background:#fbfdff;
}

.sm-dividerCol{
  position:relative;
}

.sm-dividerCol::before{
  content:"";
  position:absolute;
  left:0;
  top:18px;
  bottom:18px;
  width:1px;
  background:#eceff3;
}

.sm-col-course{
  display:flex;
  align-items:center;
  gap:12px;
  min-width:0;
}

.sm-courseIcon{
  width:40px;
  height:40px;
  border-radius:14px;
  background:linear-gradient(135deg,#3b82f6,#2563eb);
  color:#fff;
  display:flex;
  align-items:center;
  justify-content:center;
  flex:0 0 auto;
  box-shadow:0 8px 18px rgba(37,99,235,.18);
}

.sm-courseMeta{
  min-width:0;
}

.sm-courseCode{
  font-size:12px;
  font-weight:1000;
  color:#2563eb;
  text-transform:uppercase;
  letter-spacing:.03em;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}

.sm-courseDate{
  margin-top:2px;
  font-size:15px;
  font-weight:1000;
  color:#111827;
  line-height:1.15;
}

.sm-courseTime{
  margin-top:5px;
  display:inline-flex;
  align-items:center;
  gap:6px;
  padding:4px 8px;
  border-radius:999px;
  background:#f3f4f6;
  color:#6b7280;
  font-size:11px;
  font-weight:800;
}

.sm-idAssist{
  display:grid;
  gap:7px;
}

.sm-idChip{
  display:inline-flex;
  align-items:center;
  width:max-content;
  max-width:100%;
  padding:4px 8px;
  border-radius:8px;
  background:#f3f4f6;
  color:#374151;
  border:1px solid #e5e7eb;
  font-size:11px;
  font-weight:1000;
  font-family:ui-monospace,SFMono-Regular,Menlo,monospace;
}

.sm-assistantLine{
  display:flex;
  align-items:center;
  gap:6px;
  font-size:12px;
  font-weight:800;
  color:#d97706;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}

.sm-assistantLine.empty{
  color:#9ca3af;
  font-style:italic;
}

.sm-modeStack{
  display:grid;
  gap:8px;
}

.sm-modeTop{
  display:flex;
  align-items:center;
  gap:8px;
}

.sm-lockMini{
  color:#9ca3af;
  flex:0 0 auto;
}

.sm-accessPreview{
  display:flex;
  align-items:center;
  gap:8px;
}

.sm-locationText{
  min-width:0;
  font-size:12px;
  color:#6b7280;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}

.sm-eyeMini{
  width:22px;
  height:22px;
  border-radius:8px;
  border:1px solid #dbeafe;
  background:#eff6ff;
  color:#2563eb;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  cursor:pointer;
  flex:0 0 auto;
}

.sm-statMini{
  display:grid;
  justify-items:center;
  gap:7px;
}

.sm-statValue{
  font-size:15px;
  font-weight:1000;
  color:#111827;
}

.sm-ghostManage{
  min-height:28px;
  padding:5px 10px;
  border-radius:8px;
  border:1px solid #dbe4ff;
  background:#eef2ff;
  color:#4338ca;
  font-size:11px;
  font-weight:900;
  cursor:pointer;
}

.sm-configBox{
  display:grid;
  gap:8px;
  padding:10px 10px 8px;
  border-radius:14px;
  background:#f7f7f8;
  border:1px solid #eceef2;
  min-width:112px;
}

.sm-configLine{
  display:flex;
  justify-content:space-between;
  gap:10px;
  font-size:11px;
  line-height:1.2;
}

.sm-configLine .k{
  color:#6b7280;
  font-weight:800;
}

.sm-configLine .v{
  font-weight:1000;
  text-align:right;
}

.sm-configLine .v.req{
  color:#ea580c;
}

.sm-configLine .v.opt{
  color:#2563eb;
}

.sm-configLine .v.ok{
  color:#2563eb;
}

.sm-statePillWide{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:6px;
  min-height:28px;
  padding:6px 12px;
  border-radius:999px;
  border:1px solid #d1d5db;
  font-size:11px;
  font-weight:1000;
  white-space:nowrap;
}

.sm-statePillWide.on{
  background:#ecfdf5;
  color:#166534;
  border-color:#bbf7d0;
}

.sm-statePillWide.off{
  background:#f3f4f6;
  color:#4b5563;
  border-color:#e5e7eb;
}

.sm-statePillWide.running{
  background:#eff6ff;
  color:#1d4ed8;
  border-color:#bfdbfe;
}

.sm-statePillWide.final{
  background:#f3f4f6;
  color:#6b7280;
  border-color:#d1d5db;
}

.sm-actionsCol{
  display:grid;
  gap:8px;
}

.sm-manageBtn{
  width:100%;
  min-height:34px;
  border-radius:12px;
  border:1px solid #bfe8de;
  background:#ebfaf5;
  color:#0f766e;
  font-size:12px;
  font-weight:1000;
  cursor:pointer;
}

.sm-inlineActions{
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:8px;
}

.sm-editBtn,
.sm-deleteBtn{
  min-height:34px;
  border-radius:12px;
  font-size:12px;
  font-weight:1000;
  cursor:pointer;
  background:#fff;
}

.sm-editBtn{
  border:1px solid #bfd7ff;
  color:#2563eb;
}

.sm-deleteBtn{
  border:1px solid #fecaca;
  color:#dc2626;
}

.sm-deleteBtn:disabled{
  opacity:.55;
  cursor:not-allowed;
  background:#f3f4f6;
  color:#9ca3af;
}

.sm-editBtn:disabled{
  opacity:.55;
  cursor:not-allowed;
  background:#f3f4f6;
  color:#9ca3af;
}

/* cards */
.sm-cardsGrid{
  display:grid;
  grid-template-columns:repeat(auto-fit,minmax(330px,1fr));
  gap:16px;
}

.sm-cardSchedule{
  background:#fff;
  border:1px solid #dfe3ea;
  border-radius:20px;
  padding:16px;
  box-shadow:0 8px 24px rgba(15,23,42,.06);
  display:flex;
  flex-direction:column;
  gap:14px;
}

.sm-cardTop{
  display:flex;
  justify-content:space-between;
  align-items:flex-start;
  gap:12px;
}

.sm-cardBox{
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:14px;
  padding:14px;
  border-radius:16px;
  background:#f7f7f8;
  border:1px solid #eceef2;
}

.sm-cardSection{
  min-width:0;
}

.sm-cardSection .k{
  font-size:11px;
  font-weight:800;
  color:#6b7280;
  margin-bottom:6px;
}

.sm-cardSection .v{
  font-size:13px;
  font-weight:1000;
  color:#111827;
}

.sm-cardSection .sub{
  margin-top:5px;
  font-size:12px;
  color:#6b7280;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}

.sm-cardButtons{
  display:grid;
  gap:8px;
  margin-top:auto;
}

.sm-cardButtons .row2{
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:8px;
}

@media (max-width: 768px){
  .sm-scheduleTable{
    min-width:1080px;
  }

  .sm-cardBox{
    grid-template-columns:1fr 1fr;
  }
}

@media (max-width: 520px){
  .sm-cardBox{
    grid-template-columns:1fr;
  }
}

/* =========================
   DETALLES DE ACCESO
   ========================= */

.sm-accessModal{
  max-width: 420px !important;
  padding: 22px 22px 18px;
  border-radius: 20px;
}

.sm-accessHeader{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  margin-bottom:18px;
}

.sm-accessTitle{
  display:flex;
  align-items:center;
  gap:10px;
  font-size:17px;
  font-weight:900;
  color:#1f2937;
}

.sm-accessClose{
  border:none;
  background:none;
  color:#9ca3af;
  cursor:pointer;
  padding:4px;
  border-radius:10px;
}

.sm-accessClose:hover{
  background:#f3f4f6;
  color:#6b7280;
}

.sm-accessSection{
  display:grid;
  gap:8px;
  margin-bottom:16px;
}

.sm-accessLabel{
  font-size:13px;
  font-weight:800;
  color:#6b7280;
}

.sm-accessValue{
  font-size:14px;
  font-weight:900;
  color:#1f2937;
  display:flex;
  align-items:center;
  gap:8px;
}

.sm-accessInputLike{
  min-height:44px;
  display:flex;
  align-items:center;
  padding:0 14px;
  border-radius:10px;
  border:1px solid #d1d5db;
  background:#f3f4f6;
  color:#1f2937;
  font-size:14px;
  font-weight:700;
}

.sm-accessCodeBox{
  min-height:38px;
  display:flex;
  align-items:center;
  padding:0 14px;
  border-radius:10px;
  border:1px solid #bfdbfe;
  background:#eff6ff;
  color:#1e40af;
  font-size:14px;
  font-weight:900;
  letter-spacing:.04em;
}

.sm-accessGrid2{
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:18px;
  padding-top:14px;
  border-top:1px solid #e5e7eb;
  margin-top:10px;
}

.sm-accessMini{
  display:grid;
  gap:6px;
}

.sm-accessMini .k{
  font-size:13px;
  font-weight:800;
  color:#6b7280;
}

.sm-accessMini .v{
  font-size:14px;
  font-weight:900;
  color:#1f2937;
  display:flex;
  align-items:center;
  gap:6px;
}

.sm-accessFooter{
  margin-top:18px;
  padding-top:16px;
  border-top:1px solid #e5e7eb;
}

.sm-accessFooter .sm-btn-ghost{
  width:100%;
  min-height:40px;
  border-radius:12px;
  background:#f3f4f6;
  color:#374151;
  font-weight:900;
}

/* =========================
   MODAL NUEVO HORARIO
   ========================= */

   .sm-createModal{
  width:min(1100px,96vw);
  max-width:1100px;
  height:min(92vh,920px);
  max-height:92vh;
  padding:0;
  overflow:hidden;
  border-radius:18px;
  display:flex;
  flex-direction:column;
  position:relative;
}

.sm-createBody{
  flex:1 1 auto;
  overflow:auto;
  background:#f8fafc;
  padding:16px 18px 18px;
  min-height:0;
}

.sm-createFooter{
  flex:0 0 auto;
  min-height:70px;
  padding:12px 18px;
  border-top:1px solid #e5e7eb;
  background:#fff;
  display:flex;
  align-items:center;
  justify-content:flex-end;
  gap:10px;
  position:relative;
}

.sm-createHead{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  padding:14px 18px;
  border-bottom:1px solid #e5e7eb;
  background:#fff;
}

.sm-createHeadTitle{
  display:flex;
  align-items:center;
  gap:10px;
  font-size:18px;
  font-weight:900;
  color:#1f2937;
}

.sm-createTabs{
  display:flex;
  align-items:center;
  gap:20px;
  padding:0 18px;
  min-height:48px;
  border-bottom:1px solid #e5e7eb;
  background:#fff;
}

.sm-createTab{
  position:relative;
  border:none;
  background:none;
  padding:0;
  min-height:48px;
  display:inline-flex;
  align-items:center;
  gap:8px;
  font-size:14px;
  font-weight:900;
  color:#6b7280;
  cursor:pointer;
}

.sm-createTab.is-active{
  color:#16a34a;
}

.sm-createTab.is-active::after{
  content:"";
  position:absolute;
  left:0;
  right:0;
  bottom:0;
  height:2px;
  border-radius:999px;
  background:#16a34a;
}

.sm-createContent{
  display:grid;
  gap:16px;
}

.sm-createPanel{
  background:#fff;
  border:1px solid #dbe3ee;
  border-radius:18px;
  box-shadow:0 4px 18px rgba(15,23,42,.05);
  overflow:hidden;
}

.sm-createPanelBody{
  padding:16px;
  display:grid;
  gap:16px;
}

.sm-codeHero{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:14px;
  padding:14px 16px;
  border-radius:14px;
  background:#eff6ff;
  border:1px solid #bfdbfe;
}

.sm-codeHeroLabel{
  font-size:11px;
  font-weight:900;
  color:#1d4ed8;
  text-transform:uppercase;
  letter-spacing:.05em;
}

.sm-codeHeroValue{
  margin-top:4px;
  font-size:26px;
  font-weight:1000;
  color:#0f172a;
  letter-spacing:.06em;
}

.sm-modeChoices{
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:12px;
}

.sm-modeCard{
  min-height:58px;
  padding:0 14px;
  border-radius:14px;
  border:1.5px solid #d1d5db;
  background:#fff;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
  cursor:pointer;
  transition:.18s ease;
}

.sm-modeCard:hover{
  background:#fafafa;
}

.sm-modeCard.is-active.presencial{
  border-color:#a855f7;
  background:#faf5ff;
  box-shadow:0 0 0 3px rgba(168,85,247,.08);
}

.sm-modeCard.is-active.virtual{
  border-color:#3b82f6;
  background:#eff6ff;
  box-shadow:0 0 0 3px rgba(59,130,246,.08);
}

.sm-modeCardLeft{
  display:flex;
  align-items:center;
  gap:10px;
  min-width:0;
}

.sm-modeCardIcon{
  width:34px;
  height:34px;
  border-radius:999px;
  background:#f3f4f6;
  display:flex;
  align-items:center;
  justify-content:center;
  flex:0 0 auto;
}

.sm-modeCardText{
  font-size:14px;
  font-weight:900;
  color:#1f2937;
}

.sm-modeCardCheck{
  width:18px;
  height:18px;
  border-radius:999px;
  border:1.5px solid #cbd5e1;
  display:flex;
  align-items:center;
  justify-content:center;
  flex:0 0 auto;
}

.sm-modeCard.is-active .sm-modeCardCheck{
  border-color:currentColor;
}

.sm-createGrid4{
  display:grid;
  grid-template-columns:1fr 1fr 1fr 1fr;
  gap:12px;
}

.sm-createGrid3{
  display:grid;
  grid-template-columns:1fr 1fr 1fr;
  gap:12px;
}

.sm-createGrid2{
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:12px;
}

.sm-fieldBox{
  display:grid;
  gap:6px;
}

.sm-fieldBox.full{
  grid-column:1 / -1;
}

.sm-fieldWrap{
  position:relative;
}

.sm-fieldSuffix{
  position:absolute;
  right:12px;
  top:50%;
  transform:translateY(-50%);
  color:#6b7280;
  font-size:12px;
  font-weight:900;
}

.sm-fieldIconLeft{
  position:absolute;
  left:12px;
  top:50%;
  transform:translateY(-50%);
  color:#9ca3af;
}

.sm-input.with-left-icon{
  padding-left:36px;
}

.sm-assistantHintBox{
  border:1px solid #facc15;
  background:#fffbeb;
  border-radius:14px;
  padding:12px;
  display:grid;
  gap:10px;
}

.sm-assistantHintTitle{
  font-size:13px;
  font-weight:900;
  color:#92400e;
  display:flex;
  align-items:center;
  gap:8px;
}

.sm-assistantHintText{
  font-size:12px;
  color:#6b7280;
  line-height:1.45;
}

.sm-inlineSelect{
  width:100%;
  min-height:42px;
  border:1px solid #d1d5db;
  border-radius:12px;
  padding:10px 12px;
  background:#fff;
  font-size:14px;
  font-weight:700;
}

.sm-securityCard{
  border:1px solid #dbe3ee;
  border-radius:16px;
  background:#fff;
  padding:14px;
  display:grid;
  gap:14px;
}

.sm-securityTop2{
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:12px;
}

.sm-securityItem{
  border:1px solid #e5e7eb;
  border-radius:14px;
  padding:12px;
  display:grid;
  gap:10px;
  background:#fff;
}

.sm-securityTitleRow{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
}

.sm-securityTitle{
  font-size:13px;
  font-weight:900;
  color:#111827;
}

.sm-securityDesc{
  font-size:12px;
  color:#6b7280;
  line-height:1.4;
  border-top:1px solid #e5e7eb;
  padding-top:10px;
}

.sm-securityGrid2{
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:14px;
}

.sm-securityMiniGrid{
  display:grid;
  grid-template-columns:1fr auto;
  gap:8px;
  align-items:center;
}

.sm-securitySplit{
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:14px;
}

.sm-createFooter .sm-btn-primary{
  min-width:150px;
  border-radius:12px;
}

.sm-createFooter .sm-btn-ghost{
  min-width:110px;
  border-radius:12px;
}

.sm-importShell{
  display:grid;
  gap:16px;
}

.sm-importHero{
  display:grid;
  grid-template-columns:1.1fr 1fr;
  gap:16px;
  padding:18px;
  border-radius:18px;
  border:1px solid #bfdbfe;
  background:#eff6ff;
}

.sm-importDrop{
  border:1.5px dashed #93c5fd;
  border-radius:16px;
  background:#fff;
  min-height:150px;
  display:flex;
  align-items:center;
  justify-content:center;
  text-align:center;
  padding:16px;
  color:#1d4ed8;
  font-weight:900;
}

.sm-importReq{
  background:#fff;
  border:1px solid #dbe3ee;
  border-radius:16px;
  overflow:hidden;
}

.sm-importReqHead{
  padding:14px 16px;
  border-bottom:1px solid #e5e7eb;
  font-weight:900;
  color:#111827;
  display:flex;
  align-items:center;
  justify-content:space-between;
}

.sm-importReqBody{
  padding:16px;
  display:grid;
  grid-template-columns:1fr 1fr 1fr;
  gap:14px 18px;
}

.sm-importReqItem{
  display:grid;
  gap:5px;
}

.sm-importReqItem .k{
  font-size:14px;
  font-weight:900;
  color:#111827;
}

.sm-importReqItem .v{
  font-size:13px;
  color:#4b5563;
  line-height:1.45;
}

.sm-importWarn{
  margin-top:4px;
  padding:12px 14px;
  border-radius:12px;
  background:#fff7ed;
  border:1px solid #fdba74;
  color:#c2410c;
  font-size:13px;
}

@media (max-width: 960px){
  .sm-createGrid4,
  .sm-createGrid3{
    grid-template-columns:1fr 1fr;
  }

  .sm-importHero,
  .sm-importReqBody,
  .sm-securitySplit,
  .sm-securityTop2{
    grid-template-columns:1fr;
  }

  .sm-createModal{
    width:min(96vw,1100px);
  }
}

@media (max-width: 640px){
  .sm-createGrid4,
  .sm-createGrid3,
  .sm-createGrid2,
  .sm-modeChoices,
  .sm-securityGrid2{
    grid-template-columns:1fr;
  }

    .sm-createBody{
    padding:14px 12px 14px;
  }

  .sm-createHead,
  .sm-createTabs,
  .sm-createFooter{
    padding-left:12px;
    padding-right:12px;
  }

  .sm-accessGrid2{
    grid-template-columns:1fr;
    gap:14px;
  }
}

/* =========================
   ESTUDIANTES INSCRITOS REDISEÑO
   ========================= */

.sm-modal.enrolledStudentsModal{
  width:min(1220px,96vw);
  max-width:1220px;
  padding:0;
  overflow:hidden;
  border-radius:24px;
}

.sm-enrolledShell{
  display:flex;
  flex-direction:column;
  min-height:min(84vh,860px);
  max-height:84vh;
  background:#fff;
}

.sm-enrolledHead{
  padding:20px 22px 16px;
  border-bottom:1px solid #eceff3;
  background:#fbfbfc;
}

.sm-enrolledTop{
  display:flex;
  justify-content:space-between;
  align-items:flex-start;
  gap:18px;
  flex-wrap:wrap;
}

.sm-enrolledTitleWrap{
  display:flex;
  align-items:flex-start;
  gap:14px;
  min-width:0;
}

.sm-enrolledIcon{
  width:46px;
  height:46px;
  border-radius:14px;
  background:#f7e8b8;
  color:#d97706;
  display:flex;
  align-items:center;
  justify-content:center;
  flex:0 0 auto;
}

.sm-enrolledTitle{
  margin:0;
  font-size:18px;
  font-weight:1000;
  color:#111827;
}

.sm-enrolledMeta{
  margin-top:6px;
  display:flex;
  align-items:center;
  gap:10px;
  flex-wrap:wrap;
  color:#6b7280;
  font-size:13px;
}

.sm-inlineDateChip{
  display:inline-flex;
  align-items:center;
  gap:6px;
  padding:4px 10px;
  border-radius:999px;
  border:1px solid #e5e7eb;
  background:#f3f4f6;
  color:#6b7280;
  font-size:11px;
  font-weight:800;
}

.sm-usedQuota{
  display:inline-flex;
  align-items:center;
  gap:8px;
  align-self:center;
  color:#6b7280;
  font-size:13px;
  font-weight:700;
}

.sm-usedQuotaStrong{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  min-width:78px;
  min-height:34px;
  padding:0 12px;
  border-radius:12px;
  background:#fdecc8;
  color:#b45309;
  font-size:14px;
  font-weight:1000;
}

.sm-enrolledToolbar{
  padding:14px 22px 16px;
  border-bottom:1px solid #eceff3;
  display:flex;
  justify-content:space-between;
  align-items:center;
  gap:14px;
  flex-wrap:wrap;
}

.sm-toolbarLeft{
  flex:1;
  min-width:260px;
}

.sm-toolbarSearch{
  position:relative;
  max-width:420px;
}

.sm-toolbarSearchIcon{
  position:absolute;
  left:14px;
  top:50%;
  transform:translateY(-50%);
  color:#9ca3af;
}

.sm-toolbarInput{
  width:100%;
  min-height:42px;
  border:1px solid #d1d5db;
  border-radius:14px;
  background:#fff;
  padding:10px 14px 10px 40px;
  font-size:14px;
  outline:none;
}

.sm-toolbarInput:focus{
  border-color:#93c5fd;
  box-shadow:0 0 0 4px rgba(59,130,246,.12);
}

.sm-toolbarRight{
  display:flex;
  align-items:center;
  gap:10px;
  flex-wrap:wrap;
}

.sm-btn-soft{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:8px;
  min-height:40px;
  padding:10px 16px;
  border-radius:14px;
  border:1px solid #d1d5db;
  background:#fff;
  color:#374151;
  font-weight:900;
  cursor:pointer;
  box-shadow:0 4px 14px rgba(15,23,42,.04);
}

.sm-btn-soft:hover{
  background:#f9fafb;
}

.sm-btn-green{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:8px;
  min-height:40px;
  padding:10px 16px;
  border-radius:14px;
  border:1px solid #49a35d;
  background:#49a35d;
  color:#fff;
  font-weight:900;
  cursor:pointer;
  box-shadow:0 8px 18px rgba(73,163,93,.18);
}

.sm-btn-green:hover{
  background:#39884a;
  border-color:#39884a;
}

.sm-btn-green:disabled,
.sm-btn-soft:disabled{
  opacity:.55;
  cursor:not-allowed;
  box-shadow:none;
}

.sm-enrolledBody{
  padding:18px 22px 14px;
  overflow:auto;
  flex:1 1 auto;
  background:#fff;
}

.sm-enrolledTableWrap{
  border:1px solid #dfe3ea;
  border-radius:18px;
  overflow:hidden;
  background:#fff;
}

.sm-enrolledTable{
  width:100%;
  min-width:980px;
  border-collapse:separate;
  border-spacing:0;
  table-layout:fixed;
}

.sm-enrolledTable thead th{
  background:#f3f4f6;
  border-bottom:1px solid #e5e7eb;
  color:#475569;
  font-size:12px;
  font-weight:1000;
  padding:13px 12px;
  text-align:left;
}

.sm-enrolledTable tbody td{
  padding:14px 12px;
  border-bottom:1px solid #eceff3;
  vertical-align:middle;
  background:#fff;
}

.sm-enrolledTable tbody tr:last-child td{
  border-bottom:none;
}

.sm-enrolledTable tbody tr:hover td{
  background:#fcfcfd;
}

.sm-col-check{
  width:46px;
  text-align:center;
}

.sm-col-student{ width:33%; }
.sm-col-date{ width:16%; }
.sm-col-origin{ width:22%; }
.sm-col-access{ width:16%; text-align:center; }
.sm-col-actions{ width:13%; text-align:center; }

.sm-studentRich{
  display:flex;
  align-items:flex-start;
  gap:12px;
  min-width:0;
}

.sm-studentAvatarSoft{
  width:42px;
  height:42px;
  border-radius:999px;
  background:linear-gradient(135deg,#0ea5e9,#2563eb);
  color:#fff;
  font-size:14px;
  font-weight:1000;
  display:flex;
  align-items:center;
  justify-content:center;
  flex:0 0 auto;
}

.sm-studentRichMeta{
  min-width:0;
}

.sm-studentRichName{
  font-size:15px;
  font-weight:1000;
  color:#111827;
  line-height:1.2;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}

.sm-studentRichSub{
  margin-top:4px;
  font-size:12px;
  color:#6b7280;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}

.sm-studentDoc{
  margin-top:4px;
  font-size:12px;
  color:#94a3b8;
  letter-spacing:.03em;
}

.sm-dateBadge{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  min-height:30px;
  padding:0 10px;
  border-radius:8px;
  background:#f3f4f6;
  color:#4b5563;
  border:1px solid #e5e7eb;
  font-size:12px;
  font-weight:900;
}

.sm-originStack{
  display:flex;
  flex-direction:column;
  gap:6px;
  min-width:0;
}

.sm-originBadge{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  width:max-content;
  max-width:100%;
  min-height:28px;
  padding:0 10px;
  border-radius:8px;
  font-size:11px;
  font-weight:1000;
  border:1px solid transparent;
  white-space:nowrap;
}

.sm-originBadge.auto{
  background:#f5ebff;
  color:#9333ea;
  border-color:#e9d5ff;
}

.sm-originBadge.system{
  background:#ecfeff;
  color:#0f766e;
  border-color:#a5f3fc;
}

.sm-originBadge.manual{
  background:#eff6ff;
  color:#2563eb;
  border-color:#bfdbfe;
}

.sm-originSub{
  font-size:11px;
  color:#6b7280;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}

.sm-accessInline{
  display:inline-flex;
  align-items:center;
  gap:10px;
  justify-content:center;
}

.sm-accessInlineLabel{
  font-size:13px;
  font-weight:900;
  color:#374151;
}

.sm-tableIconBtn{
  width:34px;
  height:34px;
  border-radius:10px;
  border:1px solid #e5e7eb;
  background:#fff;
  color:#9ca3af;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  cursor:pointer;
}

.sm-tableIconBtn:hover{
  background:#f9fafb;
  color:#dc2626;
  border-color:#fecaca;
}

.sm-enrolledFooter{
  padding:12px 22px 20px;
  display:flex;
  justify-content:space-between;
  align-items:center;
  gap:12px;
  flex-wrap:wrap;
}

.sm-miniPager{
  display:flex;
  align-items:center;
  gap:8px;
  color:#6b7280;
  font-size:13px;
}

.sm-miniPagerBtn{
  width:28px;
  height:28px;
  border-radius:8px;
  border:1px solid #e5e7eb;
  background:#f9fafb;
  color:#9ca3af;
  display:inline-flex;
  align-items:center;
  justify-content:center;
}

@media (max-width: 768px){
  .sm-modal.enrolledStudentsModal{
    width:96vw;
    max-width:96vw;
  }

  .sm-enrolledHead,
  .sm-enrolledToolbar,
  .sm-enrolledBody,
  .sm-enrolledFooter{
    padding-left:14px;
    padding-right:14px;
  }

  .sm-toolbarRight{
    width:100%;
  }

  .sm-btn-soft,
  .sm-btn-green{
    flex:1 1 auto;
  }
}

.sm-readonlyBox{
  min-height:44px;
  display:flex;
  align-items:center;
  padding:0 14px;
  border-radius:12px;
  border:1px solid #e5e7eb;
  background:#f8fafc;
  color:#1f2937;
  font-size:14px;
  font-weight:800;
}

.sm-readonlyTextarea{
  min-height:88px;
  padding:12px 14px;
  border-radius:12px;
  border:1px solid #e5e7eb;
  background:#f8fafc;
  color:#1f2937;
  font-size:14px;
  font-weight:700;
  line-height:1.5;
  white-space:pre-wrap;
}

.sm-filterHero{
  background:#fff;
  border:1px solid #dfe3ea;
  border-radius:22px;
  padding:16px 18px;
  box-shadow:0 8px 24px rgba(15,23,42,.06);
  display:grid;
  gap:14px;
}

.sm-filterHeroTop{
  display:flex;
  align-items:flex-start;
  gap:14px;
  flex-wrap:wrap;
}

.sm-filterHeroIcon{
  width:40px;
  height:40px;
  border-radius:14px;
  background:#eaf2ff;
  color:#2563eb;
  display:flex;
  align-items:center;
  justify-content:center;
  flex:0 0 auto;
}

.sm-filterHeroTitle{
  font-size:13px;
  font-weight:1000;
  color:#1d4ed8;
  letter-spacing:.03em;
}

.sm-filterHeroSub{
  font-size:12px;
  color:#6b7280;
  margin-top:4px;
}

.sm-filterChipsRow{
  display:flex;
  flex-wrap:wrap;
  gap:12px;
  align-items:center;
}

.sm-filterMetric{
  min-width:128px;
  padding:10px 14px;
  border:1px solid #e5e7eb;
  border-radius:16px;
  background:#fff;
  display:flex;
  align-items:center;
  gap:10px;
}

.sm-filterMetricIcon{
  width:26px;
  height:26px;
  border-radius:999px;
  display:flex;
  align-items:center;
  justify-content:center;
  flex:0 0 auto;
}

.sm-filterMetricLabel{
  font-size:10px;
  font-weight:1000;
  color:#64748b;
  text-transform:uppercase;
  line-height:1.1;
}

.sm-filterMetricValue{
  font-size:12px;
  font-weight:1000;
  color:#111827;
  line-height:1.2;
  margin-top:2px;
}

.sm-filterMetric.blue .sm-filterMetricIcon{
  background:#eff6ff;
  color:#2563eb;
}

.sm-filterMetric.green .sm-filterMetricIcon{
  background:#ecfdf5;
  color:#16a34a;
}

.sm-filterMetric.orange .sm-filterMetricIcon{
  background:#fff7ed;
  color:#ea580c;
}

.sm-filterMetric.amber .sm-filterMetricIcon{
  background:#fff8e7;
  color:#d97706;
}

.sm-filterMetric.purple .sm-filterMetricIcon{
  background:#faf5ff;
  color:#9333ea;
}

`;


const toFiniteNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const pickPositiveNumber = (...values) => {
  for (const value of values) {
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
};

const getScheduleRegisteredQuestions = (exam) => {
  return pickPositiveNumber(
    exam?.preguntas_registradas,
    exam?.preguntas_configuradas,
    exam?.preguntas_asignadas,
    exam?.questions_count,
    exam?.total_preguntas,
    exam?.preguntas_reales
  );
};

function pickCourseId(obj) {
  const v = obj?.curso_id ?? obj?.course_id ?? obj?.cursoId ?? obj?.courseId ?? obj?.id_curso ?? obj?.idCourse;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function pickFacultyId(obj) {
  const v =
    obj?.facultad_id ??
    obj?.facultadId ??
    obj?.faculty_id ??
    obj?.facultyId ??
    obj?.id_facultad ??
    obj?.idFaculty ??
    obj?.facultad?.id ??
    obj?.faculty?.id;

  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function getAdminFacultyIds(user) {
  const list = Array.isArray(user?.admin_facultades) ? user.admin_facultades : [];
  return new Set(
    list
      .map((x) => Number(x?.facultad_id ?? x?.id))
      .filter((n) => Number.isFinite(n) && n > 0)
  );
}

function isAdminFacultad(user) {
  return String(user?.rol || "").toLowerCase() === "admin_facultad";
}
function useDebouncedValue(value, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const normalizeText = (s = "") =>
  String(s)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

function parseLocalDateTime(dateStr, timeStr) {
  const d = String(dateStr || "").slice(0, 10);
  if (!d) return null;

  const tRaw = String(timeStr || "").trim();
  if (!tRaw) return new Date(`${d}T00:00:00`);

  const parts = tRaw.split(":").map(x => x.trim());
  const hh = String(parts[0] ?? "00").padStart(2, "0");
  const mm = String(parts[1] ?? "00").padStart(2, "0");
  const ss = String(parts[2] ?? "00").padStart(2, "0");

  const dt = new Date(`${d}T${hh}:${mm}:${ss}`);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function getScheduleState(exam, now = new Date()) {
  const backendState = String(exam?.estado_horario || "").trim().toLowerCase();

  if (
    backendState === "activo" ||
    backendState === "inactivo" ||
    backendState === "en_ejecucion" ||
    backendState === "finalizado"
  ) {
    return backendState;
  }

  const end = parseLocalDateTime(exam?.fecha_examen, exam?.hora_fin);
  const finishedByTime = !!end && end.getTime() < now.getTime();

  if (finishedByTime) return "finalizado";
  return exam?.activo ? "activo" : "inactivo";
}

function isScheduleFinished(exam, now = new Date()) {
  return getScheduleState(exam, now) === "finalizado";
}

function isScheduleRunning(exam, now = new Date()) {
  return getScheduleState(exam, now) === "en_ejecucion";
}


function getScheduleStateMeta(exam, now = new Date()) {
  const state = getScheduleState(exam, now);

  if (state === "activo") {
    return { key: state, label: "Activo", className: "on", emoji: "🟢" };
  }
  if (state === "inactivo") {
    return { key: state, label: "Inactivo", className: "off", emoji: "⚪" };
  }
  if (state === "en_ejecucion") {
    return { key: state, label: "En ejecución", className: "running", emoji: "🟠" };
  }
  return { key: "finalizado", label: "Finalizado", className: "final", emoji: "✅" };
}

function isScheduleReadOnly(exam, now = new Date()) {
  return getScheduleState(exam, now) === "finalizado";
}

function canEditSchedule(exam, now = new Date()) {
  return getScheduleState(exam, now) !== "finalizado";
}

function canDeleteSchedule(exam, now = new Date()) {
  const state = getScheduleState(exam, now);
  return state !== "en_ejecucion" && state !== "finalizado";
}

function toBool(v) {
  return v === true || v === "true" || v === 1 || v === "1";
}

function normalizeRequirement(v) {
  const s = String(v || "").trim().toLowerCase();
  if (["requerido", "opcional", "no_requerido"].includes(s)) return s;
  return "no_requerido";
}

function canAddStudentsFor(exam) {
  return !!exam?.id;
}

function addBlockedReason(exam) {
  return "";
}

function formatRequirementLabel(v) {
  const s = normalizeRequirement(v);
  if (s === "requerido") return "Requerido";
  if (s === "opcional") return "Opcional";
  return "No requerido";
}

function formatAssistantsLabel(asistentes = []) {
  if (!Array.isArray(asistentes) || asistentes.length === 0) return "Sin asistentes";
  if (asistentes.length === 1) {
    const a = asistentes[0];
    return `${a?.nombres || ""} ${a?.apellidos || ""}`.trim() || "1 asistente";
  }
  return `${asistentes.length} asistentes`;
}

function formatStudentEnrollDate(value) {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
    return d.toISOString().slice(0, 10);
  } catch {
    return String(value).slice(0, 10);
  }
}

function getStudentDisplayName(student = {}) {
  const full = `${student?.nombres || ""} ${student?.apellidos || ""}`.trim();
  return full || student?.email || "Estudiante";
}

function getStudentInitials(student = {}) {
  const full = getStudentDisplayName(student);
  const parts = full.split(/\s+/).filter(Boolean);
  return parts.slice(0, 2).map((p) => p[0]?.toUpperCase() || "").join("") || "?";
}

function getEnrollmentOriginMeta(student = {}) {
  const raw =
    String(
      student?.matriculacion_origen ||
      student?.origen_matricula ||
      student?.origen ||
      student?.enrollment_origin ||
      ""
    )
      .trim()
      .toLowerCase();

  if (raw === "sandbox" || raw === "sistema" || raw === "system" || raw === "por_sistema") {
    return {
      label: "Por sistema",
      className: "system",
      sublabel:
        student?.matriculacion_detalle ||
        student?.creado_por_email ||
        student?.admin_email ||
        null,
    };
  }

  if (raw === "manual") {
    return {
      label: "Manual",
      className: "manual",
      sublabel: student?.matriculacion_detalle || null,
    };
  }

  return {
    label: "Automatriculación",
    className: "auto",
    sublabel: student?.matriculacion_detalle || null,
  };
}

function readonlyText(value, fallback = "—") {
  if (value === null || value === undefined) return fallback;
  const s = String(value).trim();
  return s ? s : fallback;
}

function yesNoLabel(value) {
  return value ? "Sí" : "No";
}

function modalityLabelFromBool(isVirtual) {
  return isVirtual ? "Virtual" : "Presencial";
}

function ReadonlyField({ label, value, full = false }) {
  return (
    <div className={`sm-fieldBox ${full ? "full" : ""}`}>
      <label className="sm-label-lg">{label}</label>
      <div className="sm-readonlyBox">{readonlyText(value)}</div>
    </div>
  );
}

function ReadonlyTextareaField({ label, value }) {
  return (
    <div className="sm-fieldBox full">
      <label className="sm-label-lg">{label}</label>
      <div className="sm-readonlyTextarea">{readonlyText(value)}</div>
    </div>
  );
}

export default function ScheduleManager() {
  const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
  const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[0];

  const { user, currentRole, getCurrentRole } = useAuth();

  // ===== FACULTY SCOPE (igual que AdminCursos) =====
  const {
    isGlobalAdmin,
    isFacultyAdminActive,
    effectiveFacultyId,
    shouldShowSelector: shouldShowFacultySelector,
    selectedFacultyId,
    setSelectedFacultyId,
    faculties,
    forcedFacultyId,
  } = useFacultyScope();

  // ===== ROLES Y PERMISOS PARA HORARIOS DE EXAMEN =====
  // IMPORTANTE:
  // Aquí mandamos por el ROL ACTIVO, no por todos los roles que tiene el usuario.
  // Si el usuario tiene administrador + docente, pero eligió docente,
  // esta pantalla debe comportarse como DOCENTE.
  const activeRole = useMemo(() => {
    const fromContext = String(currentRole || "").toLowerCase().trim();
    if (fromContext) return fromContext;

    const fromGetter = String(getCurrentRole?.() || "").toLowerCase().trim();
    if (fromGetter) return fromGetter;

    const fromStorage = String(
      localStorage.getItem("activeRole") ||
      localStorage.getItem("currentRole") ||
      ""
    )
      .toLowerCase()
      .trim();

    return fromStorage;
  }, [currentRole, getCurrentRole]);

  const isGlobalAdminUser = ["administrador", "admin", "superadmin"].includes(activeRole);

  const isFacultyAdminUser = activeRole === "admin_facultad";

  const isTeacherUser = activeRole === "docente";

  const isAssistantUser = activeRole === "asistente";

  const scheduleRoleLabel = useMemo(() => {
    if (isGlobalAdminUser) return "Administrador global";
    if (isFacultyAdminUser) return "Admin de facultad";
    if (isTeacherUser) return "Docente";
    if (isAssistantUser) return "Asistente";
    return "Sin permisos";
  }, [isGlobalAdminUser, isFacultyAdminUser, isTeacherUser, isAssistantUser]);

  const roleCanCreateSchedule = isGlobalAdminUser || isFacultyAdminUser || isTeacherUser;
  const roleCanDeleteSchedule = isGlobalAdminUser || isFacultyAdminUser;
  const roleCanEditSchedule = isGlobalAdminUser || isFacultyAdminUser || isTeacherUser;
  const roleCanManageScheduleStudents =
    isGlobalAdminUser || isFacultyAdminUser || isTeacherUser || isAssistantUser;
  const roleCanManageScheduleQuestions =
    isGlobalAdminUser || isFacultyAdminUser || isTeacherUser;

  const facultyIds = useMemo(() => getAdminFacultyIds(user), [user]);


  // ==========================
  // FACULTAD: selector con "Todas"
  // ==========================
  const FAC_ALL = "__ALL__";

  // valor del selector: "", "__ALL__", o "123"
  const [facultyPick, setFacultyPick] = useState(() => {
    if (isFacultyAdminUser) return FAC_ALL;

    if (isGlobalAdminUser) return FAC_ALL;

    return "";
  });


  // ✅ Set de facultades permitidas
  const allowedFacultyIds = useMemo(() => {
    if (isGlobalAdminUser) {
      return new Set(
        (faculties || [])
          .map((f) => Number(f.id))
          .filter((n) => Number.isFinite(n) && n > 0)
      );
    }

    if (isFacultyAdminUser) {
      return facultyIds;
    }

    return new Set();
  }, [isGlobalAdminUser, isFacultyAdminUser, faculties, facultyIds]);

  const facultyMode = useMemo(() => {
    if (!shouldShowFacultySelector) return "NONE";

    // ✅ Fix importante:
    // si el rol activo es admin global/admin facultad y facultyPick quedó vacío
    // por venir de docente/asistente, tratamos eso como "Todas".
    if ((isGlobalAdminUser || isFacultyAdminUser) && !facultyPick) {
      return "ALL";
    }

    if (facultyPick === FAC_ALL) return "ALL";

    const id = Number(facultyPick);
    if (Number.isFinite(id) && id > 0) return "ONE";

    return "UNSET";
  }, [shouldShowFacultySelector, facultyPick, isGlobalAdminUser, isFacultyAdminUser]);

  const selectedFacultyIdEffective = useMemo(() => {
    if (facultyMode !== "ONE") return null;
    const id = Number(facultyPick);
    return Number.isFinite(id) && id > 0 ? id : null;
  }, [facultyMode, facultyPick]);

  const facultyLabel = useMemo(() => {
    if (!shouldShowFacultySelector) return "";
    if (facultyMode === "ALL") {
      return isGlobalAdminUser ? "Todas las facultades" : "Todas mis facultades";
    }

    if (facultyMode === "ONE") {
      const f = (faculties || []).find(
        (x) => Number(x.id) === Number(selectedFacultyIdEffective)
      );
      return f?.nombre || `Facultad ${selectedFacultyIdEffective}`;
    }

    return "";
  }, [
    shouldShowFacultySelector,
    facultyMode,
    faculties,
    selectedFacultyIdEffective,
    isGlobalAdminUser,
  ]);

  const params = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const navigate = useNavigate();
  const pendingOpenScheduleIdRef = useRef(
    Number(location?.state?.openScheduleId || 0)
  );
  const pendingOpenScheduleActionRef = useRef(
    String(location?.state?.openScheduleAction || "")
  );

  // ✅ NUEVO: tamaño de página seleccionable
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  const [sortBy, setSortBy] = useState("creado");
  const [sortDir, setSortDir] = useState("desc");
  const [viewMode, setViewMode] = useState("table");
  const [infoModalData, setInfoModalData] = useState(null);

  const [filterAssistantOptions, setFilterAssistantOptions] = useState([]);


  const {
    // horarios
    fetchExamSchedules,
    fetchExamStats,
    createExamSchedule,
    updateExamSchedule,
    deleteExamSchedule,
    // estudiantes
    fetchExamStudents,
    fetchAvailableStudents,
    addStudentToExam,
    removeStudentFromExam,
    updateStudentExamStatus, // (lo mantienes por si lo usas luego)
    setStudentExamEnabled,
    // cursos
    fetchCourses,
    // exámenes
    fetchExams,
    createExam,
    updateExam,
    deleteExam,
    fetchExamAssistantOptions,
  } = useFuncion();

  // --------------------------
  // Estado: filtros y UI
  // --------------------------
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebouncedValue(searchTerm, 250);

  const [filterAssistant, setFilterAssistant] = useState("");
  const [assistantOptions, setAssistantOptions] = useState([]);

  const [filterDate, setFilterDate] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [courseFilter, setCourseFilter] = useState("");
  const [filterModality, setFilterModality] = useState("");
  const [examFrom, setExamFrom] = useState("");

  const [currentPage, setCurrentPage] = useState(1);

  const [initialLoading, setInitialLoading] = useState(true); // solo 1ra carga
  const [isFetching, setIsFetching] = useState(false);        // recargas por filtros/paginación
  // --------------------------
  // Cursos
  // --------------------------
  const [courses, setCourses] = useState([]);
  const [, setError] = useState(null);

  const [routeCourseMeta, setRouteCourseMeta] = useState(() => {
    const c = location?.state?.course;
    if (!c?.id) return null;

    return {
      id: Number(c.id),
      nombre: c.nombre || "",
      codigo_curso: c.codigo_curso || "",
      tipo: c.tipo || "",
      facultad_id: c.facultad_id || null,
    };
  });



  // ✅ Detecta curso desde ruta, querystring o location.state.
  // IMPORTANTE: no dependemos solo de params.id porque puede confundirse con otros IDs.
  const routeCourseId = useMemo(() => {
    const pathMatch = String(location?.pathname || "").match(
      /\/admin\/cursos\/(\d+)\/examenes(?:\/|$)/
    );

    const candidates = [
      params?.cursoId,
      params?.curso_id,
      params?.courseId,
      params?.course,
      pathMatch?.[1],
      searchParams.get("curso_id"),
      searchParams.get("cursoId"),
      searchParams.get("course"),
      location?.state?.courseId,
      location?.state?.cursoId,
      location?.state?.course?.id,
    ];

    for (const value of candidates) {
      const n = Number(value);
      if (Number.isFinite(n) && n > 0) return String(n);
    }

    return "";
  }, [params, searchParams, location?.pathname, location?.state]);


  useEffect(() => {
    // Cuando el usuario cambia de rol activo desde el Header,
    // facultyPick no se recalcula solo porque useState solo corre una vez.
    // Si cambia a admin global o admin de facultad, debe volver a "Todas".
    if (isGlobalAdminUser || isFacultyAdminUser) {
      setFacultyPick((prev) => (prev ? prev : FAC_ALL));
      return;
    }

    // Si cambia a docente/asistente y NO estás dentro de un curso específico,
    // limpiamos la facultad para evitar filtros viejos de admin.
    if (!routeCourseId) {
      setFacultyPick("");
    }
  }, [activeRole, isGlobalAdminUser, isFacultyAdminUser, routeCourseId]);

  // ✅ Este es el que se usa en TODO el componente
  const effectiveCourseFilter = routeCourseId ? String(routeCourseId) : courseFilter;

  const isCourseContextLocked = Boolean(routeCourseId);

  const selectedCourseMeta = useMemo(() => {
    if (!effectiveCourseFilter) return null;

    const fromCourses =
      (courses || []).find((c) => String(c.id) === String(effectiveCourseFilter)) || null;

    if (fromCourses) return fromCourses;

    if (routeCourseMeta && String(routeCourseMeta.id) === String(effectiveCourseFilter)) {
      return routeCourseMeta;
    }

    const stateCourse = location?.state?.course;

    if (stateCourse && String(stateCourse.id) === String(effectiveCourseFilter)) {
      return stateCourse;
    }

    return null;
  }, [courses, effectiveCourseFilter, routeCourseMeta, location?.state]);

  useEffect(() => {
    if (!routeCourseId) return;
    if (selectedCourseMeta?.nombre) return;

    let alive = true;

    (async () => {
      try {
        const data = await fetchCourses({
          page: 1,
          limit: 1000,
          showAll: true,
          search: "",
        });

        const arr = Array.isArray(data) ? data : data?.courses || [];

        const found = arr.find((c) => String(c.id) === String(routeCourseId));

        if (!alive || !found) return;

        setRouteCourseMeta({
          id: Number(found.id),
          nombre: found.nombre || "",
          codigo_curso: found.codigo_curso || "",
          tipo: found.tipo || "",
          facultad_id: found.facultad_id || null,
        });
      } catch (err) {
        console.error("No se pudo cargar el curso de contexto:", err);
      }
    })();

    return () => {
      alive = false;
    };
  }, [routeCourseId, selectedCourseMeta?.nombre, fetchCourses]);

  const isExamOnlyCourseContext = useMemo(() => {
    return !!routeCourseId && String(selectedCourseMeta?.tipo || "") === "examen_solo";
  }, [routeCourseId, selectedCourseMeta]);

  const facultyFilterLocked = isExamOnlyCourseContext;

  const backTo = routeCourseId ? `/admin/cursos/${routeCourseId}` : "/admin";
  const backLabel = routeCourseId ? "Volver al curso" : "Volver";


  useEffect(() => {
    let mounted = true;

    const run = async () => {
      try {
        if (!fetchExamAssistantOptions) return;

        const selectedCourseId = Number(effectiveCourseFilter);

        // ✅ Si no hay curso filtrado, NO intentes cargar asistentes.
        // Esto evita el error en consola cuando estás viendo "todos los cursos".
        if (!Number.isFinite(selectedCourseId) || selectedCourseId <= 0) {
          if (mounted) {
            setFilterAssistantOptions([]);
            setFilterAssistant("");
          }
          return;
        }

        const raw = await fetchExamAssistantOptions({
          curso_id: selectedCourseId,
        });

        if (!mounted) return;

        const normalized = Array.isArray(raw?.assistants)
          ? raw.assistants
          : Array.isArray(raw?.items)
            ? raw.items
            : Array.isArray(raw)
              ? raw
              : [];

        setFilterAssistantOptions(normalized);

        // ✅ Si el asistente filtrado ya no pertenece al curso seleccionado, limpiarlo
        const allowedIds = new Set(
          normalized
            .map((a) => Number(a?.usuario_id ?? a?.id))
            .filter((n) => Number.isFinite(n) && n > 0)
        );

        setFilterAssistant((prev) => {
          if (!prev) return "";
          return allowedIds.has(Number(prev)) ? prev : "";
        });
      } catch (err) {
        if (mounted) {
          setFilterAssistantOptions([]);
          setFilterAssistant("");
        }
      }
    };

    run();

    return () => {
      mounted = false;
    };
  }, [fetchExamAssistantOptions, effectiveCourseFilter]);

  useEffect(() => {
    if (!routeCourseId) return;

    // Fuerza el filtro al curso de la ruta
    setCourseFilter(String(routeCourseId));

    // (Opcional) también lo guarda para que al recargar siga igual
    try {
      const saved = JSON.parse(localStorage.getItem("sm:filters") || "{}");
      localStorage.setItem(
        "sm:filters",
        JSON.stringify({ ...saved, courseFilter: String(routeCourseId) })
      );
    } catch { }
  }, [routeCourseId]);


  // --------------------------
  // Datos horarios
  // --------------------------
  const [examsData, setExamsData] = useState({
    exams: [],
    pagination: {
      page: 1,
      limit: DEFAULT_PAGE_SIZE,
      total: 0,
      totalPages: 0,
      hasNext: false,
      hasPrev: false,
    },
  });


  const [examStats, setExamStats] = useState({
    total_horarios: 0,
    horarios_activos: 0,
    cupos_totales: 0,
    cupos_ocupados: 0,
    estudiantes_agendados: 0,
  });

  const visibleSchedules = useMemo(() => {
    return Array.isArray(examsData?.exams) ? examsData.exams : [];
  }, [examsData]);

  const existingScheduleCodes = useMemo(() => {
    const list = Array.isArray(examsData?.exams) ? examsData.exams : [];
    return list
      .map((item) => String(item?.codigo_horario || "").trim().toUpperCase())
      .filter(Boolean);
  }, [examsData]);

  const filteredSummary = useMemo(() => {
    const total = visibleSchedules.length;
    const activos = visibleSchedules.filter((x) => {
      const state = getScheduleState(x);
      return state === "activo" || state === "en_ejecucion";
    }).length;

    const cupos = visibleSchedules.reduce(
      (acc, x) => acc + Number(x?.cupos_disponibles || 0),
      0
    );

    return { total, activos, cupos };
  }, [visibleSchedules]);

  const requestSort = useCallback((key) => {
    setSortBy((prevKey) => {
      if (prevKey === key) {
        setSortDir((prevDir) => (prevDir === "asc" ? "desc" : "asc"));
        return prevKey;
      }
      setSortDir(key === "creado" ? "desc" : "asc");
      return key;
    });
  }, []);

  const renderSortIcon = useCallback(
    (key) => {
      if (sortBy !== key) return <ArrowUpDown size={14} style={{ opacity: 0.55 }} />;
      return sortDir === "asc" ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
    },
    [sortBy, sortDir]
  );

  const sortedSchedules = useMemo(() => {
    const list = [...visibleSchedules];

    list.sort((a, b) => {
      let av;
      let bv;

      switch (sortBy) {
        case "creado":
          av =
            new Date(a?.fecha_creacion || a?.created_at || 0).getTime() ||
            Number(a?.id || 0);
          bv =
            new Date(b?.fecha_creacion || b?.created_at || 0).getTime() ||
            Number(b?.id || 0);
          break;
        case "fecha":
          av = `${String(a?.fecha_examen || "")} ${String(a?.hora_inicio || "")}`;
          bv = `${String(b?.fecha_examen || "")} ${String(b?.hora_inicio || "")}`;
          break;
        case "modalidad":
          av = toBool(a?.is_virtual) ? "Virtual" : "Presencial";
          bv = toBool(b?.is_virtual) ? "Virtual" : "Presencial";
          break;
        case "cupos":
          av = Number(a?.cupos_ocupados || 0);
          bv = Number(b?.cupos_ocupados || 0);
          break;
        case "preguntas":
          av = Number(a?.numero_preguntas || 0);
          bv = Number(b?.numero_preguntas || 0);
          break;
        case "estado":
          av = getScheduleState(a);
          bv = getScheduleState(b);
          break;
        default:
          av = String(a?.[sortBy] || "");
          bv = String(b?.[sortBy] || "");
      }

      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

    return list;
  }, [visibleSchedules, sortBy, sortDir]);

  const generateAccessCode = useCallback(() => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let code = "";
    for (let i = 0; i < 10; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  }, []);

  const generateScheduleCodeLocal = useCallback((existingCodes = [], courseCode = "") => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    const normalize = (v) => String(v || "").trim().toUpperCase();
    const prefix = sanitizeScheduleCodePrefix(courseCode);

    const used = new Set(
      (Array.isArray(existingCodes) ? existingCodes : [])
        .map((item) => normalize(item?.codigo_horario ?? item))
        .filter(Boolean)
    );

    for (let attempt = 0; attempt < 100; attempt++) {
      let suffix = "";
      for (let i = 0; i < 8; i++) {
        suffix += chars.charAt(Math.floor(Math.random() * chars.length));
      }

      const code = `${prefix}-${suffix}`;
      if (!used.has(code)) return code;
    }

    return `${prefix}-${Date.now().toString(36).toUpperCase().slice(-8)}`;
  }, []);


  // --------------------------
  // Crear horario
  // --------------------------
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [createScheduleTab, setCreateScheduleTab] = useState("unitaria");
  const [formData, setFormData] = useState({
    curso_id: "",
    is_virtual: false,
    fecha_examen: "",
    hora_inicio: "",
    hora_fin: "",
    tiempo_limite_minutos: 60,
    numero_preguntas: 25,
    cupos_disponibles: 20,
    porcentaje_aprobacion: 70,
    permitir_navegacion: false,
    mostrar_resumen_final: false,
    requiere_codigo: true,
    codigo_acceso: "",
    codigo_horario: "",
    ubicacion_info: "",
    nota_maxima: 10,

    habilitacion_unitaria_estudiantes: false,
    camara_nivel: "requerido",
    microfono_nivel: "requerido",
    numero_fotos_requeridas: 3,
    umbral_decibeles_microfono: 55,
    cierre_por_penalizaciones: false,
    limite_penalizaciones: 3,

    asistentes_ids: [],
  });


  const [isConfigHorarioOpen, setIsConfigHorarioOpen] = useState(true);
  const [isConfigSeguridadOpen, setIsConfigSeguridadOpen] = useState(true);
  const [assistantSearch, setAssistantSearch] = useState("");
  const [assistantSuggestionsOpen, setAssistantSuggestionsOpen] = useState(false);


  useEffect(() => {
    try {
      localStorage.setItem(
        "sm:filters",
        JSON.stringify({
          courseFilter,
          filterModality,
          filterStatus,
          filterDate,
          examFrom,
          searchTerm,
          filterAssistant,
        })
      );
    } catch { }
  }, [courseFilter, filterModality, filterStatus, filterDate, examFrom, searchTerm, filterAssistant]);

  useEffect(() => {
    let mounted = true;

    const run = async () => {
      try {
        const selectedCourseId = Number(formData?.curso_id);

        if (!fetchExamAssistantOptions) return;

        // si no hay curso seleccionado en el form, no cargues asistentes
        if (!Number.isFinite(selectedCourseId) || selectedCourseId <= 0) {
          if (mounted) {
            setAssistantOptions([]);
            setFormData((prev) => ({ ...prev, asistentes_ids: [] }));
          }
          return;
        }
        const raw = await fetchExamAssistantOptions({
          curso_id: selectedCourseId,
        });

        if (!mounted) return;

        const normalized = Array.isArray(raw) ? raw : [];
        setAssistantOptions(normalized);

        // limpia asistentes seleccionados que ya no pertenezcan al curso elegido
        const allowedIds = new Set(
          normalized.map((a) => Number(a.usuario_id)).filter((n) => Number.isFinite(n))
        );

        setFormData((prev) => ({
          ...prev,
          asistentes_ids: Array.isArray(prev.asistentes_ids)
            ? prev.asistentes_ids.filter((id) => allowedIds.has(Number(id)))
            : [],
        }));
      } catch (e) {
        if (mounted) {
          setAssistantOptions([]);
          setFormData((prev) => ({ ...prev, asistentes_ids: [] }));
        }
      }
    };

    run();

    return () => {
      mounted = false;
    };
  }, [fetchExamAssistantOptions, formData.curso_id]);
  // ✅ Restore filtros (curso/modalidad/estado/fechas) al recargar
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("sm:filters") || "{}");

      if (!routeCourseId && saved?.courseFilter != null) setCourseFilter(String(saved.courseFilter));
      if (saved?.filterModality != null) setFilterModality(String(saved.filterModality));
      if (saved?.filterStatus != null) setFilterStatus(String(saved.filterStatus));
      if (saved?.filterDate != null) setFilterDate(String(saved.filterDate));
      if (saved?.examFrom != null) setExamFrom(String(saved.examFrom));
      if (saved?.searchTerm != null) setSearchTerm(String(saved.searchTerm));
      if (saved?.filterAssistant != null) setFilterAssistant(String(saved.filterAssistant));
    } catch { }
  }, [routeCourseId]);

  const editingScheduleState = useMemo(() => {
    return editingSchedule ? getScheduleState(editingSchedule) : null;
  }, [editingSchedule]);

  const isEditingFinalized = editingScheduleState === "finalizado";
  const isEditingRunning = editingScheduleState === "en_ejecucion";

  const isViewOnlyByRole = !!editingSchedule && !roleCanEditSchedule;
  const isViewOnlySchedule = !!editingSchedule && (isEditingFinalized || isViewOnlyByRole);
  const isStrictEditBlocked = isViewOnlySchedule;


  useEffect(() => {
    if (!showAddModal) return;

    if (editingSchedule) {
      if (isViewOnlySchedule) {
        setIsConfigHorarioOpen(true);
        setIsConfigSeguridadOpen(true);
      } else {
        setIsConfigHorarioOpen(false);
        setIsConfigSeguridadOpen(false);
      }
    } else {
      setIsConfigHorarioOpen(true);
      setIsConfigSeguridadOpen(true);
    }
  }, [showAddModal, editingSchedule, isViewOnlySchedule]);


  // ✅ Prefill: si hay curso filtrado, al abrir "Nuevo Horario" lo selecciona
  useEffect(() => {
    if (!showAddModal) return;

    setFormData((prev) => {
      const hasFilter = String(effectiveCourseFilter || "").trim() !== "";
      if (!hasFilter) return prev;

      // ✅ si hay curso en ruta, SIEMPRE lo fuerza
      if (routeCourseId) {
        return { ...prev, curso_id: String(effectiveCourseFilter) };
      }

      // ✅ si NO hay ruta, respeta lo que el usuario ya eligió
      const hasAlready = String(prev?.curso_id || "").trim() !== "";
      if (hasAlready) return prev;

      return { ...prev, curso_id: String(effectiveCourseFilter) };
    });
  }, [showAddModal, effectiveCourseFilter, routeCourseId]);



  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Edit % aprobación
  const [showEditPctModal, setShowEditPctModal] = useState(false);
  const [scheduleForPct, setScheduleForPct] = useState(null);
  const [pctValue, setPctValue] = useState("");
  const [pctError, setPctError] = useState("");
  const [pctSubmitting, setPctSubmitting] = useState(false);

  // --------------------------
  // Estudiantes por horario
  // --------------------------
  const [showStudentsModal, setShowStudentsModal] = useState(false);
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const [examStudents, setExamStudents] = useState([]);
  const [availableStudents, setAvailableStudents] = useState([]);
  // ✅ Multi-selección
  const [selectedStudentIds, setSelectedStudentIds] = useState(() => new Set());
  const [addStudentQuery, setAddStudentQuery] = useState("");
  const debouncedAddStudentQuery = useDebouncedValue(addStudentQuery, 250);

  const [selectedExamStudentIds, setSelectedExamStudentIds] = useState(() => new Set());
  const examStudentsMasterRef = useRef(null);

  const examStudentIdsOnPage = useMemo(
    () => (Array.isArray(examStudents) ? examStudents.map((s) => Number(s.id)) : []),
    [examStudents]
  );

  const examStudentsAllSelected = useMemo(
    () =>
      examStudentIdsOnPage.length > 0 &&
      examStudentIdsOnPage.every((id) => selectedExamStudentIds.has(id)),
    [examStudentIdsOnPage, selectedExamStudentIds]
  );

  const examStudentsSomeSelected = useMemo(
    () => examStudentIdsOnPage.some((id) => selectedExamStudentIds.has(id)),
    [examStudentIdsOnPage, selectedExamStudentIds]
  );

  useEffect(() => {
    if (examStudentsMasterRef.current) {
      examStudentsMasterRef.current.indeterminate =
        !examStudentsAllSelected && examStudentsSomeSelected;
    }
  }, [examStudentsAllSelected, examStudentsSomeSelected]);

  const clearExamStudentSelection = useCallback(() => {
    setSelectedExamStudentIds(new Set());
  }, []);

  const toggleAllExamStudents = useCallback(() => {
    setSelectedExamStudentIds((prev) => {
      const next = new Set(prev);
      if (examStudentsAllSelected) {
        examStudentIdsOnPage.forEach((id) => next.delete(id));
      } else {
        examStudentIdsOnPage.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [examStudentsAllSelected, examStudentIdsOnPage]);

  const toggleOneExamStudent = useCallback((studentId) => {
    setSelectedExamStudentIds((prev) => {
      const next = new Set(prev);
      if (next.has(studentId)) next.delete(studentId);
      else next.add(studentId);
      return next;
    });
  }, []);

  const [availableStudentsRaw, setAvailableStudentsRaw] = useState([]); // cache bruto
  const [availableStudentsView, setAvailableStudentsView] = useState([]); // lo que se muestra (limitado)
  const [availFetching, setAvailFetching] = useState(false);

  const availReqIdRef = useRef(0);

  const [studentsLoading, setStudentsLoading] = useState(false);
  // Busy por estudiante (evita bloquear toda la tabla)
  const [studentBusy, setStudentBusy] = useState(() => new Set());

  const markStudentBusy = useCallback((id, busy) => {
    setStudentBusy((prev) => {
      const next = new Set(prev);
      if (busy) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);



  // --------------------------
  // Exámenes dentro del horario
  // --------------------------
  const [showManageExams, setShowManageExams] = useState(false);
  const [scheduleForExams, setScheduleForExams] = useState(null);
  const [examsInSchedule, setExamsInSchedule] = useState([]);
  const [examForm, setExamForm] = useState({
    nombre: "",
    descripcion: "",
    numero_preguntas: 10,
    tiempo_limite_minutos: 60,
    activo: true,
  });
  const [examFormErrors, setExamFormErrors] = useState({});
  const [examSubmitting, setExamSubmitting] = useState(false);
  const [editingExamId, setEditingExamId] = useState(null);
  const [examPanelOpen, setExamPanelOpen] = useState(false);

  // Preguntas modal
  const [showQuestions, setShowQuestions] = useState(false);
  const [scheduleForQuestions, setScheduleForQuestions] = useState(null);

  // --------------------------
  // SELECCIÓN MÚLTIPLE — horarios
  // --------------------------
  const [selectedSchedules, setSelectedSchedules] = useState(() => new Set());
  const scheduleMasterRef = useRef(null);
  const [scheduleBulkBusy, setScheduleBulkBusy] = useState(false);

  const scheduleIdsOnPage = useMemo(
    () => (Array.isArray(examsData.exams) ? examsData.exams.map((e) => e.id) : []),
    [examsData.exams]
  );

  const schedulesAllSelected = useMemo(
    () => scheduleIdsOnPage.length > 0 && scheduleIdsOnPage.every((id) => selectedSchedules.has(id)),
    [scheduleIdsOnPage, selectedSchedules]
  );

  const schedulesSomeSelected = useMemo(
    () => scheduleIdsOnPage.some((id) => selectedSchedules.has(id)),
    [scheduleIdsOnPage, selectedSchedules]
  );

  const schedulesSelectedCount = selectedSchedules.size;

  useEffect(() => {
    if (scheduleMasterRef.current) {
      scheduleMasterRef.current.indeterminate = !schedulesAllSelected && schedulesSomeSelected;
    }
  }, [schedulesAllSelected, schedulesSomeSelected]);

  useEffect(() => {
    // limpiar selección al cambiar página o data
    setSelectedSchedules(new Set());
  }, [currentPage, examsData.exams]);

  const toggleAllSchedules = useCallback(() => {
    setSelectedSchedules((prev) => {
      const next = new Set(prev);
      if (schedulesAllSelected) scheduleIdsOnPage.forEach((id) => next.delete(id));
      else scheduleIdsOnPage.forEach((id) => next.add(id));
      return next;
    });
  }, [scheduleIdsOnPage, schedulesAllSelected]);

  const toggleOneSchedule = useCallback((id) => {
    setSelectedSchedules((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const openEditSchedule = useCallback((exam) => {
    setEditingSchedule(exam);
    const roleReadOnly = !roleCanEditSchedule;

    setFormData({
      curso_id: String(exam?.curso_id || ""),
      is_virtual: !!exam?.is_virtual,
      fecha_examen: String(exam?.fecha_examen || "").slice(0, 10),
      hora_inicio: String(exam?.hora_inicio || "").slice(0, 8),
      hora_fin: String(exam?.hora_fin || "").slice(0, 8),
      tiempo_limite_minutos: Number(exam?.tiempo_limite_minutos ?? 60),
      numero_preguntas: Number(exam?.numero_preguntas ?? 25),
      cupos_disponibles: Number(exam?.cupos_disponibles ?? 20),
      porcentaje_aprobacion: Number(exam?.porcentaje_aprobacion ?? 70),
      permitir_navegacion: !!exam?.permitir_navegacion,
      mostrar_resumen_final: !!exam?.mostrar_resumen_final,
      requiere_codigo: !!exam?.requiere_codigo,
      codigo_acceso: String(exam?.codigo_acceso || ""),
      codigo_horario: String(exam?.codigo_horario || ""),
      ubicacion_info: String(exam?.ubicacion_info || ""),
      nota_maxima: exam?.nota_maxima ?? 10,

      habilitacion_unitaria_estudiantes: !!exam?.habilitacion_unitaria_estudiantes,
      camara_nivel: String(exam?.camara_nivel || "no_requerido"),
      microfono_nivel: String(exam?.microfono_nivel || "no_requerido"),
      numero_fotos_requeridas: Number(exam?.numero_fotos_requeridas ?? 0),
      umbral_decibeles_microfono:
        exam?.umbral_decibeles_microfono == null ? "" : String(exam.umbral_decibeles_microfono),
      cierre_por_penalizaciones: !!exam?.cierre_por_penalizaciones,
      limite_penalizaciones: Number(exam?.limite_penalizaciones ?? MIN_SCHEDULE_PENALTIES),

      asistentes_ids: Array.isArray(exam?.asistentes)
        ? exam.asistentes.map((a) => Number(a?.usuario_id)).filter(Boolean)
        : [],
    });

    setFormErrors({});
    setCreateScheduleTab("unitaria");
    setShowAddModal(true);
  }, []);

  useEffect(() => {
    const requestedId = Number(location?.state?.openScheduleId || pendingOpenScheduleIdRef.current || 0);
    if (!Number.isFinite(requestedId) || requestedId <= 0) return;
    if (String(location?.state?.openScheduleAction || pendingOpenScheduleActionRef.current || "")) return;
    if (showAddModal) return;

    const target = (examsData?.exams || []).find((exam) => Number(exam?.id) === requestedId);
    if (!target) return;

    pendingOpenScheduleIdRef.current = 0;
    openEditSchedule(target);

    if (location?.state?.openScheduleId) {
      const nextState = { ...(location.state || {}) };
      delete nextState.openScheduleId;
      delete nextState.openScheduleSource;
      navigate(`${location.pathname}${location.search || ""}`, {
        replace: true,
        state: Object.keys(nextState).length ? nextState : null,
      });
    }
  }, [
    examsData,
    location.pathname,
    location.search,
    location.state,
    navigate,
    openEditSchedule,
    showAddModal,
  ]);


  const clearScheduleSelection = useCallback(() => setSelectedSchedules(new Set()), []);

  // --------------------------
  // SELECCIÓN MÚLTIPLE — exámenes del horario
  // --------------------------
  const [selectedExamsInModal, setSelectedExamsInModal] = useState(() => new Set());
  const modalMasterRef = useRef(null);
  const [modalBulkBusy, setModalBulkBusy] = useState(false);

  const modalExamIds = useMemo(
    () => (Array.isArray(examsInSchedule) ? examsInSchedule.map((e) => e.id) : []),
    [examsInSchedule]
  );

  const modalAllSelected = useMemo(
    () => modalExamIds.length > 0 && modalExamIds.every((id) => selectedExamsInModal.has(id)),
    [modalExamIds, selectedExamsInModal]
  );

  const modalSomeSelected = useMemo(
    () => modalExamIds.some((id) => selectedExamsInModal.has(id)),
    [modalExamIds, selectedExamsInModal]
  );

  const modalSelectedCount = selectedExamsInModal.size;

  useEffect(() => {
    if (modalMasterRef.current) {
      modalMasterRef.current.indeterminate = !modalAllSelected && modalSomeSelected;
    }
  }, [modalAllSelected, modalSomeSelected]);

  const clearModalSelection = useCallback(() => setSelectedExamsInModal(new Set()), []);

  const toggleAllModalExams = useCallback(() => {
    setSelectedExamsInModal((prev) => {
      const next = new Set(prev);
      if (modalAllSelected) modalExamIds.forEach((id) => next.delete(id));
      else modalExamIds.forEach((id) => next.add(id));
      return next;
    });
  }, [modalAllSelected, modalExamIds]);

  const toggleOneModalExam = useCallback((id) => {
    setSelectedExamsInModal((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // --------------------------
  // Helpers formato (memoizados por referencia estable)
  // --------------------------

  const formatTime = useCallback((t) => {
    if (!t) return "";
    try {
      return new Date(`2000-01-01T${t}`).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
    } catch {
      return String(t);
    }
  }, []);

  const formatDate = useCallback((d) => {
    if (!d) return "";
    try {
      return new Date(d).toLocaleDateString("es-ES", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return String(d);
    }
  }, []);

  const formatTimeShort = useCallback((t) => {
    if (!t) return "";
    try {
      return new Date(`2000-01-01T${t}`).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
    } catch {
      return String(t);
    }
  }, []);

  const formatDateLong = useCallback((d) => {
    if (!d) return "";
    try {
      return new Date(d).toLocaleDateString("es-ES", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return String(d);
    }
  }, []);

  // --------------------------
  // Cargar cursos
  // --------------------------

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        // ✅ Si mostramos selector, solo bloquea si está UNSET (si decides obligar)
        // Como ahora existe "Todas", normalmente nunca será UNSET.
        if (shouldShowFacultySelector && facultyMode === "UNSET") {
          if (alive) setCourses([]);
          return;
        }

        const pageSize = 200;
        let p = 1;
        let out = [];

        while (true) {
          const data = await fetchCourses({
            page: p,
            limit: pageSize,
            search: "",
            showAll: true,
            // ✅ Si es ONE: pide solo esa facultad
            // ✅ Si es ALL: no mandes facultad_id (trae todo y filtramos por allowedFacultyIds si aplica)
            facultad_id: facultyMode === "ONE" ? selectedFacultyIdEffective : undefined,
          });

          const arr = Array.isArray(data) ? data : (data?.courses || []);
          out = out.concat(arr);

          const pag = data?.pagination;
          if (pag?.totalPages) {
            if (p >= pag.totalPages) break;
          } else if (arr.length < pageSize) break;
          p++;
        }

        // ✅ Scoping final:
        // - Global admin ALL: no filtra
        // - Admin_facultad ALL: filtra a sus facultades asignadas
        // - ONE: si el backend filtró, ya viene ok; igual dejamos guard rails.
        const scoped = out.filter((c) => {
          const fid = pickFacultyId(c);
          if (fid == null) return false;

          if (isGlobalAdminUser) {
            if (facultyMode === "ONE") return fid === selectedFacultyIdEffective;
            return true;
          }

          // admin_facultad: solo asignadas
          if (!allowedFacultyIds.has(fid)) return false;
          if (facultyMode === "ONE") return fid === selectedFacultyIdEffective;
          return true;
        });

        if (alive) setCourses(scoped);
      } catch (e) {
        console.error(e);
        if (alive) setCourses([]);
      }
    })();

    return () => { alive = false; };
  }, [
    fetchCourses,
    shouldShowFacultySelector,
    facultyMode,
    selectedFacultyIdEffective,
    allowedFacultyIds,
    isGlobalAdminUser,
    isFacultyAdminUser,
  ]);



  const courseOptions = useMemo(
    () =>
      (courses || []).map((c) => ({
        value: String(c.id),
        label: `${c.nombre}${c.codigo_curso ? ` (${c.codigo_curso})` : ""}`,
        codigo_curso: c.codigo_curso || "",
        tipo: c.tipo,
      })),
    [courses]
  );



  useEffect(() => {
    if (routeCourseId) return;
    if (!courseFilter) return;

    const exists = courseOptions.some((o) => String(o.value) === String(courseFilter));
    if (!exists) setCourseFilter("");
  }, [courseOptions, courseFilter, routeCourseId]);

  const openManageQuestions = useCallback((schedule) => {
    // “exam” ahora es el horario
    setScheduleForQuestions({
      ...schedule,
      // por compat:
      id: schedule.id,                 // horario_examen_id
      horario_examen_id: schedule.id,  // opcional
      curso_id: schedule.curso_id,
      curso_tipo: courseOptions.find(c => Number(c.value) === Number(schedule.curso_id))?.tipo ?? null,
      nombre: schedule.nombre ?? `Horario ${schedule.id}`, // si no tienes nombre en horario
    });
    setShowQuestions(true);
  }, [courseOptions]);


  const allowedCourseIds = useMemo(() => {
    const s = new Set();
    for (const c of (courses || [])) {
      const cid = pickCourseId(c) ?? Number(c?.id);
      if (Number.isFinite(cid)) s.add(cid);
    }
    return s;
  }, [courses]);

  // --------------------------
  // Cargar horarios (con filtros) - SOLO params object
  // --------------------------
  const examsReqIdRef = useRef(0);

  function dateOnlyISO(fecha) {
    const m = String(fecha || "").match(/^(\d{4}-\d{2}-\d{2})/);
    return m ? m[1] : "";
  }
  function hhmm(h) {
    if (!h) return "";
    const [hh, mm] = String(h).split(":");
    if (hh == null || mm == null) return "";
    return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
  }
  function scheduleSortKey(x) {
    const d = dateOnlyISO(x?.fecha_examen);
    const t = hhmm(x?.hora_inicio);
    // ISO-like para comparar con localeCompare
    return `${d} ${t}`;
  }

  const loadExams = useCallback(
    async (params) => {
      const reqId = ++examsReqIdRef.current;

      // ✅ clean definido en el scope del callback (sirve en try/catch)
      const requestedLimitRaw = params?.limit ?? pageSize;
      const requestedLimit = Number(requestedLimitRaw);

      const clean = {
        page: Number(params?.page ?? 1),
        limit: Number.isFinite(requestedLimit) ? requestedLimit : pageSize,
      };


      try {
        setIsFetching(true);
        setError(null);

        const search = params?.search?.trim?.() ? params.search.trim() : "";
        const fecha = params?.fecha?.trim?.() ? params.fecha.trim() : "";
        const status = params?.status?.trim?.() ? params.status.trim() : "";
        const fecha_desde = params?.fecha_desde?.trim?.() ? params.fecha_desde.trim() : "";
        const fecha_hasta = params?.fecha_hasta?.trim?.() ? params.fecha_hasta.trim() : "";
        const course = params?.course ?? effectiveCourseFilter ?? "";
        const modalidad = params?.modalidad ?? "";

        if (search) clean.search = search;
        if (fecha) clean.fecha = fecha;
        if (status && status !== "finalizado") clean.status = status;
        if (fecha_desde) clean.fecha_desde = fecha_desde;
        if (fecha_hasta) clean.fecha_hasta = fecha_hasta;

        // curso puede ser id numérico o código
        if (typeof course === "string" && course.trim()) {
          const asNum = Number(course);
          if (!Number.isNaN(asNum) && String(asNum) === course) clean.curso_id = asNum;
          else clean.codigo_curso = course.trim();
        }

        // ✅ modalidad -> enviar robusto (compatible con backends distintos)
        if (modalidad === "virtual") {
          clean.is_virtual = "true";
          clean.modalidad = "virtual";
        } else if (modalidad === "presencial") {
          clean.is_virtual = "false";
          clean.modalidad = "presencial";
        }
        // ✅ Si mostramos selector y está UNSET (si decides obligar), no consultes
        if (shouldShowFacultySelector && facultyMode === "UNSET") {
          setExamsData({
            exams: [],
            pagination: { page: 1, limit: clean.limit, total: 0, totalPages: 0, hasNext: false, hasPrev: false },
          });
          setInitialLoading(false);
          return;
        }

        // ✅ ONE => mandar facultad_id; ALL => no mandar
        if (!facultyFilterLocked && facultyMode === "ONE" && selectedFacultyIdEffective) {
          clean.facultad_id = Number(selectedFacultyIdEffective);
        }

        const data = await fetchExamSchedules(clean);

        // ✅ anti-race
        if (reqId !== examsReqIdRef.current) return;

        const rawList = data?.schedules || [];

        // ✅ Scope cuando: NO hay curso filtrado (Todos los cursos) y estamos en modo ALL
        // (en ONE ya viene por facultad_id o por courses scoping)
        const mustScope =
          !clean?.curso_id &&
          !clean?.codigo_curso &&
          shouldShowFacultySelector &&
          (facultyMode === "ALL" || facultyMode === "ONE");

        const scopedList = mustScope
          ? (
            allowedCourseIds.size === 0
              ? []
              : rawList.filter((x) => {
                const cid = pickCourseId(x);
                return cid != null && allowedCourseIds.has(cid);
              })
          )
          : rawList;

        // ✅ Fallback: si backend no filtra modalidad, filtramos acá
        const serverMaybeFiltered = scopedList;
        const finalList =
          modalidad === "virtual"
            ? serverMaybeFiltered.filter((x) => {
              const v = x?.is_virtual;
              return v === true || v === "true" || v === 1 || v === "1";
            })
            : modalidad === "presencial"
              ? serverMaybeFiltered.filter((x) => {
                const v = x?.is_virtual;
                return v === false || v === "false" || v === 0 || v === "0" || v == null;
              })
              : serverMaybeFiltered;

        const now = new Date();

        const normalizedList = finalList.map((x) => ({
          ...x,

          preguntas_registradas: toFiniteNumber(
            x?.preguntas_registradas ??
            x?.preguntas_configuradas ??
            x?.preguntas_asignadas ??
            x?.questions_count ??
            x?.total_preguntas,
            0
          ),

          preguntas_reales: toFiniteNumber(
            x?.preguntas_reales ??
            x?.preguntas_registradas ??
            x?.preguntas_configuradas ??
            x?.total_preguntas,
            0
          ),

          preguntas_faltantes: Math.max(
            0,
            toFiniteNumber(x?.numero_preguntas, 0) -
            pickPositiveNumber(
              x?.preguntas_registradas,
              x?.preguntas_configuradas,
              x?.preguntas_asignadas,
              x?.questions_count,
              x?.total_preguntas,
              x?.preguntas_reales
            )
          ),

          preguntas_completas:
            toFiniteNumber(x?.numero_preguntas, 0) > 0 &&
            pickPositiveNumber(
              x?.preguntas_registradas,
              x?.preguntas_configuradas,
              x?.preguntas_asignadas,
              x?.questions_count,
              x?.total_preguntas,
              x?.preguntas_reales
            ) >= toFiniteNumber(x?.numero_preguntas, 0),

          codigo_acceso: String(
            x?.codigo_acceso ??
            x?.codigo ??
            x?.access_code ??
            ""
          ),
        }));

        const finalByStatus = (() => {
          if (!status) return normalizedList;
          return normalizedList.filter((x) => getScheduleState(x, now) === status);
        })();

        // ✅ Orden por fecha y hora de inicio (asc)
        finalByStatus.sort((a, b) => scheduleSortKey(a).localeCompare(scheduleSortKey(b)));

        const p = data?.pagination || {};
        const page = Number(p.page ?? clean.page) || clean.page;
        const limit = Number(p.limit ?? clean.limit) || clean.limit;

        const isClientFilteredState = status === "finalizado" || status === "en_ejecucion";

        // si finalizado: la paginación del backend no sirve (porque filtramos en UI)
        const totalForUi = isClientFilteredState ? finalByStatus.length : (Number(p.total ?? 0) || 0);
        const totalPagesForUi = isClientFilteredState ? 1 : (Number(p.totalPages ?? 0) || 0);
        const hasNextForUi = isClientFilteredState ? false : !!p.hasNext;
        const hasPrevForUi = isClientFilteredState ? false : !!p.hasPrev;

        setExamsData({
          exams: finalByStatus,
          pagination: {
            page: isClientFilteredState ? 1 : page,
            limit,
            total: totalForUi,
            totalPages: totalPagesForUi,
            hasNext: hasNextForUi,
            hasPrev: hasPrevForUi,
          },
        });


        setInitialLoading(false);
      } catch (err) {
        if (reqId !== examsReqIdRef.current) return;
        console.error("❌ Error cargando horarios:", err);
        setError(err?.message || "Error cargando horarios");

        setExamsData({
          exams: [],
          pagination: {
            page: clean.page ?? 1,
            limit: clean.limit ?? pageSize,
            total: 0,
            totalPages: 0,
            hasNext: false,
            hasPrev: false,
          },
        });

        setInitialLoading(false);
      } finally {
        if (reqId === examsReqIdRef.current) setIsFetching(false);
      }
    }, [
    fetchExamSchedules,
    pageSize,
    allowedCourseIds,
    shouldShowFacultySelector,
    facultyMode,
    selectedFacultyIdEffective,
    facultyFilterLocked,
    effectiveCourseFilter,
  ]);



  const loadExamStats = useCallback(async () => {
    try {
      const s = await fetchExamStats();
      setExamStats(s || {
        total_horarios: 0,
        horarios_activos: 0,
        cupos_totales: 0,
        cupos_ocupados: 0,
        estudiantes_agendados: 0,
      });
    } catch (e) {
      console.error(e);
    }
  }, [fetchExamStats]);

  // stats 1 vez
  useEffect(() => {
    loadExamStats();
  }, [loadExamStats]);

  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize]);


  // reset page al cambiar filtros
  useEffect(() => {
    setCurrentPage((p) => (p === 1 ? p : 1));
  }, [
    debouncedSearch,
    filterDate,
    filterStatus,
    effectiveCourseFilter,
    examFrom,
    filterModality,
    filterAssistant,
  ]);


  // carga horarios
  useEffect(() => {
    loadExams({
      page: currentPage,
      limit: pageSize,
      search: debouncedSearch,
      fecha: filterDate,
      status: filterStatus,
      course: effectiveCourseFilter,
      fecha_desde: examFrom,
      modalidad: filterModality,
      assistant_id: filterAssistant ? Number(filterAssistant) : undefined,
    });
  }, [
    currentPage,
    pageSize,
    debouncedSearch,
    filterDate,
    filterStatus,
    effectiveCourseFilter,
    examFrom,
    filterModality,
    loadExams,
    filterAssistant,
    activeRole,
  ]);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedSchedules(new Set());
  }, [activeRole]);

  useEffect(() => {
    if (!showAddStudentModal || !selectedExam?.id) return;

    const q = (debouncedAddStudentQuery || "").trim();

    // si no hay query, no pegues al server; muestra “vacío” + hint
    if (!q) {
      setAvailableStudentsView([]);
      return;
    }

    let alive = true;
    const reqId = ++availReqIdRef.current;

    (async () => {
      try {
        setAvailFetching(true);

        // ✅ Intento 1: backend soporta búsqueda (ideal)
        // Si tu fetchAvailableStudents acepta params, pásalos:
        // const list = (await fetchAvailableStudents(selectedExam.id, { search: q, limit: 20 })) || [];

        const list = (await fetchAvailableStudents(selectedExam.id, { search: q, limit: 50 })) || [];
        // 👆 Si hoy tu backend SOLO devuelve todo, igual funciona, pero…
        // hacemos fallback filtrando y limitando en UI:
        const nd = normalizeText(q);
        const filtered = list.filter((s) => {
          const full = normalizeText(`${s.nombres || ""} ${s.apellidos || ""} ${s.email || ""}`);
          return full.includes(nd);
        });

        if (!alive || reqId !== availReqIdRef.current) return;

        setAvailableStudentsRaw(list);
        setAvailableStudentsView(filtered.slice(0, 20)); // ✅ limit para rendimiento
      } catch (err) {
        if (!alive || reqId !== availReqIdRef.current) return;
        setError(err?.message || "No se pudieron cargar estudiantes disponibles");
        setAvailableStudentsView([]);
      } finally {
        if (alive && reqId === availReqIdRef.current) setAvailFetching(false);
      }
    })();

    return () => { alive = false; };
  }, [showAddStudentModal, selectedExam?.id, debouncedAddStudentQuery, fetchAvailableStudents]);


  // --------------------------
  // Form horario
  // --------------------------
  const handleFormChange = useCallback((field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };

      if (field === "curso_id") {
        next.asistentes_ids = [];

        if (!editingSchedule) {
          const selectedCourse =
            (courses || []).find((course) => String(course.id) === String(value)) || null;
          const currentCode = String(next.codigo_horario || "").trim().toUpperCase();
          const looksAutoGenerated =
            !currentCode || /^[A-Z0-9]{2,6}-[A-Z0-9]{6,8}$/.test(currentCode);

          if (looksAutoGenerated) {
            next.codigo_horario = generateScheduleCodeLocal(
              existingScheduleCodes,
              selectedCourse?.codigo_curso
            );
          }
        }
      }

      if (field === "requiere_codigo") {
        const wantsCode = value === true;
        if (wantsCode && !String(next.codigo_acceso || "").trim()) {
          next.codigo_acceso = generateAccessCode();
        }
      }

      if (field === "is_virtual" && value === true) {
        next.camara_nivel = "no_requerido";
        next.microfono_nivel = "no_requerido";
        next.numero_fotos_requeridas = 0;
        next.umbral_decibeles_microfono = "";
      }

      return next;
    });
  }, [courses, editingSchedule, existingScheduleCodes, generateAccessCode, generateScheduleCodeLocal]);

  const validateForm = useCallback(() => {
    const errors = {};
    const today = new Date().toISOString().split("T")[0];

    if (!formData.curso_id) errors.curso_id = "Debes seleccionar un curso.";

    if (!formData.fecha_examen) errors.fecha_examen = "La fecha es requerida.";
    else if (formData.fecha_examen < today) errors.fecha_examen = "La fecha debe ser futura";

    if (!formData.hora_inicio) errors.hora_inicio = "La hora de inicio es requerida.";
    if (!formData.hora_fin) errors.hora_fin = "La hora de fin es requerida.";
    else if (formData.hora_inicio && formData.hora_fin <= formData.hora_inicio) {
      errors.hora_fin = "La hora de fin debe ser posterior";
    }

    if (Number(formData.cupos_disponibles) < 1) {
      errors.cupos_disponibles = "Debe haber al menos 1 cupo.";
    }

    const np = Number(formData.numero_preguntas);
    if (!Number.isFinite(np) || np <= 0) {
      errors.numero_preguntas = "El número de preguntas debe ser mayor a 0.";
    }

    const tl = Number(formData.tiempo_limite_minutos);
    if (!Number.isFinite(tl) || tl <= 0) {
      errors.tiempo_limite_minutos = "El tiempo límite debe ser mayor a 0.";
    }

    const scheduleWindowMinutes = getScheduleWindowMinutes(
      formData.hora_inicio,
      formData.hora_fin
    );
    if (
      Number.isFinite(scheduleWindowMinutes) &&
      scheduleWindowMinutes > 0 &&
      Number.isFinite(tl) &&
      tl > scheduleWindowMinutes
    ) {
      errors.tiempo_limite_minutos =
        "El tiempo límite no puede ser mayor al rango entre la hora de inicio y fin.";
    }

    const requiredPhotos = Number(formData.numero_fotos_requeridas);
    if (!Number.isFinite(requiredPhotos) || requiredPhotos < 0) {
      errors.numero_fotos_requeridas = "El número de fotos no puede ser negativo.";
    } else if (requiredPhotos > MAX_SCHEDULE_PHOTOS) {
      errors.numero_fotos_requeridas = `El máximo permitido es ${MAX_SCHEDULE_PHOTOS} fotos.`;
    }

    if (
      formData.umbral_decibeles_microfono !== "" &&
      Number(formData.umbral_decibeles_microfono) < 0
    ) {
      errors.umbral_decibeles_microfono = "El umbral no puede ser negativo.";
    }

    const limitPenalties = Number(formData.limite_penalizaciones);
    if (!Number.isFinite(limitPenalties) || limitPenalties < MIN_SCHEDULE_PENALTIES) {
      errors.limite_penalizaciones = `El límite mínimo es ${MIN_SCHEDULE_PENALTIES}.`;
    } else if (
      formData.cierre_por_penalizaciones &&
      (!Number.isInteger(limitPenalties) || limitPenalties < MIN_SCHEDULE_PENALTIES)
    ) {
      errors.limite_penalizaciones =
        `Si activas el cierre por penalizaciones, el límite debe ser de al menos ${MIN_SCHEDULE_PENALTIES}.`;
    }

    if (formData.is_virtual === undefined || formData.is_virtual === null) {
      errors.is_virtual = "Modalidad es requerida";
    }

    if (formData.requiere_codigo) {
      const code = String(formData.codigo_acceso || "").trim();
      if (!code) {
        errors.codigo_acceso = "El código de acceso es requerido";
      } else if (code.length !== 10) {
        errors.codigo_acceso = "El código debe tener exactamente 10 caracteres";
      }
    }

    const nm = formData.nota_maxima;
    if (nm !== "" && nm !== null && nm !== undefined) {
      const n = Number(nm);
      if (!Number.isFinite(n) || n <= 0) errors.nota_maxima = "Nota máxima debe ser > 0";
    }

    const pct = Number(formData.porcentaje_aprobacion);
    if (Number.isNaN(pct)) errors.porcentaje_aprobacion = "Porcentaje es requerido";
    else if (pct < 0 || pct > 100) errors.porcentaje_aprobacion = "El porcentaje debe estar entre 0 y 100";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!validateForm()) return;
      setSubmitting(true);

      try {
        if (editingSchedule?.id) {
          const state = getScheduleState(editingSchedule);

          if (state === "finalizado") {
            alert("Este horario ya finalizó y solo puede visualizarse.");
            setSubmitting(false);
            return;
          }

          if (state === "en_ejecucion") {
            const ok = window.confirm(
              "Este horario está en ejecución. Solo se deben modificar fecha, hora, cupos, tiempo límite y ubicación. ¿Deseas continuar?"
            );
            if (!ok) {
              setSubmitting(false);
              return;
            }
          }
        }

        const payload = {
          ...formData,
          curso_id: Number(formData.curso_id),
          cupos_disponibles: Number(formData.cupos_disponibles),
          porcentaje_aprobacion: Number(formData.porcentaje_aprobacion),
          numero_preguntas: Number(formData.numero_preguntas),
          tiempo_limite_minutos: Number(formData.tiempo_limite_minutos),
          requiere_codigo: !!formData.requiere_codigo,
          ubicacion_info: String(formData.ubicacion_info || ""),
          codigo_acceso: formData.requiere_codigo ? String(formData.codigo_acceso || "").trim() : null,
          nota_maxima:
            formData.nota_maxima === "" || formData.nota_maxima == null
              ? null
              : Number(formData.nota_maxima),
          habilitacion_unitaria_estudiantes: !!formData.habilitacion_unitaria_estudiantes,
          camara_nivel: String(formData.camara_nivel || "no_requerido"),
          microfono_nivel: String(formData.microfono_nivel || "no_requerido"),
          numero_fotos_requeridas: Number(formData.numero_fotos_requeridas ?? 0),
          umbral_decibeles_microfono:
            formData.umbral_decibeles_microfono === "" || formData.umbral_decibeles_microfono == null
              ? null
              : Number(formData.umbral_decibeles_microfono),
          cierre_por_penalizaciones: !!formData.cierre_por_penalizaciones,
          limite_penalizaciones: Number(formData.limite_penalizaciones ?? 0),
          asistentes_ids: Array.isArray(formData.asistentes_ids)
            ? formData.asistentes_ids.map(Number).filter(Boolean)
            : [],
        };

        if (!editingSchedule?.id) {
          delete payload.codigo_horario;
        }

        if (editingSchedule?.id) {
          await updateExamSchedule(editingSchedule.id, payload);
        } else {
          await createExamSchedule(payload);
        }

        setShowAddModal(false);
        setFormData({
          curso_id: "",
          is_virtual: false,
          fecha_examen: "",
          hora_inicio: "",
          hora_fin: "",
          cupos_disponibles: 20,
          porcentaje_aprobacion: 70,
          permitir_navegacion: false,
          mostrar_resumen_final: false,
          numero_preguntas: 25,
          tiempo_limite_minutos: 60,
          codigo_acceso: generateAccessCode(),
          codigo_horario: generateScheduleCodeLocal(
            existingScheduleCodes,
            selectedCourseMeta?.codigo_curso || routeCourseMeta?.codigo_curso || ""
          ),
          requiere_codigo: true,
          ubicacion_info: "",
          nota_maxima: 10,

          habilitacion_unitaria_estudiantes: false,
          camara_nivel: "requerido",
          microfono_nivel: "requerido",
          numero_fotos_requeridas: 3,
          umbral_decibeles_microfono: 55,
          cierre_por_penalizaciones: false,
          limite_penalizaciones: 3,
          asistentes_ids: [],
        });
        setFormErrors({});
        setEditingSchedule(null);

        await loadExams({
          page: currentPage,
          limit: pageSize,
          search: debouncedSearch,
          fecha: filterDate,
          status: filterStatus,
          course: effectiveCourseFilter,
          fecha_desde: examFrom,
          modalidad: filterModality,
          assistant_id: filterAssistant ? Number(filterAssistant) : undefined,
        });
        loadExamStats();
      } catch (err) {
        setError(err?.message || "No se pudo crear el horario");
      } finally {
        setSubmitting(false);
      }
    },
    [validateForm, formData, pageSize, createExamSchedule, updateExamSchedule, loadExams, loadExamStats, currentPage, debouncedSearch, filterDate, filterStatus, effectiveCourseFilter, examFrom, filterModality, editingSchedule]
  );

  // --------------------------
  // Acciones horario (optimistic para no mover todo)
  // --------------------------
  const [rowBusy, setRowBusy] = useState(() => new Set()); // ids ocupados (toggle/delete)

  const markRowBusy = useCallback((id, busy) => {
    setRowBusy((prev) => {
      const next = new Set(prev);
      if (busy) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const handleDelete = useCallback(
    async (exam) => {
      if (!roleCanDeleteSchedule) {
        alert("Tu rol no permite eliminar horarios.");
        return;
      }

      const state = getScheduleState(exam);

      if (state === "en_ejecucion") {
        alert("No se puede eliminar un horario que está en ejecución.");
        return;
      }

      if (state === "finalizado") {
        alert("No se puede eliminar un horario finalizado.");
        return;
      }

      if (!window.confirm("¿Eliminar este horario?")) return;

      markRowBusy(exam.id, true);

      try {
        await deleteExamSchedule(exam.id);

        setExamsData((prev) => ({
          ...prev,
          exams: prev.exams.filter((x) => x.id !== exam.id),
          pagination: {
            ...prev.pagination,
            total: Math.max(0, (prev.pagination?.total || 0) - 1),
          },
        }));

        loadExamStats();
      } catch (err) {
        setError(err?.message || "No se pudo eliminar");
      } finally {
        markRowBusy(exam.id, false);
      }
    },
    [deleteExamSchedule, markRowBusy, loadExamStats, roleCanDeleteSchedule]
  );

  const handleToggleStatus = useCallback(
    async (examId, currentStatus) => {
      // optimistic toggle (sin recargar lista)
      const prevList = examsData.exams;
      markRowBusy(examId, true);

      setExamsData((prev) => ({
        ...prev,
        exams: prev.exams.map((x) => (x.id === examId ? { ...x, activo: !currentStatus } : x)),
      }));

      try {
        await updateExamSchedule(examId, { activo: !currentStatus });
        // stats sí conviene refrescar (pero sin recargar tabla)
        loadExamStats();
      } catch (err) {
        // revert
        setExamsData((prev) => ({ ...prev, exams: prevList }));
        setError(err?.message || "No se pudo cambiar estado");
      } finally {
        markRowBusy(examId, false);
      }
    },
    [updateExamSchedule, examsData.exams, markRowBusy, loadExamStats]
  );

  // --------------------------
  // % aprobación
  // --------------------------
  const openEditPct = useCallback((exam) => {
    setScheduleForPct(exam);
    setPctValue(exam?.porcentaje_aprobacion ?? 70);
    setPctError("");
    setShowEditPctModal(true);
  }, []);

  const handleSavePct = useCallback(
    async (e) => {
      e.preventDefault();
      const v = Number(pctValue);
      if (Number.isNaN(v) || v < 0 || v > 100) {
        setPctError("El porcentaje debe estar entre 0 y 100.");
        return;
      }
      setPctSubmitting(true);
      try {
        await updateExamSchedule(scheduleForPct.id, { porcentaje_aprobacion: v });

        // update local row (no recargar)
        setExamsData((prev) => ({
          ...prev,
          exams: prev.exams.map((x) => (x.id === scheduleForPct.id ? { ...x, porcentaje_aprobacion: v } : x)),
        }));

        setShowEditPctModal(false);
        setScheduleForPct(null);
        setPctError("");
      } catch (err) {
        setPctError(err?.message || "No se pudo guardar el porcentaje.");
      } finally {
        setPctSubmitting(false);
      }
    },
    [pctValue, updateExamSchedule, scheduleForPct]
  );

  // --------------------------
  // Bulk delete schedules (sin recargar todo si no es necesario)
  // --------------------------
  const handleBulkDeleteSchedules = useCallback(async () => {
    if (schedulesSelectedCount === 0) return;

    const selectedRows = (examsData.exams || []).filter((x) => selectedSchedules.has(x.id));
    const blocked = selectedRows.filter((x) => {
      const state = getScheduleState(x);
      return state === "en_ejecucion" || state === "finalizado";
    });

    if (blocked.length > 0) {
      alert(
        `No se puede eliminar la selección porque ${blocked.length} horario(s) están en ejecución o finalizados.`
      );
      return;
    }

    const msg = `¿Eliminar ${schedulesSelectedCount} horario${schedulesSelectedCount > 1 ? "s" : ""} seleccionado${schedulesSelectedCount > 1 ? "s" : ""}?`;
    if (!window.confirm(msg)) return;

    try {
      setScheduleBulkBusy(true);

      const ids = Array.from(selectedSchedules);
      const results = await Promise.allSettled(ids.map((id) => deleteExamSchedule(id)));

      const successIds = ids.filter((id, idx) => results[idx].status === "fulfilled");
      const failedCount = results.length - successIds.length;

      if (successIds.length > 0) {
        setExamsData((prev) => ({
          ...prev,
          exams: prev.exams.filter((x) => !successIds.includes(x.id)),
          pagination: {
            ...prev.pagination,
            total: Math.max(0, (prev.pagination?.total || 0) - successIds.length),
          },
        }));
      }

      clearScheduleSelection();
      loadExamStats();

      if (failedCount > 0) {
        alert(`Se eliminaron ${successIds.length} horario(s), pero ${failedCount} no pudieron eliminarse.`);
      }
    } catch (e) {
      alert(e?.message || "No se pudieron eliminar los horarios seleccionados.");
    } finally {
      setScheduleBulkBusy(false);
    }
  }, [
    schedulesSelectedCount,
    examsData.exams,
    selectedSchedules,
    deleteExamSchedule,
    clearScheduleSelection,
    loadExamStats,
  ]);

  // --------------------------
  // Estudiantes por horario
  // --------------------------
  const handleViewStudents = useCallback(async (exam) => {
    setSelectedExam(exam);
    setStudentsLoading(true);
    try {
      const list = (await fetchExamStudents(exam.id)) || [];
      setExamStudents(list);
      clearExamStudentSelection();
      setShowStudentsModal(true);
    } catch (err) {
      setError(err?.message || "No se pudieron cargar estudiantes");
    } finally {
      setStudentsLoading(false);
    }
  }, [fetchExamStudents]);

  useEffect(() => {
    const requestedId = Number(location?.state?.openScheduleId || pendingOpenScheduleIdRef.current || 0);
    const action = String(location?.state?.openScheduleAction || pendingOpenScheduleActionRef.current || "").trim();

    if (!Number.isFinite(requestedId) || requestedId <= 0 || !action) return;

    const target = (examsData?.exams || []).find((exam) => Number(exam?.id) === requestedId);
    if (!target) return;

    pendingOpenScheduleIdRef.current = 0;
    pendingOpenScheduleActionRef.current = "";

    if (action === "students") {
      handleViewStudents(target);
    } else if (action === "questions") {
      openManageQuestions(target);
    } else if (action === "config") {
      openEditSchedule(target);
    }

    if (location?.state?.openScheduleId || location?.state?.openScheduleAction) {
      const nextState = { ...(location.state || {}) };
      delete nextState.openScheduleId;
      delete nextState.openScheduleAction;
      delete nextState.openScheduleSource;

      navigate(`${location.pathname}${location.search || ""}`, {
        replace: true,
        state: Object.keys(nextState).length ? nextState : null,
      });
    }
  }, [
    examsData?.exams,
    handleViewStudents,
    location.pathname,
    location.search,
    location.state,
    navigate,
    openEditSchedule,
    openManageQuestions,
  ]);


  const clearStudentPicks = useCallback(() => setSelectedStudentIds(new Set()), []);
  const handleShowAddStudent = useCallback(async () => {
    if (!canAddStudentsFor(selectedExam)) {
      alert(`No se puede agregar estudiantes. ${addBlockedReason(selectedExam)}`);
      return;
    }

    setStudentsLoading(true);
    try {
      const list = (await fetchAvailableStudents(selectedExam.id)) || [];
      setAvailableStudents(list);

      setAvailableStudentsRaw(list);
      setAvailableStudentsView([]);
      setAddStudentQuery("");
      clearStudentPicks();

      setShowAddStudentModal(true);
    } catch (err) {
      setError(err?.message || "No se pudieron cargar estudiantes disponibles");
    } finally {
      setStudentsLoading(false);
    }
  }, [fetchAvailableStudents, selectedExam, clearStudentPicks]);


  const runPool = async (items, worker, concurrency = 4) => {
    const queue = [...items];
    const results = [];
    const runners = Array.from({ length: Math.max(1, concurrency) }, async () => {
      while (queue.length) {
        const item = queue.shift();
        try {
          results.push({ ok: true, item, value: await worker(item) });
        } catch (e) {
          results.push({ ok: false, item, error: e });
        }
      }
    });
    await Promise.all(runners);
    return results;
  };



  const remainingSlots = useMemo(() => {
    if (!selectedExam) return 0;
    const occ = Number(selectedExam.cupos_ocupados || 0);
    const cap = Number(selectedExam.cupos_disponibles || 0);
    return Math.max(0, cap - occ);
  }, [selectedExam]);


  const handleAddStudentsBulk = useCallback(async () => {
    if (!selectedExam?.id) return;

    const ids = Array.from(selectedStudentIds);
    if (ids.length === 0) return;

    // respeta cupos: agrega hasta remainingSlots
    const toAdd = ids.slice(0, remainingSlots);
    if (toAdd.length === 0) return;

    setStudentsLoading(true);
    try {
      const results = await runPool(
        toAdd,
        (studentId) => addStudentToExam(selectedExam.id, studentId),
        4
      );

      const addedOk = results.filter((r) => r.ok).length;
      const failed = results.filter((r) => !r.ok);

      // refresco 1 vez
      const updated = (await fetchExamStudents(selectedExam.id)) || [];
      setExamStudents(updated);

      setShowAddStudentModal(false);
      clearStudentPicks();
      setAddStudentQuery("");
      loadExamStats();

      // update local cupos_ocupados: suma SOLO los exitosos
      const realOccupied = Array.isArray(updated) ? updated.length : 0;

      setExamsData((prev) => ({
        ...prev,
        exams: prev.exams.map((x) =>
          x.id === selectedExam.id
            ? { ...x, cupos_ocupados: realOccupied, estudiantes_agendados: realOccupied }
            : x
        ),
      }));

      setSelectedExam((prev) =>
        prev
          ? { ...prev, cupos_ocupados: realOccupied, estudiantes_agendados: realOccupied }
          : prev
      );

      if (failed.length) {
        const reasons = failed
          .map((r) => r?.error?.message || r?.error?.code || "Error")
          .slice(0, 6);

        alert(
          `✅ Se agregaron ${addedOk} estudiante(s).\n` +
          `❌ Fallaron ${failed.length}.\n\n` +
          `Motivos (primeros ${reasons.length}):\n- ${reasons.join("\n- ")}`
        );
      }
    } catch (err) {
      setError(err?.message || "No se pudieron agregar estudiantes");
    } finally {
      setStudentsLoading(false);
    }
  }, [
    selectedExam,
    selectedStudentIds,
    remainingSlots,
    addStudentToExam,
    fetchExamStudents,
    loadExamStats,
    clearStudentPicks,
  ]);


  const handleRemoveStudent = useCallback(async (studentId) => {
    if (!window.confirm("¿Remover este estudiante?")) return;
    setStudentsLoading(true);
    try {
      await removeStudentFromExam(selectedExam.id, studentId);
      const updated = (await fetchExamStudents(selectedExam.id)) || [];
      setExamStudents(updated);
      loadExamStats();

      // update local cupos_ocupados
      const realOccupied = Array.isArray(updated) ? updated.length : 0;

      setExamsData((prev) => ({
        ...prev,
        exams: prev.exams.map((x) =>
          x.id === selectedExam.id
            ? { ...x, cupos_ocupados: realOccupied, estudiantes_agendados: realOccupied }
            : x
        ),
      }));

      setSelectedExam((prev) =>
        prev
          ? { ...prev, cupos_ocupados: realOccupied, estudiantes_agendados: realOccupied }
          : prev
      );
    } catch (err) {
      setError(err?.message || "No se pudo remover estudiante");
    } finally {
      setStudentsLoading(false);
    }
  }, [removeStudentFromExam, selectedExam, fetchExamStudents, loadExamStats]);

  const handleToggleEnable = useCallback(
    async (studentId, nextEnabled) => {
      if (!selectedExam?.id) return;

      // snapshot de ese estudiante para revertir si falla
      const prevStudent = examStudents.find((s) => String(s.id) === String(studentId));
      if (!prevStudent) return;

      markStudentBusy(studentId, true);

      // ✅ Optimistic update (no recargar lista completa)
      setExamStudents((prev) =>
        prev.map((s) =>
          String(s.id) === String(studentId)
            ? {
              ...s,
              habilitado: !!nextEnabled,
              // si quieres, setea fecha_habilitacion local (opcional)
              fecha_habilitacion: nextEnabled ? new Date().toISOString() : null,
            }
            : s
        )
      );

      try {
        await setStudentExamEnabled(selectedExam.id, studentId, nextEnabled, user?.id);

        // (Opcional) sincronización “silenciosa” sin spinner global:
        // const updated = (await fetchExamStudents(selectedExam.id)) || [];
        // setExamStudents(updated);

      } catch (err) {
        // ❌ Revert
        setExamStudents((prev) =>
          prev.map((s) => (String(s.id) === String(studentId) ? prevStudent : s))
        );
        setError(err?.message || "No se pudo actualizar habilitación");
      } finally {
        markStudentBusy(studentId, false);
      }
    },
    [selectedExam, user, examStudents, setStudentExamEnabled, markStudentBusy]
  );

  const handleBulkEnableStudents = useCallback(async (nextEnabled) => {
    if (!selectedExam?.id) return;

    const ids = Array.from(selectedExamStudentIds);
    if (ids.length === 0) return;

    setStudentsLoading(true);
    try {
      const results = await runPool(
        ids,
        (studentId) =>
          setStudentExamEnabled(selectedExam.id, studentId, nextEnabled, user?.id),
        4
      );

      const okIds = results.filter((r) => r.ok).map((r) => Number(r.item));
      const failed = results.filter((r) => !r.ok);

      if (okIds.length > 0) {
        setExamStudents((prev) =>
          prev.map((s) =>
            okIds.includes(Number(s.id))
              ? {
                ...s,
                habilitado: !!nextEnabled,
                fecha_habilitacion: nextEnabled ? new Date().toISOString() : null,
              }
              : s
          )
        );
      }

      clearExamStudentSelection();

      if (failed.length > 0) {
        alert(
          `Se actualizaron ${okIds.length} estudiante(s), pero fallaron ${failed.length}.`
        );
      }
    } catch (err) {
      setError(err?.message || "No se pudo actualizar la habilitación masiva");
    } finally {
      setStudentsLoading(false);
    }
  }, [
    selectedExam,
    selectedExamStudentIds,
    setStudentExamEnabled,
    user,
    runPool,
    clearExamStudentSelection,
  ]);


  // --------------------------
  // Exámenes dentro de horario
  // --------------------------
  const persistExamPanelOpen = useCallback((scheduleId, isOpen) => {
    try { localStorage.setItem(`sm:examPanelOpen:${scheduleId}`, isOpen ? "1" : "0"); } catch { }
  }, []);

  const readExamPanelOpen = useCallback((scheduleId) => {
    try { return localStorage.getItem(`sm:examPanelOpen:${scheduleId}`) === "1"; } catch { return false; }
  }, []);

  const loadExamsInThisSchedule = useCallback(async (scheduleId) => {
    const data = await fetchExams?.({ horario_examen_id: scheduleId });
    const list = Array.isArray(data) ? data : data?.exams || [];
    const filtered = list.filter((ex) => Number(ex.horario_examen_id) === Number(scheduleId));
    setExamsInSchedule(filtered);
    setSelectedExamsInModal(new Set());
  }, [fetchExams]);

  useEffect(() => {
    if (!showAddModal) return;
    if (editingSchedule) return;

    setFormData((prev) => {
      const current = String(prev?.codigo_horario || "").trim();
      if (current) return prev;

      return {
        ...prev,
        codigo_horario: generateScheduleCodeLocal(
          existingScheduleCodes,
          selectedCourseMeta?.codigo_curso || routeCourseMeta?.codigo_curso || ""
        ),
      };
    });
  }, [
    showAddModal,
    editingSchedule,
    existingScheduleCodes,
    generateScheduleCodeLocal,
    selectedCourseMeta,
    routeCourseMeta,
  ]);

  useEffect(() => {
    // ✅ evita selección fantasma si cambias curso/filtros
    setSelectedSchedules(new Set());
  }, [
    effectiveCourseFilter,
    filterModality,
    filterStatus,
    filterDate,
    examFrom,
    debouncedSearch,
  ]);

  const openManageExams = useCallback(async (schedule) => {
    // ✅ Enriquecemos con info del curso (tipo, label, etc.)
    const cursoId = schedule?.curso_id ?? null;
    const courseMeta =
      courseOptions?.find((c) => Number(c.value) === Number(cursoId)) || null;

    const enriched = {
      ...schedule,
      curso_id: cursoId != null ? Number(cursoId) : schedule?.curso_id,
      curso_tipo: schedule?.curso_tipo ?? courseMeta?.tipo ?? null,
      curso_label: schedule?.curso_nombre ?? courseMeta?.label ?? schedule?.curso_nombre ?? null,
    };


    setScheduleForExams(enriched);
    await loadExamsInThisSchedule(schedule.id);
    setShowManageExams(true);
    setEditingExamId(null);
    setExamForm({ nombre: "", descripcion: "", numero_preguntas: 10, tiempo_limite_minutos: 60, activo: true });
    setExamFormErrors({});
    setExamPanelOpen(readExamPanelOpen(schedule.id));
  }, [courseOptions, loadExamsInThisSchedule, readExamPanelOpen]);


  const validateExamForm = useCallback(() => {
    const e = {};
    if (!examForm.nombre?.trim()) e.nombre = "Nombre es requerido";
    if (examForm.numero_preguntas < 1) e.numero_preguntas = "Debe ser ≥ 1";
    if (examForm.tiempo_limite_minutos < 1) e.tiempo_limite_minutos = "Debe ser ≥ 1";
    setExamFormErrors(e);
    return Object.keys(e).length === 0;
  }, [examForm]);

  const submitExam = useCallback(async (ev) => {
    ev.preventDefault();
    if (!scheduleForExams) return;

    if (!validateExamForm()) {
      setExamPanelOpen(true);
      if (scheduleForExams?.id) persistExamPanelOpen(scheduleForExams.id, true);
      return;
    }

    setExamSubmitting(true);
    try {
      const payload = {
        ...examForm,
        curso_id: Number(scheduleForExams.curso_id),
        horario_examen_id: Number(scheduleForExams.id),
      };

      if (editingExamId) await updateExam(editingExamId, payload);
      else await createExam(payload);

      await loadExamsInThisSchedule(scheduleForExams.id);

      setEditingExamId(null);
      setExamForm({ nombre: "", descripcion: "", numero_preguntas: 10, tiempo_limite_minutos: 60, activo: true });
      setExamFormErrors({});

      setExamPanelOpen(false);
      persistExamPanelOpen(scheduleForExams.id, false);
    } catch (e) {
      alert(e?.message || "No se pudo guardar el examen.");
    } finally {
      setExamSubmitting(false);
    }
  }, [
    scheduleForExams,
    validateExamForm,
    examForm,
    editingExamId,
    updateExam,
    createExam,
    loadExamsInThisSchedule,
    persistExamPanelOpen,
  ]);

  const editExam = useCallback((ex) => {
    setEditingExamId(ex.id);
    setExamForm({
      nombre: ex.nombre || "",
      descripcion: ex.descripcion || "",
      numero_preguntas: ex.numero_preguntas ?? 10,
      tiempo_limite_minutos: ex.tiempo_limite_minutos ?? 60,
      activo: !!ex.activo,
    });
    setExamFormErrors({});
    setExamPanelOpen(true);
    if (scheduleForExams?.id) persistExamPanelOpen(scheduleForExams.id, true);
  }, [scheduleForExams, persistExamPanelOpen]);

  const removeExam = useCallback(async (id) => {
    if (!window.confirm("¿Eliminar este examen?")) return;
    await deleteExam(id);
    await loadExamsInThisSchedule(scheduleForExams.id);
  }, [deleteExam, loadExamsInThisSchedule, scheduleForExams]);

  const handleBulkDeleteModalExams = useCallback(async () => {
    if (modalSelectedCount === 0) return;

    const msg = `¿Eliminar ${modalSelectedCount} examen${modalSelectedCount > 1 ? "es" : ""} seleccionado${modalSelectedCount > 1 ? "s" : ""}? Esta acción no se puede deshacer.`;
    if (!window.confirm(msg)) return;

    try {
      setModalBulkBusy(true);
      const ids = Array.from(selectedExamsInModal);

      // optimistic remove
      setExamsInSchedule((prev) => prev.filter((x) => !selectedExamsInModal.has(x.id)));

      await Promise.allSettled(ids.map((id) => deleteExam(id)));
      clearModalSelection();
      if (scheduleForExams) await loadExamsInThisSchedule(scheduleForExams.id);
    } catch (e) {
      alert(e?.message || "No se pudieron eliminar los exámenes seleccionados.");
    } finally {
      setModalBulkBusy(false);
    }
  }, [modalSelectedCount, selectedExamsInModal, deleteExam, clearModalSelection, scheduleForExams, loadExamsInThisSchedule]);

  // --------------------------
  // Clear filters
  // --------------------------
  const clearFilters = useCallback(() => {
    setSearchTerm("");
    setFilterDate("");
    setFilterStatus("");
    if (!routeCourseId) setCourseFilter("");
    setFilterModality("");
    setExamFrom("");
    setFilterAssistant("");
    setCurrentPage(1);
  }, [routeCourseId]);

  // --------------------------
  // Derivados UI
  // --------------------------
  const activeCount = useMemo(
    () => [searchTerm, filterStatus, courseFilter, filterDate, examFrom, filterModality].filter(Boolean).length,
    [searchTerm, filterStatus, courseFilter, filterDate, examFrom, filterModality]
  );

  const selectedStudents = useMemo(() => {
    // arma objetos completos para render chips/lista
    const pickById = (arr, id) => (arr || []).find((x) => String(x.id) === String(id));
    return Array.from(selectedStudentIds)
      .map((id) => pickById(availableStudentsView, id) || pickById(availableStudentsRaw, id) || pickById(availableStudents, id) || null)
      .filter(Boolean);
  }, [selectedStudentIds, availableStudentsView, availableStudentsRaw, availableStudents]);

  const toggleStudentPick = useCallback((id) => {
    setSelectedStudentIds((prev) => {
      const next = new Set(prev);
      if (next.has(String(id))) next.delete(String(id));
      else next.add(String(id));
      return next;
    });
  }, []);


  const canSelectMore = useMemo(() => {
    // permitimos seleccionar hasta remainingSlots (si ya seleccionaste algunos, cuenta eso)
    return selectedStudentIds.size < remainingSlots;
  }, [selectedStudentIds.size, remainingSlots]);

  const allViewSelected = useMemo(() => {
    const ids = (availableStudentsView || []).map((s) => String(s.id));
    if (!ids.length) return false;
    return ids.every((id) => selectedStudentIds.has(id));
  }, [availableStudentsView, selectedStudentIds]);

  const someViewSelected = useMemo(() => {
    const ids = (availableStudentsView || []).map((s) => String(s.id));
    return ids.some((id) => selectedStudentIds.has(id));
  }, [availableStudentsView, selectedStudentIds]);

  const viewMasterRef = useRef(null);
  useEffect(() => {
    if (viewMasterRef.current) {
      viewMasterRef.current.indeterminate = !allViewSelected && someViewSelected;
    }
  }, [allViewSelected, someViewSelected]);

  const toggleSelectAllView = useCallback(() => {
    setSelectedStudentIds((prev) => {
      const next = new Set(prev);
      const ids = (availableStudentsView || []).map((s) => String(s.id));

      if (!ids.length) return next;

      if (ids.every((id) => next.has(id))) {
        // deseleccionar todos los visibles
        ids.forEach((id) => next.delete(id));
        return next;
      }

      // seleccionar visibles, pero respetando cupos restantes
      const cap = remainingSlots;
      // ya seleccionados cuentan
      for (const id of ids) {
        if (next.size >= cap) break;
        next.add(id);
      }
      return next;
    });
  }, [availableStudentsView, remainingSlots]);



  const anyFilterOn = activeCount > 0;

  const statusLabel = useMemo(() => {
    if (filterStatus === "activo") return "Activos";
    if (filterStatus === "inactivo") return "Inactivos";
    if (filterStatus === "en_ejecucion") return "En ejecución";
    if (filterStatus === "finalizado") return "Finalizados";
    return "";
  }, [filterStatus]);

  const courseLabel = useMemo(() => {
    const v = String(effectiveCourseFilter || "").trim();
    if (!v) return "";

    const found = courseOptions.find((o) => String(o.value) === String(v));
    if (found?.label) return found.label;

    const metaName =
      selectedCourseMeta?.nombre ||
      routeCourseMeta?.nombre ||
      location?.state?.course?.nombre ||
      "";

    const metaCode =
      selectedCourseMeta?.codigo_curso ||
      routeCourseMeta?.codigo_curso ||
      location?.state?.course?.codigo_curso ||
      "";

    if (metaName) {
      return `${metaName}${metaCode ? ` (${metaCode})` : ""}`;
    }

    return "Cargando curso...";
  }, [
    effectiveCourseFilter,
    courseOptions,
    selectedCourseMeta,
    routeCourseMeta,
    location?.state,
  ]);


  // ✅ NUEVO: ir a Proctoring filtrado por horario (y curso)
  const goReviewProctoring = useCallback((schedule) => {
    const horarioId = schedule?.id;
    if (!horarioId) return;

    const cursoMeta =
      courseOptions?.find((c) => Number(c.value) === Number(schedule?.curso_id)) || null;

    navigate(`../proctoring/${horarioId}`, {
      state: {
        schedule: {
          ...schedule,
          curso_label:
            schedule?.curso_nombre ||
            schedule?.nombre_curso ||
            cursoMeta?.label ||
            courseLabel ||
            null,
          curso_tipo: schedule?.curso_tipo || cursoMeta?.tipo || null,
        },
        fromScheduleManager: true,
      },
    });
  }, [navigate, courseOptions, courseLabel]);

  const summaryForView = useMemo(() => {
    const list = Array.isArray(examsData?.exams) ? examsData.exams : [];
    const totalPagina = list.length;

    const now = new Date();
    const activosPagina = list.reduce((a, x) => a + (getScheduleState(x, now) === "activo" ? 1 : 0), 0);
    const cuposTotPagina = list.reduce((a, x) => a + Number(x?.cupos_disponibles || 0), 0);
    const cuposOccPagina = list.reduce((a, x) => a + Number(x?.cupos_ocupados || 0), 0);

    const totalFiltrado = Number(examsData?.pagination?.total || 0); // ✅ total del filtro en backend (si viene)

    return {
      totalFiltrado,
      totalPagina,
      activosPagina,
      cuposTotPagina,
      cuposOccPagina,
    };
  }, [examsData]);


  const modalityLabel = useMemo(() => {
    if (filterModality === "virtual") return "💻 Virtual";
    if (filterModality === "presencial") return "🏫 Presencial";
    return "";
  }, [filterModality]);

  const getScheduleSortValue = useCallback((exam, key) => {
    switch (key) {
      case "fecha":
        return `${dateOnlyISO(exam?.fecha_examen)} ${hhmm(exam?.hora_inicio)}`;
      case "modalidad":
        return exam?.is_virtual ? "virtual" : "presencial";
      case "estudiantes":
        return Number(exam?.cupos_ocupados || 0);
      case "preguntas":
        return Number(exam?.numero_preguntas || 0);
      case "estado": {
        const state = getScheduleState(exam);
        if (state === "finalizado") return 4;
        if (state === "en_ejecucion") return 3;
        if (state === "activo") return 2;
        return 1;
      }
      default:
        return "";
    }
  }, []);

  const handleSort = useCallback((key) => {
    setSortBy((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        return prev;
      }
      setSortDir("asc");
      return key;
    });
  }, []);

  const sortIcon = useCallback((key) => {
    if (sortBy !== key) return "↕";
    return sortDir === "asc" ? "↑" : "↓";
  }, [sortBy, sortDir]);

  const sortedExams = useMemo(() => {
    const list = [...(examsData?.exams || [])];
    list.sort((a, b) => {
      const av = getScheduleSortValue(a, sortBy);
      const bv = getScheduleSortValue(b, sortBy);

      if (typeof av === "number" && typeof bv === "number") {
        return sortDir === "asc" ? av - bv : bv - av;
      }

      const cmp = String(av).localeCompare(String(bv), "es", {
        numeric: true,
        sensitivity: "base",
      });

      return sortDir === "asc" ? cmp : -cmp;
    });
    return list;
  }, [examsData?.exams, getScheduleSortValue, sortBy, sortDir]);

  const filteredAssistantOptions = useMemo(() => {
    const q = normalizeText(assistantSearch || "");
    if (!q) return assistantOptions || [];

    return (assistantOptions || []).filter((a) => {
      const text = normalizeText(
        `${a?.nombres || ""} ${a?.apellidos || ""} ${a?.email || ""} ${a?.codigo_institucional || ""}`
      );
      return text.includes(q);
    });
  }, [assistantOptions, assistantSearch]);

  const availableAssistantOptions = useMemo(() => {
    const selectedIds = new Set((formData?.asistentes_ids || []).map(Number));
    return (filteredAssistantOptions || []).filter(
      (assistant) => !selectedIds.has(Number(assistant?.usuario_id))
    );
  }, [filteredAssistantOptions, formData?.asistentes_ids]);

  const selectedAssistants = useMemo(() => {
    const selectedIds = new Set((formData?.asistentes_ids || []).map(Number));
    return (assistantOptions || []).filter((a) => selectedIds.has(Number(a.usuario_id)));
  }, [assistantOptions, formData?.asistentes_ids]);

  const toggleAssistantPick = useCallback((assistantId) => {
    const parsedId = Number(assistantId);

    setFormData((prev) => {
      const current = new Set((prev.asistentes_ids || []).map(Number));
      if (current.has(parsedId)) current.delete(parsedId);
      else current.add(parsedId);

      return {
        ...prev,
        asistentes_ids: Array.from(current),
      };
    });
  }, []);

  const removeAssistantPick = useCallback((assistantId) => {
    const parsedId = Number(assistantId);

    setFormData((prev) => ({
      ...prev,
      asistentes_ids: (prev.asistentes_ids || []).filter((id) => Number(id) !== parsedId),
    }));
  }, []);


  useEffect(() => {
    window.__smHandleViewStudents = (exam) => {
      if (!roleCanManageScheduleStudents) {
        alert("Tu rol no permite gestionar estudiantes en este horario.");
        return;
      }

      handleViewStudents(exam);
    };

    window.__smHandleManageQuestions = (exam) => {
      if (!roleCanManageScheduleQuestions) {
        alert("Tu rol no permite gestionar preguntas en este horario.");
        return;
      }

      openManageQuestions(exam);
    };

    window.__smHandleReview = goReviewProctoring;

    window.__smHandleEdit = (exam) => {
      openEditSchedule(exam);
    };

    window.__smHandleDelete = (exam) => {
      if (!roleCanDeleteSchedule) {
        alert("Tu rol no permite eliminar horarios.");
        return;
      }

      handleDelete(exam);
    };

    return () => {
      delete window.__smHandleViewStudents;
      delete window.__smHandleManageQuestions;
      delete window.__smHandleReview;
      delete window.__smHandleEdit;
      delete window.__smHandleDelete;
    };
  }, [
    handleViewStudents,
    openManageQuestions,
    goReviewProctoring,
    openEditSchedule,
    handleDelete,
    roleCanManageScheduleStudents,
    roleCanManageScheduleQuestions,
    roleCanDeleteSchedule,
  ]);

  // ===========================
  // Render
  // ===========================
  return (
    <>
      <style>{CSS}</style>

      <div className="sm-shell" style={{ background: "#f3f4f6", minHeight: "100vh" }}>
        <div className="sm" style={{ maxWidth: 1500, margin: "0 auto" }}>
          {/* HEADER */}
          <div
            className="sm-card soft"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
              <button
                type="button"
                className="sm-btn"
                onClick={() => navigate(backTo)}
                style={{ borderRadius: 14 }}
              >
                <ArrowLeft size={16} />
                {backLabel}
              </button>

              <div>
                <h2 style={{ margin: 0, fontSize: "1.35rem", color: "#111827" }}>
                  {isCourseContextLocked ? "Horarios del Curso" : "Horarios de Exámenes"}
                </h2>

                <p style={{ margin: "6px 0 0", color: "#6b7280", fontSize: 13 }}>
                  {isCourseContextLocked
                    ? `Administrando horarios de ${courseLabel || "este curso"}.`
                    : "Administra horarios, estudiantes, preguntas y proctoring."}
                </p>
              </div>
            </div>

            {roleCanCreateSchedule && (
              <button
                type="button"
                className="sm-btn-primary"
                onClick={() => {
                  const newScheduleCode = generateScheduleCodeLocal(
                    existingScheduleCodes,
                    selectedCourseMeta?.codigo_curso || routeCourseMeta?.codigo_curso || ""
                  );

                  setEditingSchedule(null);
                  setFormErrors({});
                  setCreateScheduleTab("unitaria");
                  setFormData((prev) => ({
                    ...prev,
                    curso_id: effectiveCourseFilter ? String(effectiveCourseFilter) : "",
                    codigo_acceso: generateAccessCode(),
                    codigo_horario: newScheduleCode,
                  }));
                  setShowAddModal(true);
                }}
              >
                <Plus size={16} />
                Nuevo Horario
              </button>
            )}
          </div>

          {/* BÚSQUEDA + FILTROS */}
          <div className="sm-card soft">
            <div
              style={{
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div style={{ flex: 1, minWidth: 260, position: "relative" }}>
                <Search
                  size={17}
                  style={{
                    position: "absolute",
                    left: 14,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#3b82f6",
                  }}
                />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar horario, fecha, ubicación o código..."
                  className="sm-input sm-input-pill"
                  style={{ paddingLeft: 40 }}
                />
              </div>

              <button
                type="button"
                className="sm-btn"
                onClick={() => setFiltersCollapsed((v) => !v)}
                style={{
                  borderRadius: 999,
                  background: filtersCollapsed ? "#eff6ff" : "#fff",
                  borderColor: filtersCollapsed ? "#bfdbfe" : "#e5e7eb",
                  color: filtersCollapsed ? "#1d4ed8" : "#374151",
                }}
              >
                <Filter size={15} />
                Filtros
                <ChevronDown
                  size={14}
                  style={{
                    transform: filtersCollapsed ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform .2s ease",
                  }}
                />
              </button>

              <button
                type="button"
                className="sm-btn"
                onClick={() => {
                  setSearchTerm("");
                  setFilterAssistant("");
                  setFilterDate("");
                  setFilterStatus("");
                  if (!isCourseContextLocked) setCourseFilter("");
                  setFilterModality("");
                  setExamFrom("");
                }}
                style={{ borderRadius: 999 }}
              >
                Limpiar
              </button>

              <div
                style={{
                  display: "flex",
                  background: "#f3f4f6",
                  padding: 4,
                  borderRadius: 999,
                  border: "1px solid #e5e7eb",
                }}
              >
                <button
                  type="button"
                  onClick={() => setViewMode("table")}
                  className="sm-btn"
                  style={{
                    minHeight: 34,
                    padding: "6px 10px",
                    borderRadius: 999,
                    background: viewMode === "table" ? "#fff" : "transparent",
                    border: "none",
                    boxShadow: viewMode === "table" ? "0 2px 8px rgba(0,0,0,.08)" : "none",
                  }}
                  title="Vista tabla"
                >
                  <List size={16} />
                </button>

                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className="sm-btn"
                  style={{
                    minHeight: 34,
                    padding: "6px 10px",
                    borderRadius: 999,
                    background: viewMode === "grid" ? "#fff" : "transparent",
                    border: "none",
                    boxShadow: viewMode === "grid" ? "0 2px 8px rgba(0,0,0,.08)" : "none",
                  }}
                  title="Vista tarjetas"
                >
                  <LayoutGrid size={16} />
                </button>
              </div>
            </div>

            {filtersCollapsed && (
              <div
                style={{
                  marginTop: 18,
                  paddingTop: 18,
                  borderTop: "1px solid #e5e7eb",
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
                  gap: 14,
                }}
              >
                {shouldShowFacultySelector && !facultyFilterLocked && (
                  <div>
                    <label className="sm-label-sm">Facultad</label>
                    <select
                      className="sm-select"
                      value={facultyPick}
                      onChange={(e) => setFacultyPick(e.target.value)}
                    >
                      <option value={FAC_ALL}>
                        {isGlobalAdminUser ? "Todas las facultades" : "Todas mis facultades"}
                      </option>
                      {(faculties || []).map((f) => (
                        <option key={f.id} value={String(f.id)}>
                          {f.nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="sm-label-sm">
                    Curso {isCourseContextLocked ? "(desde el curso actual)" : ""}
                  </label>

                  <select
                    className="sm-select"
                    value={effectiveCourseFilter}
                    onChange={(e) => {
                      if (isCourseContextLocked) return;
                      setCourseFilter(e.target.value);
                    }}
                    disabled={isCourseContextLocked}
                    title={isCourseContextLocked ? courseLabel : undefined}
                  >
                    {!isCourseContextLocked && <option value="">Todos los cursos</option>}

                    {isCourseContextLocked &&
                      effectiveCourseFilter &&
                      !courseOptions.some((c) => String(c.value) === String(effectiveCourseFilter)) && (
                        <option value={String(effectiveCourseFilter)}>
                          {courseLabel || "Cargando curso..."}
                        </option>
                      )}

                    {courseOptions.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>

                  {isCourseContextLocked && (
                    <div
                      style={{
                        marginTop: 6,
                        fontSize: 12,
                        color: "#6b7280",
                        fontWeight: 700,
                        lineHeight: 1.35,
                      }}
                    >
                      Mostrando únicamente horarios de:{" "}
                      <span style={{ color: "#111827", fontWeight: 900 }}>
                        {courseLabel || "Cargando curso..."}
                      </span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="sm-label-sm">Estado</label>
                  <select
                    className="sm-select"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <option value="">Todos</option>
                    <option value="activo">Activo</option>
                    <option value="inactivo">Inactivo</option>
                    <option value="en_ejecucion">En ejecución</option>
                    <option value="finalizado">Finalizado</option>
                  </select>
                </div>

                <div>
                  <label className="sm-label-sm">Modalidad</label>
                  <select
                    className="sm-select"
                    value={filterModality}
                    onChange={(e) => setFilterModality(e.target.value)}
                  >
                    <option value="">Todas</option>
                    <option value="presencial">Presencial</option>
                    <option value="virtual">Virtual</option>
                  </select>
                </div>

                <div>
                  <label className="sm-label-sm">Desde</label>
                  <input
                    type="date"
                    className="sm-input"
                    value={examFrom}
                    onChange={(e) => setExamFrom(e.target.value)}
                  />
                </div>

                <div>
                  <label className="sm-label-sm">Asistente</label>
                  <select
                    className="sm-select"
                    value={filterAssistant}
                    onChange={(e) => setFilterAssistant(e.target.value)}
                  >
                    <option value="">Todos</option>
                    {filterAssistantOptions.map((a) => (
                      <option key={a.usuario_id} value={String(a.usuario_id)}>
                        {`${a.nombres || ""} ${a.apellidos || ""}`.trim() || a.email}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="sm-label-sm">Fecha exacta</label>
                  <input
                    type="date"
                    className="sm-input"
                    value={filterDate}
                    onChange={(e) => setFilterDate(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>

          {/* RESUMEN / FILTROS ACTUALES */}
          <div className="sm-filterHero">
            <div className="sm-filterHeroTop">
              <div className="sm-filterHeroIcon">
                <Filter size={18} />
              </div>

              <div>
                <div className="sm-filterHeroTitle">FILTROS</div>
                <div className="sm-filterHeroSub">Criterios actuales</div>
              </div>
            </div>

            <div className="sm-filterChipsRow">
              <div className="sm-filterMetric blue">
                <div className="sm-filterMetricIcon">
                  <Shield size={14} />
                </div>
                <div>
                  <div className="sm-filterMetricLabel">ROL</div>
                  <div className="sm-filterMetricValue">
                    {scheduleRoleLabel}
                  </div>
                </div>
              </div>

              <div className="sm-filterMetric blue" style={{ minWidth: 220 }}>
                <div className="sm-filterMetricIcon">
                  <FileText size={14} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div className="sm-filterMetricLabel">
                    CURSO
                  </div>
                  <div
                    className="sm-filterMetricValue"
                    title={courseLabel || "Todos los cursos"}
                    style={{
                      maxWidth: 280,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {courseLabel || "Todos los cursos"}
                  </div>
                </div>
              </div>
              <div className="sm-filterMetric blue">
                <div className="sm-filterMetricIcon">
                  <List size={14} />
                </div>
                <div>
                  <div className="sm-filterMetricLabel">TOTAL</div>
                  <div className="sm-filterMetricValue">
                    {filteredSummary.total} horarios
                  </div>
                </div>
              </div>

              <div className="sm-filterMetric green">
                <div className="sm-filterMetricIcon">
                  <Activity size={14} />
                </div>
                <div>
                  <div className="sm-filterMetricLabel">ESTADO</div>
                  <div className="sm-filterMetricValue">
                    {statusLabel || "Todos"}
                  </div>
                </div>
              </div>

              <div className="sm-filterMetric orange">
                <div className="sm-filterMetricIcon">
                  <Laptop size={14} />
                </div>
                <div>
                  <div className="sm-filterMetricLabel">MODALIDAD</div>
                  <div className="sm-filterMetricValue">
                    {filterModality === "virtual"
                      ? "Virtual"
                      : filterModality === "presencial"
                        ? "Presencial"
                        : "Todas"}
                  </div>
                </div>
              </div>

              <div className="sm-filterMetric amber">
                <div className="sm-filterMetricIcon">
                  <Users size={14} />
                </div>
                <div>
                  <div className="sm-filterMetricLabel">ASISTENTE</div>
                  <div className="sm-filterMetricValue">
                    {filterAssistant
                      ? (
                        filterAssistantOptions.find((a) => String(a.usuario_id) === String(filterAssistant))
                          ? `${filterAssistantOptions.find((a) => String(a.usuario_id) === String(filterAssistant)).nombres || ""} ${filterAssistantOptions.find((a) => String(a.usuario_id) === String(filterAssistant)).apellidos || ""}`.trim()
                          : "Filtrado"
                      )
                      : "Todos"}
                  </div>
                </div>
              </div>

              <div className="sm-filterMetric purple">
                <div className="sm-filterMetricIcon">
                  <Calendar size={14} />
                </div>
                <div>
                  <div className="sm-filterMetricLabel">FECHA</div>
                  <div className="sm-filterMetricValue">
                    {filterDate
                      ? "Exacta"
                      : examFrom
                        ? "Desde fecha"
                        : "Histórico"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* LOADING */}
          {(initialLoading || isFetching) && (
            <div className="sm-card soft" style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <RefreshCw size={16} className={isFetching ? "spin" : ""} />
              <span style={{ fontWeight: 800, color: "#374151" }}>
                Cargando horarios...
              </span>
            </div>
          )}

          {/* LISTADO */}
          {!initialLoading && viewMode === "table" ? (
            <div className="sm-tableFrame">
              <div className="sm-tableViewport">
                <table className="sm-scheduleTable">
                  <thead>
                    <tr>
                      <th style={{ width: "24%" }}>
                        <button type="button" className="sm-th-sort" onClick={() => requestSort("fecha")}>
                          Curso, fecha y hora {renderSortIcon("fecha")}
                        </button>
                      </th>
                      <th style={{ width: "14%" }}>
                        <button type="button" className="sm-th-sort" onClick={() => requestSort("codigo")}>
                          ID y asist
                        </button>
                      </th>
                      <th style={{ width: "18%" }}>
                        <button type="button" className="sm-th-sort" onClick={() => requestSort("modalidad")}>
                          Modalidad {renderSortIcon("modalidad")}
                        </button>
                      </th>
                      <th style={{ width: "10%", textAlign: "center" }}>
                        <button type="button" className="sm-th-sort" onClick={() => requestSort("cupos")}>
                          Inscritos {renderSortIcon("cupos")}
                        </button>
                      </th>
                      <th style={{ width: "10%", textAlign: "center" }}>
                        <button type="button" className="sm-th-sort" onClick={() => requestSort("preguntas")}>
                          Preguntas {renderSortIcon("preguntas")}
                        </button>
                      </th>
                      <th style={{ width: "14%", textAlign: "center" }}>
                        <button type="button" className="sm-th-sort" onClick={() => requestSort("estado")}>
                          Estado y config. {renderSortIcon("estado")}
                        </button>
                      </th>
                      <th style={{ width: "16%", textAlign: "center" }}>Acciones</th>
                    </tr>
                  </thead>

                  <tbody>
                    {sortedSchedules.length === 0 ? (
                      <tr>
                        <td colSpan={7} style={{ padding: 28, textAlign: "center", color: "#6b7280" }}>
                          No hay resultados que coincidan con los filtros aplicados.
                        </td>
                      </tr>
                    ) : (
                      sortedSchedules.map((exam) => {
                        const stateMeta = getScheduleStateMeta(exam);
                        const isVirtual = toBool(exam?.is_virtual);
                        const canDelete = roleCanDeleteSchedule && canDeleteSchedule(exam);
                        const canEdit = canEditSchedule(exam);
                        const isViewOnly = !canEdit;
                        const asistentes = Array.isArray(exam?.asistentes) ? exam.asistentes : [];
                        const firstAssistant = asistentes[0] || null;
                        const currentOccupied = Number(exam?.cupos_ocupados ?? exam?.cupos_ocupados_reales ?? 0);
                        const currentTotal = Number(exam?.cupos_disponibles ?? 0);


                        const getScheduleRegisteredQuestions = (exam) => {
                          const candidates = [
                            exam?.preguntas_registradas,
                            exam?.total_preguntas,
                            exam?.preguntas_reales,
                          ];

                          for (const value of candidates) {
                            const n = Number(value);
                            if (Number.isFinite(n) && n > 0) return n;
                          }

                          return 0;
                        };

                        const requiredQuestions = toFiniteNumber(exam?.numero_preguntas, 0);
                        const realQuestions = getScheduleRegisteredQuestions(exam);

                        const missingQuestions = Math.max(0, requiredQuestions - realQuestions);
                        const questionsOk = requiredQuestions > 0 && realQuestions >= requiredQuestions;

                        return (
                          <tr key={exam.id}>
                            <td>
                              <div className="sm-col-course">
                                <div className="sm-courseIcon">
                                  <Calendar size={18} />
                                </div>

                                <div className="sm-courseMeta">
                                  <div
                                    className="sm-courseCode"
                                    title={exam?.curso_nombre || exam?.codigo_curso || ""}
                                  >
                                    {exam?.curso_nombre || "Curso"}
                                    {exam?.codigo_curso ? ` (${exam.codigo_curso})` : ""}
                                  </div>

                                  <div className="sm-courseDate">
                                    {String(exam?.fecha_examen || "").slice(0, 10)}
                                  </div>

                                  <div className="sm-courseTime">
                                    <Clock size={11} />
                                    {formatTimeShort(exam?.hora_inicio)} - {formatTimeShort(exam?.hora_fin)}
                                  </div>
                                </div>
                              </div>
                            </td>

                            <td className="sm-dividerCol">
                              <div className="sm-idAssist">
                                <span className="sm-idChip">
                                  {exam?.codigo_horario || `HOR-${exam?.id}`}
                                </span>

                                {firstAssistant ? (
                                  <div
                                    className="sm-assistantLine"
                                    title={`${firstAssistant?.nombres || ""} ${firstAssistant?.apellidos || ""}`.trim()}
                                  >
                                    <Users size={11} />
                                    {firstAssistant?.nombres} {firstAssistant?.apellidos}
                                  </div>
                                ) : (
                                  <div className="sm-assistantLine empty">
                                    No asignado
                                  </div>
                                )}
                              </div>
                            </td>

                            <td className="sm-dividerCol">
                              <div className="sm-modeStack">
                                <div className="sm-modeTop">
                                  <span className={`sm-modpill ${isVirtual ? "virtual" : "presencial"}`}>
                                    {isVirtual ? <Laptop size={12} /> : <MapPin size={12} />}
                                    {isVirtual ? "Virtual" : "Presencial"}
                                  </span>

                                  {exam?.requiere_codigo ? (
                                    <Lock size={12} className="sm-lockMini" />
                                  ) : (
                                    <Unlock size={12} className="sm-lockMini" />
                                  )}
                                </div>

                                <div className="sm-accessPreview">
                                  <div
                                    className="sm-locationText"
                                    title={String(exam?.ubicacion_info || "Sin ubicación / enlace")}
                                  >
                                    {String(exam?.ubicacion_info || "Sin ubicación / enlace")}
                                  </div>

                                  <button
                                    type="button"
                                    className="sm-eyeMini"
                                    onClick={() => setInfoModalData(exam)}
                                    title="Ver detalles"
                                  >
                                    <Eye size={12} />
                                  </button>
                                </div>
                              </div>
                            </td>

                            <td className="sm-dividerCol">
                              <div className="sm-statMini">
                                <div className="sm-statValue">
                                  {currentOccupied}/{currentTotal}
                                </div>

                                <button
                                  type="button"
                                  className="sm-ghostManage"
                                  disabled={!roleCanManageScheduleStudents}
                                  onClick={() => {
                                    if (!roleCanManageScheduleStudents) {
                                      setInfoModalData({
                                        title: "Solo lectura",
                                        message: "Tu rol no permite gestionar estudiantes en este horario.",
                                      });
                                      return;
                                    }

                                    handleViewStudents(exam);
                                  }}
                                >
                                  Gestionar
                                </button>
                              </div>
                            </td>

                            <td className="sm-dividerCol">
                              <div className="sm-statMini">
                                <div className="sm-statValue">
                                  <span
                                    title={
                                      questionsOk
                                        ? "Preguntas completas"
                                        : `Faltan ${missingQuestions} pregunta${missingQuestions === 1 ? "" : "s"}`
                                    }
                                    style={{
                                      color: questionsOk ? "#166534" : "#b45309",
                                      fontWeight: 800,
                                    }}
                                  >
                                    {realQuestions}/{requiredQuestions}
                                  </span>
                                </div>

                                <button
                                  type="button"
                                  className="sm-ghostManage"
                                  disabled={!roleCanManageScheduleQuestions}
                                  onClick={() => {
                                    if (!roleCanManageScheduleQuestions) {
                                      setInfoModalData({
                                        title: "Solo lectura",
                                        message: "Tu rol no permite gestionar preguntas en este horario.",
                                      });
                                      return;
                                    }

                                    openManageQuestions(exam);
                                  }}
                                >
                                  Gestionar
                                </button>
                              </div>
                            </td>

                            <td className="sm-dividerCol">
                              <div className="sm-configBox">
                                <div className="sm-configLine">
                                  <span className="k">Cámara:</span>
                                  <span
                                    className={`v ${normalizeRequirement(exam?.camara_nivel) === "requerido"
                                      ? "req"
                                      : normalizeRequirement(exam?.camara_nivel) === "opcional"
                                        ? "opt"
                                        : ""
                                      }`}
                                  >
                                    {formatRequirementLabel(exam?.camara_nivel)}
                                  </span>
                                </div>

                                <div className="sm-configLine">
                                  <span className="k">C. Unitario:</span>
                                  <span className={`v ${exam?.habilitacion_unitaria_estudiantes ? "ok" : ""}`}>
                                    {exam?.habilitacion_unitaria_estudiantes ? "Activo" : "Inactivo"}
                                  </span>
                                </div>

                                <div style={{ display: "flex", justifyContent: "center", marginTop: 2 }}>
                                  <span className={`sm-statePillWide ${stateMeta.className}`}>
                                    {stateMeta.label}
                                  </span>
                                </div>
                              </div>
                            </td>

                            <td className="sm-dividerCol">
                              <div className="sm-actionsCol">
                                <button
                                  type="button"
                                  className="sm-manageBtn"
                                  onClick={() => goReviewProctoring(exam)}
                                >
                                  <Activity size={13} />
                                  Gestionar horario
                                </button>

                                <div className="sm-inlineActions">
                                  <button
                                    type="button"
                                    className="sm-editBtn"
                                    onClick={() => {
                                      if (!roleCanEditSchedule) {
                                        setInfoModalData({
                                          title: "Solo lectura",
                                          message: "Tu rol puede visualizar este horario, pero no modificarlo.",
                                          schedule: exam,
                                        });

                                        openEditSchedule(exam);
                                        return;
                                      }

                                      openEditSchedule(exam);
                                    }}
                                  >
                                    {isViewOnly || !roleCanEditSchedule ? <Eye size={13} /> : <Edit2 size={13} />}
                                    {isViewOnly || !roleCanEditSchedule ? "Ver" : "Editar"}
                                  </button>

                                  <button
                                    type="button"
                                    className="sm-deleteBtn"
                                    onClick={() => handleDelete(exam)}
                                    disabled={!canDelete || rowBusy.has(exam.id)}
                                    title={
                                      !roleCanDeleteSchedule
                                        ? "Tu rol no permite eliminar horarios"
                                        : !canDeleteSchedule(exam)
                                          ? "No se puede eliminar un horario en ejecución o finalizado"
                                          : "Eliminar horario"
                                    }
                                  >
                                    <Trash2 size={13} />
                                    Borrar
                                  </button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : !initialLoading ? (
            <div className="sm-cardsGrid">
              {sortedSchedules.length === 0 ? (
                <div className="sm-card soft sm-center">
                  No hay resultados que coincidan.
                </div>
              ) : (
                sortedSchedules.map((exam) => (
                  <ScheduleCard
                    key={exam.id}
                    exam={exam}
                    roleCanDeleteSchedule={roleCanDeleteSchedule}
                    roleCanEditSchedule={roleCanEditSchedule}
                    roleCanManageScheduleStudents={roleCanManageScheduleStudents}
                    roleCanManageScheduleQuestions={roleCanManageScheduleQuestions}
                  />
                ))
              )}
            </div>
          ) : null}

          {/* PAGINACIÓN */}
          <div className="sm-pagination">
            <div className="sm-pageinfo">
              Página <strong>{examsData?.pagination?.page || 1}</strong> de{" "}
              <strong>{Math.max(1, examsData?.pagination?.totalPages || 1)}</strong>
            </div>

            <div className="sm-pagesize">
              <div className="pagesizeBox">
                <span className="pagesizeLbl">
                  <span className="dot" />
                  Mostrar
                </span>
                <select
                  className="sm-select pagesizeSelect"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n} registros
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  className="sm-pagebtn"
                  disabled={!examsData?.pagination?.hasPrev}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft size={16} />
                </button>

                <button
                  type="button"
                  className="sm-pagebtn"
                  disabled={!examsData?.pagination?.hasNext}
                  onClick={() =>
                    setCurrentPage((p) =>
                      Math.min(
                        Number(examsData?.pagination?.totalPages || p),
                        p + 1
                      )
                    )
                  }
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MODAL INFO SIMPLE */}
      {infoModalData && (
        <div className="sm-overlay" onClick={() => setInfoModalData(null)}>
          <div
            className="sm-modal sm-accessModal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sm-accessHeader">
              <div className="sm-accessTitle">
                <Info size={18} color="#2563eb" />
                Detalles de Acceso
              </div>

              <button
                type="button"
                className="sm-accessClose"
                onClick={() => setInfoModalData(null)}
              >
                <X size={18} />
              </button>
            </div>

            <div className="sm-accessSection">
              <div className="sm-accessLabel">Modalidad</div>
              <div className="sm-accessValue">
                {toBool(infoModalData?.is_virtual) ? <Laptop size={14} color="#ea580c" /> : <MapPin size={14} color="#7c3aed" />}
                {toBool(infoModalData?.is_virtual) ? "Virtual" : "Presencial"}
              </div>
            </div>

            <div className="sm-accessSection">
              <div className="sm-accessLabel">
                {toBool(infoModalData?.is_virtual) ? "Enlace" : "Ubicación"}
              </div>
              <div className="sm-accessInputLike">
                {String(infoModalData?.ubicacion_info || "—")}
              </div>
            </div>

            <div className="sm-accessSection">
              <div className="sm-accessLabel">Acceso</div>
              <div className="sm-accessValue">
                {infoModalData?.requiere_codigo ? <Lock size={15} /> : <Unlock size={15} />}
                {infoModalData?.requiere_codigo ? "Requiere Código de Acceso" : "Acceso Libre"}
              </div>

              {infoModalData?.requiere_codigo && (
                <div className="sm-accessCodeBox">
                  Código:&nbsp; {String(infoModalData?.codigo_acceso || "—")}
                </div>
              )}
            </div>

            <div className="sm-accessGrid2">
              <div className="sm-accessMini">
                <div className="k">Tiempo Límite</div>
                <div className="v">
                  <Clock size={14} />
                  {Number(infoModalData?.tiempo_limite_minutos || 0)} min
                </div>
              </div>

              <div className="sm-accessMini">
                <div className="k">Aprobación mín.</div>
                <div className="v">
                  <CheckSquare size={14} />
                  {Number(infoModalData?.porcentaje_aprobacion || 0)}%
                </div>
              </div>
            </div>

            <div className="sm-accessFooter">
              <button
                type="button"
                className="sm-btn-ghost"
                onClick={() => setInfoModalData(null)}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* -------------------------- MODALES -------------------------- */}

      {/* Modal: crear horario */}
      {showAddModal && (
        <div className="sm-overlay">
          <div className="sm-modal sm-createModal">
            <div className="sm-createHead">
              <div className="sm-createHeadTitle">
                <Calendar size={20} color="#2563eb" />
                {editingSchedule
                  ? isViewOnlySchedule
                    ? "Detalle del Horario"
                    : "Editar Horario"
                  : "Nuevos Horarios"}
              </div>

              <button
                type="button"
                className="sm-accessClose"
                onClick={() => {
                  setShowAddModal(false);
                  setFormErrors({});
                  setEditingSchedule(null);
                }}
              >
                <X size={18} />
              </button>
            </div>

            {isViewOnlySchedule && (
              <div
                style={{
                  padding: "12px 18px",
                  borderBottom: "1px solid #e5e7eb",
                  background: "#f8fafc",
                  color: "#475569",
                  fontSize: 13,
                  fontWeight: 800,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Eye size={16} color="#2563eb" />
                {isEditingFinalized
                  ? "Este horario está finalizado. Solo puedes visualizar su configuración."
                  : `Tu rol actual (${scheduleRoleLabel}) solo permite visualizar este horario.`}
              </div>
            )}

            {!editingSchedule && (
              <div className="sm-createTabs">
                <button
                  type="button"
                  className={`sm-createTab ${createScheduleTab === "unitaria" ? "is-active" : ""}`}
                  onClick={() => setCreateScheduleTab("unitaria")}
                >
                  <Plus size={14} />
                  Creación Unitaria
                </button>

                <button
                  type="button"
                  className={`sm-createTab ${createScheduleTab === "importar" ? "is-active" : ""}`}
                  onClick={() => setCreateScheduleTab("importar")}
                >
                  <RefreshCw size={14} />
                  Importar desde Archivo
                </button>
              </div>
            )}

            <div className="sm-createBody">
              {createScheduleTab === "importar" && !editingSchedule ? (
                <div className="sm-importShell">
                  <div className="sm-importHero">
                    <div style={{ display: "grid", gap: 12 }}>
                      <div style={{ fontSize: 24, fontWeight: 1000, color: "#1e3a8a" }}>
                        Importar plantilla de Excel (.xls)
                      </div>

                      <div style={{ fontSize: 15, lineHeight: 1.6, color: "#1d4ed8" }}>
                        Esta opción estará disponible próximamente. Mientras tanto, dejamos visible
                        el espacio para que sepas que la importación masiva sigue en construcción.
                      </div>

                      <div style={{ display: "grid", gap: 6 }}>
                        <button type="button" className="sm-btn-outline" disabled title="Próximamente">
                          Descargar Plantilla (Próximamente)
                        </button>
                        <div style={{ fontSize: 12, color: "#6b7280" }}>
                          Comentario: la importación desde plantilla se habilitará en una siguiente entrega.
                        </div>
                      </div>
                    </div>

                    <div className="sm-importDrop">
                      <div>
                        <div style={{ fontSize: 18, marginBottom: 8 }}>Haz clic o arrastra tu archivo</div>
                        <div style={{ fontSize: 13, color: "#6b7280" }}>
                          Formatos: .xls, .csv (Máx 5MB)
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="sm-importReq">
                    <div className="sm-importReqHead">
                      <span>Estructura requerida de la plantilla</span>
                      <ChevronDown size={16} />
                    </div>

                    <div className="sm-importReqBody">
                      <div className="sm-importReqItem">
                        <div className="k">ID Curso:</div>
                        <div className="v">Obligatorio. Debe coincidir con PRB-748.</div>
                      </div>

                      <div className="sm-importReqItem">
                        <div className="k">Fecha y Horas:</div>
                        <div className="v">Obligatorio. Formato YYYY-MM-DD y HH:MM.</div>
                      </div>

                      <div className="sm-importReqItem">
                        <div className="k">Modalidad:</div>
                        <div className="v">Obligatorio. “Presencial” o “Virtual”.</div>
                      </div>

                      <div className="sm-importReqItem">
                        <div className="k">Tiempo Límite:</div>
                        <div className="v">Obligatorio. Duración en minutos.</div>
                      </div>

                      <div className="sm-importReqItem">
                        <div className="k">Cupos / Preguntas:</div>
                        <div className="v">Obligatorio. Números enteros.</div>
                      </div>

                      <div className="sm-importReqItem">
                        <div className="k">Ubicación / Enlace:</div>
                        <div className="v">Opcional en Virtual, obligatorio en Presencial.</div>
                      </div>

                      <div className="sm-importReqItem">
                        <div className="k">Correo Asistente:</div>
                        <div className="v">Correo del asistente en el sistema o celda vacía.</div>
                      </div>

                      <div className="sm-importReqItem">
                        <div className="k">Config. Cámara/Micro:</div>
                        <div className="v">Requerido, Opcional, No requerido y sus máximos.</div>
                      </div>

                      <div className="sm-importReqItem">
                        <div className="k">Seguridad General:</div>
                        <div className="v">“SI” o “NO” para código, cierre automático y más.</div>
                      </div>
                    </div>

                    <div style={{ padding: "0 16px 16px" }}>
                      <div className="sm-importWarn">
                        Importante: Los códigos de identificación y códigos de acceso se autogenerarán
                        automáticamente al procesar el archivo.
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="sm-createContent">
                  <div className="sm-createPanel">
                    <button
                      type="button"
                      className="sm-sectionHead"
                      onClick={() => setIsConfigHorarioOpen((v) => !v)}
                    >
                      <span className="sm-sectionTitle">
                        <Calendar size={18} color="#2563eb" />
                        Configuración del Horario
                      </span>
                      <ChevronDown
                        size={18}
                        style={{
                          transform: isConfigHorarioOpen ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform .2s ease",
                        }}
                      />
                    </button>

                    {isConfigHorarioOpen && (
                      <div className="sm-createPanelBody">
                        {isViewOnlySchedule ? (
                          <>
                            <div className="sm-codeHero">
                              <div>
                                <div className="sm-codeHeroLabel">Horario actual</div>
                                <div className="sm-codeHeroValue">
                                  {String(formData.codigo_horario || "").trim() || "—"}
                                </div>
                              </div>
                              <Eye size={18} color="#60a5fa" />
                            </div>

                            <div className="sm-createGrid4">
                              <ReadonlyField label="Modalidad" value={modalityLabelFromBool(!!formData.is_virtual)} />
                              <ReadonlyField label="Fecha" value={formData.fecha_examen} />
                              <ReadonlyField label="Inicio" value={formData.hora_inicio} />
                              <ReadonlyField label="Fin" value={formData.hora_fin} />
                            </div>

                            <div className="sm-createGrid4">
                              <ReadonlyField label="T. Límite (min)" value={formData.tiempo_limite_minutos} />
                              <ReadonlyField label="Cupos" value={formData.cupos_disponibles} />
                              <ReadonlyField label="Preguntas" value={formData.numero_preguntas} />
                              <ReadonlyField label="% Aprob." value={`${formData.porcentaje_aprobacion}%`} />
                            </div>

                            <div className="sm-createGrid2">
                              <ReadonlyField
                                label="Curso"
                                value={
                                  courseOptions.find((c) => String(c.value) === String(formData.curso_id))?.label ||
                                  formData.curso_id
                                }
                              />
                              <ReadonlyField label="Código horario" value={formData.codigo_horario} />
                            </div>

                            <div className="sm-createGrid2">
                              <ReadonlyField label="Nota Máx." value={formData.nota_maxima} />
                              <ReadonlyField
                                label={formData.is_virtual ? "Enlace" : "Ubicación"}
                                value={formData.ubicacion_info}
                              />
                            </div>

                            <div className="sm-assistantHintBox">
                              <div className="sm-assistantHintTitle">
                                <Users size={14} />
                                Asistentes asignados
                              </div>

                              <div className="sm-selectedAssistants">
                                {selectedAssistants.length > 0 ? (
                                  selectedAssistants.map((a) => (
                                    <span key={a.usuario_id} className="sm-chip">
                                      {a.nombres} {a.apellidos}
                                    </span>
                                  ))
                                ) : (
                                  <div className="sm-selectedAssistantsEmpty">-- Sin asignar --</div>
                                )}
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="sm-codeHero">
                              <div>
                                <div className="sm-codeHeroLabel">Código del horario</div>
                                <div className="sm-codeHeroValue">
                                  {String(formData.codigo_horario || "").trim() || "—"}
                                </div>
                              </div>

                              {!editingSchedule && (
                                <button
                                  type="button"
                                  className="sm-btn-outline"
                                  onClick={() =>
                                    handleFormChange(
                                      "codigo_horario",
                                      generateScheduleCodeLocal(
                                        existingScheduleCodes,
                                        selectedCourseMeta?.codigo_curso || routeCourseMeta?.codigo_curso || ""
                                      )
                                    )
                                  }
                                >
                                  <RefreshCw size={15} />
                                  Regenerar
                                </button>
                              )}
                            </div>

                            <div className="sm-createGrid2">
                              <div className="sm-fieldBox">
                                <label className="sm-label-lg">Curso *</label>
                                <select
                                  className={`sm-select ${formErrors.curso_id ? "is-error" : ""}`}
                                  value={formData.curso_id}
                                  onChange={(e) => handleFormChange("curso_id", e.target.value)}
                                  disabled={!!routeCourseId || !!editingSchedule || isStrictEditBlocked}
                                >
                                  <option value="">Seleccione un curso</option>
                                  {courseOptions.map((c) => (
                                    <option key={c.value} value={c.value}>
                                      {c.label}
                                    </option>
                                  ))}
                                </select>
                                {formErrors.curso_id && <div className="sm-error">{formErrors.curso_id}</div>}
                              </div>

                              <div className="sm-fieldBox">
                                <label className="sm-label-lg">Modalidad *</label>
                                <div className="sm-modeChoices">
                                  <button
                                    type="button"
                                    className={`sm-modeCard ${!formData.is_virtual ? "is-active presencial" : ""}`}
                                    onClick={() => !isStrictEditBlocked && handleFormChange("is_virtual", false)}
                                    disabled={isStrictEditBlocked}
                                  >
                                    <span className="sm-modeCardLeft">
                                      <span className="sm-modeCardIcon">
                                        <MapPin size={16} />
                                      </span>
                                      <span className="sm-modeCardText">Presencial</span>
                                    </span>
                                    <span className="sm-modeCardCheck">
                                      {!formData.is_virtual ? <CheckCircle2 size={14} /> : null}
                                    </span>
                                  </button>

                                  <button
                                    type="button"
                                    className={`sm-modeCard ${formData.is_virtual ? "is-active virtual" : ""}`}
                                    onClick={() => !isStrictEditBlocked && handleFormChange("is_virtual", true)}
                                    disabled={isStrictEditBlocked}
                                  >
                                    <span className="sm-modeCardLeft">
                                      <span className="sm-modeCardIcon">
                                        <Laptop size={16} />
                                      </span>
                                      <span className="sm-modeCardText">Virtual</span>
                                    </span>
                                    <span className="sm-modeCardCheck">
                                      {formData.is_virtual ? <CheckCircle2 size={14} /> : null}
                                    </span>
                                  </button>
                                </div>
                              </div>
                            </div>

                            <div className="sm-createGrid4">
                              <div className="sm-fieldBox">
                                <label className="sm-label-lg">Fecha *</label>
                                <input
                                  type="date"
                                  className={`sm-input ${formErrors.fecha_examen ? "is-error" : ""}`}
                                  value={formData.fecha_examen}
                                  onChange={(e) => handleFormChange("fecha_examen", e.target.value)}
                                  disabled={isStrictEditBlocked}
                                />
                                {formErrors.fecha_examen && <div className="sm-error">{formErrors.fecha_examen}</div>}
                              </div>

                              <div className="sm-fieldBox">
                                <label className="sm-label-lg">Hora inicio *</label>
                                <input
                                  type="time"
                                  className={`sm-input ${formErrors.hora_inicio ? "is-error" : ""}`}
                                  value={formData.hora_inicio}
                                  onChange={(e) => handleFormChange("hora_inicio", e.target.value)}
                                  disabled={isStrictEditBlocked}
                                />
                                {formErrors.hora_inicio && <div className="sm-error">{formErrors.hora_inicio}</div>}
                              </div>

                              <div className="sm-fieldBox">
                                <label className="sm-label-lg">Hora fin *</label>
                                <input
                                  type="time"
                                  className={`sm-input ${formErrors.hora_fin ? "is-error" : ""}`}
                                  value={formData.hora_fin}
                                  onChange={(e) => handleFormChange("hora_fin", e.target.value)}
                                  disabled={isStrictEditBlocked}
                                />
                                {formErrors.hora_fin && <div className="sm-error">{formErrors.hora_fin}</div>}
                              </div>

                              <div className="sm-fieldBox">
                                <label className="sm-label-lg">Tiempo límite *</label>
                                <input
                                  type="number"
                                  min="1"
                                  className={`sm-input ${formErrors.tiempo_limite_minutos ? "is-error" : ""}`}
                                  value={formData.tiempo_limite_minutos}
                                  onChange={(e) => handleFormChange("tiempo_limite_minutos", e.target.value)}
                                  disabled={isStrictEditBlocked}
                                />
                                {formErrors.tiempo_limite_minutos && <div className="sm-error">{formErrors.tiempo_limite_minutos}</div>}
                              </div>
                            </div>

                            <div className="sm-createGrid4">
                              <div className="sm-fieldBox">
                                <label className="sm-label-lg">Cupos *</label>
                                <input
                                  type="number"
                                  min="1"
                                  className={`sm-input ${formErrors.cupos_disponibles ? "is-error" : ""}`}
                                  value={formData.cupos_disponibles}
                                  onChange={(e) => handleFormChange("cupos_disponibles", e.target.value)}
                                  disabled={isStrictEditBlocked}
                                />
                                {formErrors.cupos_disponibles && <div className="sm-error">{formErrors.cupos_disponibles}</div>}
                              </div>

                              <div className="sm-fieldBox">
                                <label className="sm-label-lg">Preguntas *</label>
                                <input
                                  type="number"
                                  min="1"
                                  className={`sm-input ${formErrors.numero_preguntas ? "is-error" : ""}`}
                                  value={formData.numero_preguntas}
                                  onChange={(e) => handleFormChange("numero_preguntas", e.target.value)}
                                  disabled={isStrictEditBlocked}
                                />
                                {formErrors.numero_preguntas && <div className="sm-error">{formErrors.numero_preguntas}</div>}
                              </div>

                              <div className="sm-fieldBox">
                                <label className="sm-label-lg">% Aprobación *</label>
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  className={`sm-input ${formErrors.porcentaje_aprobacion ? "is-error" : ""}`}
                                  value={formData.porcentaje_aprobacion}
                                  onChange={(e) => handleFormChange("porcentaje_aprobacion", e.target.value)}
                                  disabled={isStrictEditBlocked}
                                />
                                {formErrors.porcentaje_aprobacion && <div className="sm-error">{formErrors.porcentaje_aprobacion}</div>}
                              </div>

                              <div className="sm-fieldBox">
                                <label className="sm-label-lg">Nota máxima</label>
                                <input
                                  type="number"
                                  min="1"
                                  className={`sm-input ${formErrors.nota_maxima ? "is-error" : ""}`}
                                  value={formData.nota_maxima}
                                  onChange={(e) => handleFormChange("nota_maxima", e.target.value)}
                                  disabled={isStrictEditBlocked}
                                />
                                {formErrors.nota_maxima && <div className="sm-error">{formErrors.nota_maxima}</div>}
                              </div>
                            </div>

                            <div className="sm-fieldBox full">
                              <label className="sm-label-lg">
                                {formData.is_virtual ? "Enlace" : "Ubicación"}
                              </label>
                              <input
                                type="text"
                                className="sm-input"
                                value={formData.ubicacion_info}
                                onChange={(e) => handleFormChange("ubicacion_info", e.target.value)}
                                disabled={isStrictEditBlocked}
                                placeholder={formData.is_virtual ? "https://..." : "Aula / laboratorio / bloque"}
                              />
                            </div>

                            <div className="sm-fieldBox full">
                              <label className="sm-label-lg">Buscar asistente</label>
                              <input
                                type="text"
                                value={assistantSearch}
                                onChange={(e) => setAssistantSearch(e.target.value)}
                                onFocus={() => setAssistantSuggestionsOpen(true)}
                                onClick={() => setAssistantSuggestionsOpen(true)}
                                onBlur={() => {
                                  window.setTimeout(() => setAssistantSuggestionsOpen(false), 150);
                                }}
                                className="sm-input"
                                placeholder="Buscar asistente..."
                                disabled={isStrictEditBlocked}
                              />
                            </div>

                            <div className="sm-selectedAssistants">
                              {selectedAssistants.length > 0 ? (
                                selectedAssistants.map((a) => (
                                  <span key={a.usuario_id} className="sm-chip">
                                    {a.nombres} {a.apellidos}
                                    <button
                                      type="button"
                                      className="sm-chip-x"
                                      onClick={() => removeAssistantPick(a.usuario_id)}
                                      disabled={isStrictEditBlocked}
                                    >
                                      ×
                                    </button>
                                  </span>
                                ))
                              ) : (
                                <div className="sm-selectedAssistantsEmpty">-- Sin asignar --</div>
                              )}
                            </div>

                            {assistantSuggestionsOpen && !isStrictEditBlocked && (
                              <div
                                style={{
                                  border: "1px solid #dbe3f0",
                                  borderRadius: 14,
                                  background: "#fff",
                                  maxHeight: 260,
                                  overflowY: "auto",
                                  boxShadow: "0 12px 32px rgba(15,23,42,.08)",
                                }}
                                onMouseDown={(e) => e.preventDefault()}
                              >
                                {availableAssistantOptions.length > 0 ? (
                                  availableAssistantOptions.slice(0, 12).map((a) => (
                                    <button
                                      key={a.usuario_id}
                                      type="button"
                                      onClick={() => {
                                        toggleAssistantPick(Number(a.usuario_id));
                                        setAssistantSearch("");
                                        setAssistantSuggestionsOpen(true);
                                      }}
                                      style={{
                                        width: "100%",
                                        textAlign: "left",
                                        border: "none",
                                        background: "transparent",
                                        padding: "12px 14px",
                                        borderBottom: "1px solid #eef2f7",
                                        cursor: "pointer",
                                      }}
                                    >
                                      <div style={{ fontWeight: 700, color: "#111827" }}>
                                        {a.nombres} {a.apellidos}
                                      </div>
                                      <div style={{ fontSize: 12, color: "#6b7280" }}>
                                        {a.email || "Sin correo"}{a.codigo_institucional ? ` • ${a.codigo_institucional}` : ""}
                                      </div>
                                    </button>
                                  ))
                                ) : (
                                  <div className="sm-selectedAssistantsEmpty" style={{ padding: 14 }}>
                                    No hay asistentes disponibles con ese filtro.
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="sm-createPanel">
                    <button
                      type="button"
                      className="sm-sectionHead"
                      onClick={() => setIsConfigSeguridadOpen((v) => !v)}
                    >
                      <span className="sm-sectionTitle">
                        <Shield size={18} color="#f97316" />
                        Configuración y Seguridad
                      </span>
                      <ChevronDown
                        size={18}
                        style={{
                          transform: isConfigSeguridadOpen ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform .2s ease",
                        }}
                      />
                    </button>

                    {isConfigSeguridadOpen && (
                      <div className="sm-createPanelBody">
                        <div className="sm-securityCard">
                          <div className="sm-securityTop2">
                            <div className="sm-securityItem">
                              <div className="sm-securityTitleRow">
                                <div className="sm-securityTitle">Control unitario de estudiantes</div>
                                <ToggleSwitch
                                  checked={!!formData.habilitacion_unitaria_estudiantes}
                                  onChange={(val) => handleFormChange("habilitacion_unitaria_estudiantes", val)}
                                  disabled={isStrictEditBlocked}
                                  horizontal
                                />
                              </div>

                              <div className="sm-securityDesc">
                                Requiere que el docente o asistente habilite manualmente el acceso a cada estudiante.
                              </div>
                            </div>

                            <div className="sm-securityItem">
                              <div className="sm-securityTitleRow">
                                <div className="sm-securityTitle">Código de acceso</div>
                                <ToggleSwitch
                                  checked={!!formData.requiere_codigo}
                                  onChange={(val) => handleFormChange("requiere_codigo", val)}
                                  disabled={isStrictEditBlocked}
                                  horizontal
                                />
                              </div>

                              {formData.requiere_codigo && (
                                <div className="sm-securityMiniGrid">
                                  <input
                                    type="text"
                                    value={formData.codigo_acceso}
                                    onChange={(e) => handleFormChange("codigo_acceso", e.target.value.toUpperCase())}
                                    disabled={isStrictEditBlocked}
                                    className={`sm-input ${formErrors.codigo_acceso ? "is-error" : ""}`}
                                    maxLength={10}
                                  />

                                  {!isEditingFinalized && (
                                    <button
                                      type="button"
                                      className="sm-btn-outline"
                                      onClick={() => handleFormChange("codigo_acceso", generateAccessCode())}
                                      title="Regenerar código"
                                    >
                                      <RefreshCw size={15} />
                                    </button>
                                  )}
                                </div>
                              )}

                              <div className="sm-securityDesc">
                                Clave extra requerida por los estudiantes inscritos para poder iniciar el examen.
                              </div>
                            </div>
                          </div>

                          {!formData.is_virtual && (
                            <div className="sm-securitySplit">
                              <div className="sm-securityItem">
                                <div className="sm-securityTitle">Uso de Cámara</div>

                                <div className="sm-securityMiniGrid">
                                  <select
                                    value={formData.camara_nivel}
                                    onChange={(e) => handleFormChange("camara_nivel", e.target.value)}
                                    disabled={isStrictEditBlocked}
                                    className="sm-select"
                                  >
                                    <option value="requerido">Requerido</option>
                                    <option value="opcional">Opcional</option>
                                    <option value="no_requerido">No requerido</option>
                                  </select>

                                  <input
                                    type="number"
                                    min="0"
                                    max={MAX_SCHEDULE_PHOTOS}
                                    value={formData.numero_fotos_requeridas}
                                    onChange={(e) => handleFormChange("numero_fotos_requeridas", e.target.value)}
                                    disabled={isStrictEditBlocked}
                                    className={`sm-input ${formErrors.numero_fotos_requeridas ? "is-error" : ""}`}
                                    style={{ width: 82 }}
                                  />
                                </div>

                                <div className="sm-securityDesc">
                                  Define si la vigilancia biométrica es obligatoria y cuántas fotos se solicitarán.
                                </div>
                              </div>

                              <div className="sm-securityItem">
                                <div className="sm-securityTitle">Uso de Micrófono</div>

                                <div className="sm-securityMiniGrid">
                                  <select
                                    value={formData.microfono_nivel}
                                    onChange={(e) => handleFormChange("microfono_nivel", e.target.value)}
                                    disabled={isStrictEditBlocked}
                                    className="sm-select"
                                  >
                                    <option value="requerido">Requerido</option>
                                    <option value="opcional">Opcional</option>
                                    <option value="no_requerido">No requerido</option>
                                  </select>

                                  <input
                                    type="number"
                                    min="0"
                                    value={formData.umbral_decibeles_microfono}
                                    onChange={(e) => handleFormChange("umbral_decibeles_microfono", e.target.value)}
                                    disabled={isStrictEditBlocked}
                                    className={`sm-input ${formErrors.umbral_decibeles_microfono ? "is-error" : ""}`}
                                    style={{ width: 82 }}
                                  />
                                </div>

                                <div className="sm-securityDesc">
                                  Define si el micrófono es obligatorio y el umbral máximo permitido.
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="sm-securityGrid2">
                            <div className="sm-securityItem">
                              <div className="sm-securityTitleRow">
                                <div className="sm-securityTitle">Navegar entre preguntas</div>
                                <ToggleSwitch
                                  checked={!!formData.permitir_navegacion}
                                  onChange={(val) => handleFormChange("permitir_navegacion", val)}
                                  disabled={isStrictEditBlocked}
                                  horizontal
                                />
                              </div>

                              <div className="sm-securityTitleRow">
                                <div className="sm-securityTitle">Ver preguntas al finalizar</div>
                                <ToggleSwitch
                                  checked={!!formData.mostrar_resumen_final}
                                  onChange={(val) => handleFormChange("mostrar_resumen_final", val)}
                                  disabled={isStrictEditBlocked}
                                  horizontal
                                />
                              </div>

                              <div className="sm-securityDesc">
                                Controla si el estudiante puede navegar entre preguntas y si verá el resumen final.
                              </div>
                            </div>

                            <div className="sm-securityItem">
                              <div className="sm-securityTitleRow">
                                <div className="sm-securityTitle">Cierre Auto. por Penalizaciones</div>
                                <ToggleSwitch
                                  checked={!!formData.cierre_por_penalizaciones}
                                  onChange={(val) => handleFormChange("cierre_por_penalizaciones", val)}
                                  disabled={isStrictEditBlocked}
                                  horizontal
                                />
                              </div>

                              <div className="sm-securityMiniGrid">
                                <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 800 }}>
                                  Límite de penalizaciones permitido:
                                </span>
                                <input
                                  type="number"
                                  min={MIN_SCHEDULE_PENALTIES}
                                  value={formData.limite_penalizaciones}
                                  onChange={(e) => handleFormChange("limite_penalizaciones", e.target.value)}
                                  disabled={isStrictEditBlocked || !formData.cierre_por_penalizaciones}
                                  className={`sm-input ${formErrors.limite_penalizaciones ? "is-error" : ""}`}
                                  style={{ width: 82 }}
                                />
                              </div>

                              <div className="sm-securityDesc">
                                Si el cierre automático está activo, el examen se enviará forzosamente al alcanzar el límite.
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </form>
              )}
            </div>

            <div className="sm-createFooter">
              {isViewOnlySchedule ? (
                <button
                  type="button"
                  className="sm-btn-ghost"
                  onClick={() => {
                    setShowAddModal(false);
                    setFormErrors({});
                    setEditingSchedule(null);
                  }}
                >
                  Cerrar
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="sm-btn-ghost"
                    onClick={() => {
                      setShowAddModal(false);
                      setFormErrors({});
                      setEditingSchedule(null);
                    }}
                  >
                    Cancelar
                  </button>

                  {/* 🔔 Mensajes de estado */}
                  {editingSchedule && isEditingRunning && (
                    <div className="sm-warning">
                      <strong>⚠️ Horario en ejecución:</strong> solo puedes modificar fecha, hora de inicio, hora de fin, cupos, tiempo límite y ubicación.
                    </div>
                  )}

                  {editingSchedule && isEditingFinalized && (
                    <div className="sm-note">
                      <strong>🔒 Horario finalizado:</strong> este horario solo puede visualizarse. No se permiten cambios.
                    </div>
                  )}

                  {/* 🔘 Botón principal (tu lógica original) */}
                  {createScheduleTab === "unitaria" && (
                    <button
                      type="button"
                      className="sm-btn-primary"
                      onClick={(e) => {
                        const modalRoot = e.currentTarget.closest(".sm-createModal");
                        const formEl = modalRoot?.querySelector("form");
                        if (formEl) formEl.requestSubmit();
                      }}
                      disabled={submitting || isViewOnlySchedule || (editingSchedule && !roleCanEditSchedule)}
                    >
                      <Plus size={15} />
                      {submitting
                        ? "Guardando..."
                        : editingSchedule
                          ? "Actualizar Horario"
                          : "Crear Horario"}
                    </button>
                  )}

                  {createScheduleTab === "importar" && !editingSchedule && (
                    <button
                      type="button"
                      className="sm-btn-primary"
                      disabled
                      style={{ opacity: 0.55 }}
                    >
                      Crear 0 horarios
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <ExamScheduleStudentsModal
        open={showStudentsModal && !!selectedExam}
        schedule={selectedExam}
        onClose={() => {
          setShowStudentsModal(false);
          setSelectedExam(null);
          setExamStudents([]);
          clearExamStudentSelection();
          loadExamStats();
        }}
        onChanged={({ students }) => {
          const realOccupied = Array.isArray(students) ? students.length : 0;
          setExamStudents(Array.isArray(students) ? students : []);
          setExamsData((prev) => ({
            ...prev,
            exams: prev.exams.map((x) =>
              Number(x.id) === Number(selectedExam?.id)
                ? { ...x, cupos_ocupados: realOccupied, estudiantes_agendados: realOccupied }
                : x
            ),
          }));
          setSelectedExam((prev) =>
            prev
              ? { ...prev, cupos_ocupados: realOccupied, estudiantes_agendados: realOccupied }
              : prev
          );
        }}
      />

      {/* Modal: estudiantes del horario */}
      {false && showStudentsModal && selectedExam && (
        <div className="sm-overlay">
          <div className="sm-modal enrolledStudentsModal">
            <div className="sm-enrolledShell">
              <div className="sm-enrolledHead">
                <div className="sm-enrolledTop">
                  <div className="sm-enrolledTitleWrap">
                    <div className="sm-enrolledIcon">
                      <Users size={22} />
                    </div>

                    <div>
                      <h2 className="sm-enrolledTitle">Estudiantes Inscritos</h2>

                      <div className="sm-enrolledMeta">
                        <span>{String(selectedExam?.fecha_examen || "").slice(0, 10)}</span>
                        <span className="sm-inlineDateChip">
                          {formatTimeShort(selectedExam?.hora_inicio)} - {formatTimeShort(selectedExam?.hora_fin)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                    <div className="sm-usedQuota">
                      <span>Cupos Usados:</span>
                      <span className="sm-usedQuotaStrong">
                        {Number(selectedExam?.cupos_ocupados || examStudents.length || 0)} / {Number(selectedExam?.cupos_disponibles || 0)}
                      </span>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        setShowStudentsModal(false);
                        setSelectedExam(null);
                        setExamStudents([]);
                        clearExamStudentSelection();
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        fontSize: "1.5rem",
                        cursor: "pointer",
                        color: "#9ca3af",
                        padding: "4px 8px",
                        borderRadius: 8,
                      }}
                      title="Cerrar"
                    >
                      ×
                    </button>
                  </div>
                </div>
              </div>

              <div className="sm-enrolledToolbar">
                <div className="sm-toolbarLeft">
                  <div className="sm-toolbarSearch">
                    <Search size={16} className="sm-toolbarSearchIcon" />
                    <input
                      type="text"
                      className="sm-toolbarInput"
                      placeholder="Buscar por cédula, nombre o correo..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>

                <div className="sm-toolbarRight">
                  <button
                    type="button"
                    className="sm-btn-soft"
                    disabled={studentsLoading || examStudents.length === 0}
                    title="Luego puedes conectarlo a exportación real"
                    onClick={() => {
                      const rows = examStudents.map((s) => ({
                        nombre: getStudentDisplayName(s),
                        email: s?.email || "",
                        codigo: s?.codigo_institucional || "",
                        agendado: formatStudentEnrollDate(s?.fecha_agendamiento || s?.fecha_inscripcion || s?.created_at),
                        acceso: s?.habilitado ? "Habilitado" : "Inhabilitado",
                      }));

                      const header = ["Nombre", "Email", "Código", "Agendado", "Acceso"];
                      const csv = [
                        header.join(","),
                        ...rows.map((r) =>
                          [
                            r.nombre,
                            r.email,
                            r.codigo,
                            r.agendado,
                            r.acceso,
                          ]
                            .map((v) => `"${String(v || "").replace(/"/g, '""')}"`)
                            .join(",")
                        ),
                      ].join("\n");

                      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `horario-${selectedExam?.codigo_horario || selectedExam?.id || "estudiantes"}.csv`;
                      a.click();
                      URL.revokeObjectURL(url);
                    }}
                  >
                    <FileText size={15} />
                    Descargar
                  </button>

                  <button
                    type="button"
                    className="sm-btn-green"
                    onClick={handleShowAddStudent}
                    disabled={studentsLoading}
                  >
                    <Users size={15} />
                    Inscribir
                  </button>
                </div>
              </div>

              <div className="sm-enrolledBody">
                {selectedExamStudentIds.size > 0 && (
                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                      alignItems: "center",
                      flexWrap: "wrap",
                      marginBottom: 14,
                    }}
                  >
                    <button
                      type="button"
                      className="sm-btn-outline"
                      disabled={studentsLoading}
                      onClick={() => handleBulkEnableStudents(true)}
                    >
                      Habilitar seleccionados
                    </button>

                    <button
                      type="button"
                      className="sm-btn-outline"
                      disabled={studentsLoading}
                      onClick={() => handleBulkEnableStudents(false)}
                    >
                      Inhabilitar seleccionados
                    </button>
                  </div>
                )}

                {studentsLoading ? (
                  <div className="sm-center" style={{ padding: 30 }}>
                    <LoadingSpinner />
                  </div>
                ) : examStudents.length === 0 ? (
                  <div className="sm-warning">
                    No hay estudiantes agendados en este horario.
                  </div>
                ) : (
                  <StudentsTable
                    students={examStudents.filter((s) => {
                      const q = normalizeText(searchTerm || "");
                      if (!q) return true;
                      const haystack = normalizeText(
                        `${s?.nombres || ""} ${s?.apellidos || ""} ${s?.email || ""} ${s?.codigo_institucional || ""}`
                      );
                      return haystack.includes(q);
                    })}
                    studentBusy={studentBusy}
                    onRemove={handleRemoveStudent}
                    onToggleEnable={handleToggleEnable}
                    showEnable={true}
                    selectedIds={selectedExamStudentIds}
                    onToggleSelect={toggleOneExamStudent}
                    onToggleSelectAll={toggleAllExamStudents}
                    allSelected={examStudentsAllSelected}
                    masterRef={examStudentsMasterRef}
                  />
                )}
              </div>

              <div className="sm-enrolledFooter">
                <div className="sm-pagesize">
                  <div className="pagesizeBox">
                    <span className="pagesizeLbl">
                      <span className="dot" />
                      Mostrar
                    </span>

                    <select
                      className="sm-select pagesizeSelect"
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value))}
                    >
                      {PAGE_SIZE_OPTIONS.map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>

                    <span className="sm-muted" style={{ fontSize: 12 }}>registros</span>
                  </div>
                </div>

                <div className="sm-miniPager">
                  <span>
                    Página {Math.max(1, currentPage)} de {Math.max(1, examsData?.pagination?.totalPages || 1)}
                  </span>
                  <span className="sm-miniPagerBtn">
                    <ChevronLeft size={14} />
                  </span>
                  <span className="sm-miniPagerBtn">
                    <ChevronRight size={14} />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: agregar estudiante */}
      {showAddStudentModal && selectedExam && (
        <div className="sm-overlay">
          <div className="sm-modal sm-modal-addStudent">
            <div className="sm-modal-head">
              <div className="sm-row" style={{ justifyContent: "space-between", marginBottom: 10 }}>
                <h2 className="sm-modal-title" style={{ margin: 0 }}>
                  <span style={{ fontSize: "1.4rem" }}>➕</span>
                  Agregar estudiantes
                </h2>

                <button
                  type="button"
                  onClick={() => {
                    setShowAddStudentModal(false);
                    clearStudentPicks();
                    setAvailableStudents([]);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "1.4rem",
                    cursor: "pointer",
                    color: "#6c757d",
                    padding: "4px 8px",
                    borderRadius: 8,
                  }}
                >
                  ×
                </button>
              </div>

              <div className="sm-note">
                <strong>Cupos disponibles:</strong> {remainingSlots}
              </div>

              <div className="sm-searchbox" style={{ maxWidth: "100%", marginBottom: 10 }}>
                <input
                  type="text"
                  placeholder="Buscar estudiante por nombre o correo..."
                  value={addStudentQuery}
                  onChange={(e) => setAddStudentQuery(e.target.value)}
                  className="sm-input small"
                />
              </div>

              {selectedStudents.length > 0 && (
                <div className="sm-selectedPin">
                  <div className="left" style={{ width: "100%" }}>
                    <div style={{ minWidth: 0, width: "100%" }}>
                      <div className="name">Seleccionados: {selectedStudents.length}</div>
                      <div className="chipsRow" style={{ marginTop: 8 }}>
                        {selectedStudents.map((s) => (
                          <span key={s.id} className="sm-chip">
                            {s.nombres} {s.apellidos}
                            <button
                              type="button"
                              className="sm-chip-x"
                              onClick={() => toggleStudentPick(s.id)}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="right">
                    <span className="tag">Máx. {remainingSlots}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="sm-modal-body">
              {studentsLoading || availFetching ? (
                <div className="sm-center" style={{ padding: 30 }}>
                  <LoadingSpinner />
                </div>
              ) : (
                <>
                  {availableStudentsView.length > 0 ? (
                    <div style={{ display: "grid", gap: 8 }}>
                      {availableStudentsView.map((s) => {
                        const selected = selectedStudentIds.has(String(s.id));
                        const disablePick = !selected && selectedStudentIds.size >= remainingSlots;

                        return (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => !disablePick && toggleStudentPick(s.id)}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 12,
                              width: "100%",
                              padding: "12px 14px",
                              borderRadius: 14,
                              border: "1px solid var(--sm-border)",
                              background: "#fff",
                              cursor: disablePick ? "not-allowed" : "pointer",
                              opacity: disablePick ? 0.55 : 1,
                            }}
                          >
                            <span style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={() => toggleStudentPick(s.id)}
                                onClick={(e) => e.stopPropagation()}
                                disabled={disablePick}
                                style={{ width: 16, height: 16 }}
                              />

                              <span className="sm-avatar" style={{ width: 34, height: 34, fontSize: 14 }}>
                                {(s.nombres?.[0] || s.email?.[0] || "?").toUpperCase()}
                              </span>

                              <span style={{ minWidth: 0 }}>
                                <div
                                  style={{
                                    fontWeight: 1000,
                                    color: "#111827",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {s.nombres} {s.apellidos}
                                </div>
                                <div
                                  className="sm-muted"
                                  style={{
                                    fontSize: 12,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {s.email}
                                </div>
                              </span>
                            </span>

                            <span
                              style={{
                                fontSize: 12,
                                fontWeight: 1000,
                                padding: "4px 10px",
                                borderRadius: 999,
                                border: "1px solid var(--sm-border)",
                                background: selected ? "#4CAF5014" : "#f9fafb",
                                color: selected ? "#166534" : "#6b7280",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {selected ? "Seleccionado" : disablePick ? "Sin cupos" : "Elegir"}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="sm-warning">
                      <strong>⚠️ No hay estudiantes disponibles</strong>
                      <p style={{ margin: "4px 0 0 0" }}>
                        Todos los estudiantes elegibles ya están agendados o no cumplen requisitos.
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="sm-modal-foot">
              <div className="sm-row right">
                <button
                  onClick={() => {
                    setShowAddStudentModal(false);
                    clearStudentPicks();
                    setAvailableStudents([]);
                  }}
                  className="sm-btn-ghost"
                >
                  Cancelar
                </button>

                <button
                  onClick={handleAddStudentsBulk}
                  disabled={selectedStudentIds.size === 0 || studentsLoading || remainingSlots <= 0}
                  className="sm-btn-primary"
                >
                  {studentsLoading
                    ? "Agregando..."
                    : `Agregar ${Math.min(selectedStudentIds.size, remainingSlots)} estudiante(s)`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: editar % aprobación */}
      {showEditPctModal && scheduleForPct && (
        <div className="sm-overlay">
          <div className="sm-modal w500">
            <h2 className="sm-modal-title">
              <span style={{ fontSize: "1.4rem" }}>🎯</span>
              <span>Porcentaje de aprobación del horario</span>
            </h2>

            <p className="sm-muted" style={{ marginBottom: 16 }}>
              Define el porcentaje mínimo de aprobación para los exámenes asociados a este horario.
            </p>

            <form onSubmit={handleSavePct}>
              <div style={{ marginBottom: 16 }}>
                <label className="sm-label-lg">% mínimo de aprobación *</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={pctValue}
                  onChange={(e) => {
                    setPctValue(e.target.value);
                    setPctError("");
                  }}
                  className={`sm-input ${pctError ? "is-error" : ""}`}
                />
                {pctError && <div className="sm-error">{pctError}</div>}
              </div>

              <div className="sm-note">
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  <li>0% = cualquiera aprueba.</li>
                  <li>100% = solo puntaje perfecto aprueba.</li>
                  <li>Un valor típico es 70% u 80%.</li>
                </ul>
              </div>

              <div className="sm-row right" style={{ marginTop: 16 }}>
                <button
                  type="button"
                  className="sm-btn-ghost"
                  onClick={() => {
                    setShowEditPctModal(false);
                    setScheduleForPct(null);
                    setPctError("");
                  }}
                  disabled={pctSubmitting}
                >
                  Cancelar
                </button>
                <button type="submit" className="sm-btn-primary" disabled={pctSubmitting}>
                  {pctSubmitting ? "Guardando..." : "Guardar porcentaje"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: gestionar exámenes del horario */}
      {showManageExams && scheduleForExams && (
        <div className="sm-overlay">
          <div className="sm-modal w980">
            <div className="sm-row" style={{ justifyContent: "space-between", marginBottom: 14 }}>
              <h2 className="sm-modal-title" style={{ margin: 0 }}>
                <span style={{ fontSize: "1.4rem" }}>🧪</span>
                Exámenes del horario
              </h2>

              <button
                type="button"
                onClick={() => {
                  setShowManageExams(false);
                  setScheduleForExams(null);
                  setExamsInSchedule([]);
                  setSelectedExamsInModal(new Set());
                }}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "1.4rem",
                  cursor: "pointer",
                  color: "#6c757d",
                  padding: "4px 8px",
                  borderRadius: 8,
                }}
              >
                ×
              </button>
            </div>

            <div className="sm-note">
              <strong>Horario:</strong> {scheduleForExams?.codigo_horario || "—"} <br />
              <strong>Curso:</strong> {scheduleForExams?.curso_label || scheduleForExams?.curso_nombre || "—"}
            </div>

            <div className="sm-row" style={{ justifyContent: "space-between", marginBottom: 16 }}>
              <button
                type="button"
                className="sm-btn-primary"
                onClick={() => {
                  setEditingExamId(null);
                  setExamForm({
                    nombre: "",
                    descripcion: "",
                    numero_preguntas: 10,
                    tiempo_limite_minutos: 60,
                    activo: true,
                  });
                  setExamFormErrors({});
                  setExamPanelOpen(true);
                }}
              >
                + Nuevo examen
              </button>

              <button
                type="button"
                className="sm-btn-outline"
                disabled={modalSelectedCount === 0 || modalBulkBusy}
                onClick={handleBulkDeleteModalExams}
              >
                Eliminar seleccionados
              </button>
            </div>

            {examPanelOpen && (
              <div className="sm-card" style={{ marginBottom: 16 }}>
                <form onSubmit={submitExam}>
                  <div style={{ display: "grid", gap: 12 }}>
                    <div>
                      <label className="sm-label-lg">Nombre *</label>
                      <input
                        type="text"
                        value={examForm.nombre}
                        onChange={(e) => setExamForm((p) => ({ ...p, nombre: e.target.value }))}
                        className={`sm-input ${examFormErrors.nombre ? "is-error" : ""}`}
                      />
                      {examFormErrors.nombre && <div className="sm-error">{examFormErrors.nombre}</div>}
                    </div>

                    <div>
                      <label className="sm-label-lg">Descripción</label>
                      <textarea
                        value={examForm.descripcion}
                        onChange={(e) => setExamForm((p) => ({ ...p, descripcion: e.target.value }))}
                        className="sm-textarea"
                      />
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      <div>
                        <label className="sm-label-lg">Número de preguntas *</label>
                        <input
                          type="number"
                          min="1"
                          value={examForm.numero_preguntas}
                          onChange={(e) => setExamForm((p) => ({ ...p, numero_preguntas: Number(e.target.value) }))}
                          className={`sm-input ${examFormErrors.numero_preguntas ? "is-error" : ""}`}
                        />
                        {examFormErrors.numero_preguntas && (
                          <div className="sm-error">{examFormErrors.numero_preguntas}</div>
                        )}
                      </div>

                      <div>
                        <label className="sm-label-lg">Tiempo límite (min) *</label>
                        <input
                          type="number"
                          min="1"
                          value={examForm.tiempo_limite_minutos}
                          onChange={(e) =>
                            setExamForm((p) => ({ ...p, tiempo_limite_minutos: Number(e.target.value) }))
                          }
                          className={`sm-input ${examFormErrors.tiempo_limite_minutos ? "is-error" : ""}`}
                        />
                        {examFormErrors.tiempo_limite_minutos && (
                          <div className="sm-error">{examFormErrors.tiempo_limite_minutos}</div>
                        )}
                      </div>
                    </div>

                    <label className="sm-row" style={{ justifyContent: "space-between" }}>
                      <span>Examen activo</span>
                      <input
                        type="checkbox"
                        checked={!!examForm.activo}
                        onChange={(e) => setExamForm((p) => ({ ...p, activo: e.target.checked }))}
                      />
                    </label>
                  </div>

                  <div className="sm-row right" style={{ marginTop: 16 }}>
                    <button
                      type="button"
                      className="sm-btn-ghost"
                      onClick={() => {
                        setExamPanelOpen(false);
                        setEditingExamId(null);
                        setExamForm({
                          nombre: "",
                          descripcion: "",
                          numero_preguntas: 10,
                          tiempo_limite_minutos: 60,
                          activo: true,
                        });
                        setExamFormErrors({});
                      }}
                    >
                      Cancelar
                    </button>

                    <button type="submit" className="sm-btn-primary" disabled={examSubmitting}>
                      {examSubmitting ? "Guardando..." : editingExamId ? "Actualizar examen" : "Crear examen"}
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="sm-table-wrap">
              <table className="sm-table">
                <thead>
                  <tr>
                    <th className="sm-th center">
                      <input
                        ref={modalMasterRef}
                        type="checkbox"
                        checked={modalAllSelected}
                        onChange={toggleAllModalExams}
                      />
                    </th>
                    <th className="sm-th">Nombre</th>
                    <th className="sm-th">Descripción</th>
                    <th className="sm-th center">Preguntas</th>
                    <th className="sm-th center">Tiempo</th>
                    <th className="sm-th center">Estado</th>
                    <th className="sm-th">Seguridad / asistentes</th>
                    <th className="sm-th">Códigos</th>
                    <th className="sm-th center">Acciones</th>
                  </tr>
                </thead>

                <tbody>
                  {examsInSchedule.length === 0 ? (
                    <tr>
                      <td className="sm-td center" colSpan={9}>
                        No hay exámenes en este horario.
                      </td>
                    </tr>
                  ) : (
                    examsInSchedule.map((ex) => (
                      <tr key={ex.id} className="sm-tr">
                        <td className="sm-td center">
                          <input
                            type="checkbox"
                            checked={selectedExamsInModal.has(ex.id)}
                            onChange={() => toggleOneModalExam(ex.id)}
                          />
                        </td>

                        <td className="sm-td">
                          <div style={{ fontWeight: 1000, color: "#111827" }}>{ex.nombre || "—"}</div>
                        </td>

                        <td className="sm-td">
                          <div className="sm-subline">{ex.descripcion || "Sin descripción"}</div>
                        </td>

                        <td className="sm-td center">{ex.numero_preguntas || 0}</td>
                        <td className="sm-td center">{ex.tiempo_limite_minutos || 0} min</td>

                        <td className="sm-td center">
                          <span className={`sm-statusFinal ${ex.activo ? "active" : "inactive"}`}>
                            {ex.activo ? "Activo" : "Inactivo"}
                          </span>
                        </td>

                        <td className="sm-td">
                          <div className="sm-col">
                            <div className="sm-subline">
                              Cámara: <strong>{formatRequirementLabel(ex?.camara_nivel)}</strong>
                            </div>
                            <div className="sm-subline">
                              Micrófono: <strong>{formatRequirementLabel(ex?.microfono_nivel)}</strong>
                            </div>
                            <div className="sm-subline">
                              {formatAssistantsLabel(ex?.asistentes)}
                            </div>
                          </div>
                        </td>

                        <td className="sm-td">
                          <div className="sm-col">
                            <div style={{ fontWeight: 1000, color: "#111827" }}>
                              {ex?.codigo_horario || "—"}
                            </div>
                            <div className="sm-subline">
                              Acceso: {ex?.codigo_acceso || "Sin código"}
                            </div>
                          </div>
                        </td>

                        <td className="sm-td center">
                          <div className="sm-mini-actions">
                            <button
                              onClick={() => {
                                const cursoId = scheduleForExams?.curso_id ?? ex?.curso_id ?? null;
                                const cursoTipo =
                                  scheduleForExams?.curso_tipo ??
                                  courseOptions?.find((c) => Number(c.value) === Number(cursoId))?.tipo ??
                                  ex?.curso_tipo ??
                                  null;

                                setScheduleForQuestions({
                                  ...ex,
                                  curso_id: cursoId,
                                  curso_tipo: cursoTipo,
                                });
                                setShowQuestions(true);
                              }}
                              className="sm-btn-outline"
                              style={{
                                borderColor: "var(--sm-blue)",
                                color: "var(--sm-blue)",
                                background: "#E3F2FD",
                              }}
                            >
                              Preguntas
                            </button>

                            <button
                              onClick={() => editExam(ex)}
                              className="sm-btn-outline"
                              style={{
                                borderColor: "var(--sm-violet)",
                                color: "var(--sm-violet)",
                                background: "#F3E5F5",
                              }}
                            >
                              Editar
                            </button>

                            <button
                              onClick={() => removeExam(ex.id)}
                              className="sm-btn-outline"
                              style={{
                                borderColor: "var(--sm-red)",
                                color: "var(--sm-red)",
                                background: "#FFEBEE",
                              }}
                            >
                              Eliminar
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="sm-row right" style={{ marginTop: 16 }}>
              <button
                onClick={() => {
                  setShowManageExams(false);
                  setScheduleForExams(null);
                  setExamsInSchedule([]);
                  setSelectedExamsInModal(new Set());
                }}
                className="sm-btn-ghost"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: preguntas del examen */}
      {showQuestions && scheduleForQuestions && (
        <ManageQuestionsModal
          exam={scheduleForQuestions}
          onClose={() => {
            setShowQuestions(false);
            setScheduleForQuestions(null);
          }}
        />
      )}
    </>
  );
}

/* ===================== Subcomponentes optimizados ===================== */

const ScheduleRow = React.memo(function ScheduleRow({
  exam,
  isChecked,
  onToggleCheck,
  scheduleBulkBusy,
  busy,
  formatDate,
  formatTime,
  onViewStudents,
  onManageQuestions,
  onEditSchedule,
  onOpenPct,
  onToggleStatus,
  onDelete,
  onReview,
}) {
  const stateMeta = getScheduleStateMeta(exam);
  const isFinalized = stateMeta.key === "finalizado";
  const isRunning = stateMeta.key === "en_ejecucion";
  const canEdit = true;
  const canDelete = !isRunning && !isFinalized;
  const canToggle = stateMeta.key === "activo" || stateMeta.key === "inactivo";

  const isVirtual = !!exam.is_virtual;
  const modText = isVirtual ? "💻 Virtual" : "🏫 Presencial";
  const modClass = isVirtual ? "virtual" : "presencial";

  const locationText = String(exam?.ubicacion_info || "").trim();
  const accessText = exam?.requiere_codigo ? "Con código" : "Acceso abierto";


  return (
    <tr className="sm-tr">
      <td className="sm-td center">
        <input
          type="checkbox"
          checked={isChecked}
          onChange={() => onToggleCheck(exam.id)}
          disabled={scheduleBulkBusy || busy}
          title={isChecked ? "Quitar de la selección" : "Seleccionar"}
        />
      </td>

      <td className="sm-td">
        <div className="sm-row" style={{ flexWrap: "nowrap" }}>
          <div className="sm-avatar">📅</div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 1000, color: "#2c3e50" }}>
              {formatDate(exam.fecha_examen)}
            </div>
            <div className="sm-muted" style={{ fontSize: 14 }}>
              {formatTime(exam.hora_inicio)} - {formatTime(exam.hora_fin)}
            </div>

            <div
              style={{
                fontSize: 13,
                color: "#6c757d",
                marginTop: 4,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: 320,
              }}
            >
              Curso: <span style={{ fontWeight: 900, color: "#1f2937" }}>{exam.curso_nombre || "—"}</span>
              {exam.codigo_curso ? <span className="sm-code">{exam.codigo_curso}</span> : null}
            </div>
          </div>
        </div>
      </td>

      <td className="sm-td center">
        <div className="sm-col" style={{ alignItems: "center" }}>
          <span className={`sm-modpill ${modClass}`}>{modText}</span>

          <div className="sm-subline">
            {locationText ? (
              isVirtual ? (
                <span className="sm-linkClamp" title={locationText}>🔗 {locationText}</span>
              ) : (
                <span className="sm-linkClamp" title={locationText}>📍 {locationText}</span>
              )
            ) : (
              <span>{isVirtual ? "🔗 Sin enlace" : "📍 Sin ubicación"}</span>
            )}
          </div>

          <div className="sm-subline">
            {accessText}
          </div>
        </div>
      </td>

      <td className="sm-td center">
        <div className="sm-col" style={{ alignItems: "center", gap: 8 }}>
          <div style={{ fontWeight: 1000, color: "#2c3e50" }}>
            {exam.cupos_ocupados || 0} / {exam.cupos_disponibles || 0}
          </div>

          <button
            onClick={() => onViewStudents(exam)}
            className="sm-btn-chip blue"
            disabled={busy}
            title="Gestionar estudiantes agendados"
          >
            👥 Gestionar
          </button>
        </div>
      </td>

      <td className="sm-td center">
        <div className="sm-col" style={{ alignItems: "center", gap: 8 }}>
          <div style={{ fontWeight: 1000, color: "#2c3e50" }}>
            {Number(exam.numero_preguntas ?? 0) || 0}
          </div>

          <button
            onClick={() => onManageQuestions(exam)}
            className="sm-btn-chip green"
            disabled={busy}
          >
            🧩 Gestionar
          </button>
        </div>
      </td>

      <td className="sm-td center">
        <div className="sm-col" style={{ alignItems: "center" }}>
          <div style={{ fontWeight: 1000, color: "#2c3e50" }}>
            {exam.porcentaje_aprobacion != null ? `${exam.porcentaje_aprobacion}%` : "—"}
          </div>
          <div className="sm-subline">Aprobación</div>
        </div>
      </td>

      <td className="sm-td center">
        <div className="sm-col" style={{ alignItems: "center" }}>
          {canToggle ? (
            <button
              type="button"
              className={`sm-switch ${exam.activo ? "on" : "off"}`}
              onClick={() => onToggleStatus(exam.id, exam.activo)}
              aria-disabled={busy ? "true" : "false"}
              disabled={busy}
              title="Cambiar estado"
            >
              <span className="dot" />
              {busy ? "Actualizando…" : stateMeta.label}
            </button>
          ) : (
            <span className={`sm-statusFinal ${stateMeta.className}`}>
              {stateMeta.emoji} {stateMeta.label}
            </span>
          )}
        </div>
      </td>

      <td className="sm-td center">
        <div className="sm-mini-actions">
          <button
            onClick={() => onReview?.(exam)}
            className="sm-btn-outline"
            style={{ borderColor: "var(--sm-blue)", color: "var(--sm-blue)", background: "#E3F2FD" }}
            disabled={busy}
            title="Revisar proctoring de este horario"
          >
            🛠️ Gestionar horario
          </button>

          <button
            onClick={() => onEditSchedule(exam)}
            className="sm-btn-outline"
            style={{ borderColor: "var(--sm-violet)", color: "var(--sm-violet)", background: "#F3E5F5" }}
            disabled={busy}
            title={isFinalized ? "Ver horario finalizado" : "Editar horario"}
          >
            ✏️ {isFinalized ? "Ver" : "Editar"}
          </button>

          <button
            onClick={() => onDelete(exam)}
            className="sm-btn-outline"
            style={{ borderColor: "var(--sm-red)", color: "var(--sm-red)" }}
            disabled={busy || !canDelete}
            title={!canDelete ? "No se puede eliminar este horario" : "Eliminar horario"}
          >
            🗑️ Eliminar
          </button>
        </div>
      </td>
    </tr>
  );
});

const ScheduleCard = React.memo(function ScheduleCard({
  exam,
  roleCanDeleteSchedule = false,
  roleCanEditSchedule = false,
  roleCanManageScheduleStudents = false,
  roleCanManageScheduleQuestions = false,
}) {
  const stateMeta = getScheduleStateMeta(exam);
  const isVirtual = toBool(exam?.is_virtual);
  const canDelete = roleCanDeleteSchedule && canDeleteSchedule(exam);
  const canEdit = canEditSchedule(exam);
  const isViewOnly = !canEdit;
  const asistentes = Array.isArray(exam?.asistentes) ? exam.asistentes : [];
  const firstAssistant = asistentes[0] || null;
  const currentOccupied = Number(exam?.cupos_ocupados ?? exam?.cupos_ocupados_reales ?? 0);
  const currentTotal = Number(exam?.cupos_disponibles ?? 0);


  const getScheduleRegisteredQuestions = (exam) => {
    { console.log(exam) }
    const candidates = [
      exam?.preguntas_registradas,
      exam?.total_preguntas,
      exam?.preguntas_reales,
    ];

    for (const value of candidates) {
      const n = Number(value);
      if (Number.isFinite(n) && n > 0) return n;
    }

    return 0;
  };

  const requiredQuestions = toFiniteNumber(exam?.numero_preguntas, 0);
  const realQuestions = getScheduleRegisteredQuestions(exam);

  const missingQuestions = Math.max(0, requiredQuestions - realQuestions);
  const questionsOk = requiredQuestions > 0 && realQuestions >= requiredQuestions;

  return (
    <div className="sm-cardSchedule">
      <div className="sm-cardTop">
        <div className="sm-col-course" style={{ gap: 10 }}>
          <div className="sm-courseIcon">
            <Calendar size={18} />
          </div>

          <div className="sm-courseMeta">
            <div className="sm-courseCode">
              {exam?.curso_nombre || "Curso"}
              {exam?.codigo_curso ? ` (${exam.codigo_curso})` : ""}
            </div>

            <div className="sm-courseDate">
              {String(exam?.fecha_examen || "").slice(0, 10)}
            </div>

            <div className="sm-courseTime">
              <Clock size={11} />
              {String(exam?.hora_inicio || "").slice(0, 5)} - {String(exam?.hora_fin || "").slice(0, 5)}
            </div>
          </div>
        </div>

        <div style={{ display: "grid", justifyItems: "end", gap: 6 }}>
          <span className="sm-idChip">
            {exam?.codigo_horario || `HOR-${exam?.id}`}
          </span>

          <span className={`sm-statePillWide ${stateMeta.className}`}>
            {stateMeta.label}
          </span>
        </div>
      </div>

      <div className="sm-cardBox">
        <div className="sm-cardSection">
          <div className="k">Modalidad / Acceso</div>
          <div className="v">
            <span className={`sm-modpill ${isVirtual ? "virtual" : "presencial"}`}>
              {isVirtual ? <Laptop size={12} /> : <MapPin size={12} />}
              {isVirtual ? "Virtual" : "Presencial"}
            </span>
          </div>
          <div className="sub" title={String(exam?.ubicacion_info || "Sin ubicación / enlace")}>
            {String(exam?.ubicacion_info || "Sin ubicación / enlace")}
          </div>
        </div>

        <div className="sm-cardSection">
          <div className="k">Asistente</div>
          <div className="v" style={{ color: firstAssistant ? "#d97706" : "#9ca3af" }}>
            {firstAssistant
              ? `${firstAssistant?.nombres || ""} ${firstAssistant?.apellidos || ""}`.trim()
              : "No asignado"}
          </div>
        </div>

        <div className="sm-cardSection">
          <div className="k">Inscritos</div>
          <div className="v">
            <Users size={12} style={{ display: "inline", marginRight: 4 }} />
            {currentOccupied}/{currentTotal}
          </div>
          <button
            type="button"
            className="sm-manageBtn"
            disabled={!roleCanManageScheduleStudents}
            onClick={() => window.__smHandleViewStudents?.(exam)}
          >
            Gestionar estudiantes
          </button>
        </div>

        <div className="sm-cardSection">
          <div className="k">Preguntas</div>
          <div className="v">
            <FileText size={12} style={{ display: "inline", marginRight: 4 }} />
            <span
              title={
                questionsOk
                  ? "Preguntas completas"
                  : `Faltan ${missingQuestions} pregunta${missingQuestions === 1 ? "" : "s"}`
              }
              style={{
                color: questionsOk ? "#166534" : "#b45309",
                fontWeight: 800,
              }}
            >
              {realQuestions}/{requiredQuestions}
            </span>
          </div>
          <button
            type="button"
            className="sm-ghostManage"
            disabled={!roleCanManageScheduleQuestions}
            onClick={() => window.__smHandleManageQuestions?.(exam)}
          >
            Preguntas
          </button>
        </div>
      </div>

      <div className="sm-cardButtons">
        <button
          type="button"
          className="sm-manageBtn"
          onClick={() => window.__smHandleReview?.(exam)}
        >
          <Activity size={13} />
          Gestionar horario
        </button>

        <div className="row2">
          <button
            type="button"
            className="sm-editBtn"
            onClick={() => window.__smHandleEdit?.(exam)}
          >
            {roleCanEditSchedule && canEditSchedule(exam) ? (
              <>
                <Edit2 size={13} />
                Editar
              </>
            ) : (
              <>
                <Eye size={13} />
                Ver
              </>
            )}
          </button>

          <button
            type="button"
            className="sm-deleteBtn"
            onClick={() => window.__smHandleDelete?.(exam)}
            disabled={!roleCanDeleteSchedule || !canDeleteSchedule(exam)}
            title={
              !roleCanDeleteSchedule
                ? "Tu rol no permite eliminar horarios"
                : !canDeleteSchedule(exam)
                  ? "No se puede eliminar un horario en ejecución o finalizado"
                  : "Eliminar horario"
            }
          >
            <Trash2 size={13} />
            Borrar
          </button>
        </div>
      </div>
    </div>
  );
});

function StatsCompact({ examStats }) {
  const items = [
    { label: "Horarios", value: examStats?.total_horarios || 0, icon: "📅", hint: "Total" },
    { label: "Activos", value: examStats?.horarios_activos || 0, icon: "✅", hint: "En curso" },
    { label: "Cupos", value: examStats?.cupos_totales || 0, icon: "🎯", hint: "Total" },
    { label: "Agendados", value: examStats?.estudiantes_agendados || 0, icon: "👥", hint: "Est." },
  ];

  return (
    <div className="grid">
      {items.map((it, idx) => (
        <div key={idx} className="sm-statrow">
          <div className="sm-statleft">
            <div className="sm-staticon">{it.icon}</div>
            <div className="sm-statmeta">
              <div className="t">{it.label}</div>
              <div className="v">{it.value}</div>
            </div>
          </div>
          <div className="sm-statpill">{it.hint}</div>
        </div>
      ))}
    </div>
  );
}

function StudentsSearch({ loading, students, onShowAdd, disabledAdd, canAdd = true, addBlockedReason = "" }) {
  const [q, setQ] = useState("");
  const deb = useDebouncedValue(q, 150);

  const count = useMemo(() => {
    const list = students || [];
    if (!deb) return list.length;
    const nd = normalizeText(deb);
    return list.filter((s) => {
      const n = normalizeText(`${s.nombres || ""} ${s.apellidos || ""}`);
      const e = normalizeText(s.email || "");
      return n.includes(nd) || e.includes(nd);
    }).length;
  }, [students, deb]);

  const fullDisabled = loading || disabledAdd;

  return (
    <>
      <div className="sm-searchbox">
        <input
          type="text"
          placeholder="🔎 Buscar por nombre o correo..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          disabled={loading || !students?.length}
          className="sm-input small"
        />
        {q && (
          <button onClick={() => setQ("")} title="Limpiar búsqueda" className="sm-clear-search">
            ✕
          </button>
        )}
        <div className={`sm-count ${q ? "with-clear" : ""}`}>
          {count}/{students?.length || 0}
        </div>
      </div>

      {/* ✅ SOLO mostrar botón si se permite agregar */}
      {canAdd && (
        <button
          onClick={onShowAdd}
          disabled={fullDisabled}
          className="sm-btn-primary"
          style={{ whiteSpace: "nowrap" }}
          title={disabledAdd ? "No hay cupos disponibles" : "Agregar estudiantes"}
        >
          + Agregar
        </button>
      )}
    </>
  );
}


function ToggleSwitch({
  label,
  description,
  checked,
  onChange,
  disabled = false,
  horizontal = false,
}) {
  return (
    <div
      className={`sm-toggleRow ${horizontal ? "is-horizontal" : ""} ${disabled ? "is-disabled" : ""
        }`}
    >
      <div className="sm-toggleMeta">
        {label ? <div className="sm-toggleLabel">{label}</div> : null}
        {description ? <div className="sm-toggleDesc">{description}</div> : null}
      </div>

      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        className={`sm-switchBtn ${checked ? "is-on" : "is-off"}`}
        aria-pressed={checked}
      >
        <span className="sm-switchThumb" />
      </button>
    </div>
  );
}


function StudentsTable({
  students,
  studentBusy,
  onRemove,
  onToggleEnable,
  showEnable = true,
  selectedIds = new Set(),
  onToggleSelect,
  onToggleSelectAll,
  allSelected = false,
  masterRef = null,
}) {
  return (
    <div className="sm-enrolledTableWrap">
      <table className="sm-enrolledTable">
        <thead>
          <tr>
            <th className="sm-col-check">
              <input
                ref={masterRef}
                type="checkbox"
                checked={allSelected}
                onChange={onToggleSelectAll}
                disabled={!students.length}
                title="Seleccionar todos"
              />
            </th>

            <th className="sm-col-student">
              <button type="button" className="sm-th-sort">
                ESTUDIANTE ↑
              </button>
            </th>

            <th className="sm-col-date">AGENDAMIENTO</th>
            <th className="sm-col-origin">MATRICULACIÓN</th>
            {showEnable && <th className="sm-col-access">ACCESO</th>}
            <th className="sm-col-actions">ACCIONES</th>
          </tr>
        </thead>

        <tbody>
          {students.map((s, i) => {
            const busy = studentBusy?.has?.(s.id);
            const originMeta = getEnrollmentOriginMeta(s);
            const checked = selectedIds.has(Number(s.id));

            return (
              <tr key={s.id ?? i}>
                <td className="sm-col-check">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onToggleSelect?.(Number(s.id))}
                    disabled={busy}
                    title="Seleccionar estudiante"
                  />
                </td>

                <td className="sm-col-student">
                  <div className="sm-studentRich">
                    <div className="sm-studentAvatarSoft">
                      {getStudentInitials(s)}
                    </div>

                    <div className="sm-studentRichMeta">
                      <div className="sm-studentRichName">
                        {getStudentDisplayName(s)}
                      </div>

                      <div className="sm-studentRichSub">
                        {s?.email || "—"}
                      </div>

                      <div className="sm-studentDoc">
                        C.I. {s?.codigo_institucional || "—"}
                      </div>
                    </div>
                  </div>
                </td>

                <td className="sm-col-date">
                  <span className="sm-dateBadge">
                    {formatStudentEnrollDate(s?.fecha_agendamiento || s?.fecha_inscripcion || s?.created_at)}
                  </span>
                </td>

                <td className="sm-col-origin">
                  <div className="sm-originStack">
                    <span className={`sm-originBadge ${originMeta.className}`}>
                      {originMeta.label}
                    </span>

                    {originMeta.sublabel ? (
                      <span className="sm-originSub" title={originMeta.sublabel}>
                        Por: {originMeta.sublabel}
                      </span>
                    ) : null}
                  </div>
                </td>

                {showEnable && (
                  <td className="sm-col-access">
                    <div className="sm-accessInline">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => onToggleEnable?.(Number(s.id), !s?.habilitado)}
                        className={`sm-switchBtn ${s?.habilitado ? "is-on" : "is-off"}`}
                        aria-pressed={!!s?.habilitado}
                        title={s?.habilitado ? "Deshabilitar acceso" : "Habilitar acceso"}
                      >
                        <span className="sm-switchThumb" />
                      </button>

                      <span className="sm-accessInlineLabel">
                        {s?.habilitado ? "Habilitado" : "Inhabilitado"}
                      </span>
                    </div>
                  </td>
                )}

                <td className="sm-col-actions">
                  <button
                    type="button"
                    className="sm-tableIconBtn"
                    onClick={() => onRemove?.(Number(s.id))}
                    disabled={busy}
                    title="Eliminar estudiante del horario"
                  >
                    <Trash2 size={15} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
